/**
 * api.ts - Centralized API client for the Forge frontend
 *
 * This file provides:
 * 1. ApiClient class - Axios wrapper with CSRF and auth handling
 * 2. api object - All API endpoints organized by resource
 *
 * Authentication flow:
 * - Uses Laravel Sanctum with cookies (withCredentials: true)
 * - X-Active-License-Id header scopes all requests to current site
 * - CSRF token auto-fetched from /sanctum/csrf-cookie before mutations
 *
 * Usage in components:
 *   import { api, apiClient } from '@/lib/api'
 *
 *   // Use api.* for typed endpoints
 *   const { data } = useQuery({ queryKey: ['topics'], queryFn: () => api.topics.list() })
 *
 *   // Use apiClient.setActiveLicense() to switch sites
 *   apiClient.setActiveLicense(license.id, license.domain)
 *
 * Error handling:
 * - 401 with code 'unauthenticated' → session-expired dialog (registered via
 *   setUnauthenticatedHandler; falls back to a /login redirect when nothing
 *   registered)
 * - 403 (no license access) → let UI handle
 * - Use getApiErrorMessage() to extract human-readable errors
 *
 * Resource organization:
 * - api.license - Auth verification
 * - api.workflows - AI workflow management
 * - api.topics - Topic library CRUD + generation
 * - api.content - Content items with WP sync
 * - api.newsroom - News review lane (approve publishes live)
 * - api.alerts - Agentic Alerts feed and monitors
 * - api.wordpress - WP connection management
 * - api.settings - Site settings (integrations, profile)
 * - api.analytics - Usage stats and metrics
 */

import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios"
import { ensureXsrfCookie, readXsrfCookie, refreshXsrfCookie } from "./csrf"
import type { Workflow, Topic, ContentItem, Cta, CtaInput, CtaStatsResponse, CtaDetailedStats, CtaLead, Paginated, Transcription, TranscriptionTopic, TranscriptionSettings, TranscriptionFilters, TranscriptionStats, GenerateFromTranscriptionRequest, UpdateTranscriptionSettingsRequest } from "./types"
import type { AutopilotSettings, AutopilotStatus, AutopilotScheduleItem, AutopilotActivityLogEntry, AutopilotStats, AutopilotValidation, UpdateAutopilotSettingsRequest, GenerateScheduleRequest, AutopilotScheduleParams, AutopilotActivityParams, AutopilotStatsParams, AutopilotReviewRule } from "./types"
import type { Task, TaskSection, TaskComment, TaskActivity, TaskAttachment, TaskDeliverable, TaskNotificationPreferences, TaskRelation, Tag } from "./types"
import { BOARD_ARCHIVED_MESSAGE, isBoardArchivedError, type Board, type BoardMember, type BoardRole, type BoardVisibility } from "./task-boards"
import type { SettlementApproveResponse, SettlementAttention, SettlementAttentionRow, SettlementCatalogResponse, SettlementCreatePayload, SettlementDetail, SettlementExtractState, SettlementListParams, SettlementListResponse, SettlementStats, SettlementUnpublishResponse, SettlementUpdatePayload } from "./types"
import type { PostSummary, SummarySettings, PromptPreview } from "@/types/post-summary.types"
import type { Component, ComponentInput, RenderedComponent } from "@/types/component.types"
import type { SiteRepository, CreateSiteRepositoryRequest, UpdateSiteRepositoryRequest, GithubRegistryRepository, RegistryRepositoryOption } from "@/types/repository.types"
import type { SiteActivityEvent, SiteActivityEventDetail, SiteActivityPagination, SiteActivitySummary } from "@/types/site-activity.types"
import type { Project } from "@/types/project.types"
import type { Agent, AgentKind, AgentMemory, AgentPaginated, AgentRun, AgentRunEvent, AgentRunnerInfo, AgentStatus, AgentToolCatalogueItem, AgentTriggerType, DeliveryStatus } from "@/types/agent.types"
import { unwrapRunners } from "@/lib/agent-runners"
import { readTaskNotificationPreferences, type TaskNotificationPreferencesBody } from "@/lib/task-notification-preferences"
import {
  readTaskEmailPreferences,
  type TaskEmailPreferences,
  type TaskEmailPreferencesBody,
  type TaskEmailPreferencesPut,
} from "@/lib/task-email-preferences"
import type { NewsroomItemDetail, NewsroomProcess, NewsroomQueueResponse, NewsroomSettings, NewsroomSettingsPayload } from "@/types/newsroom.types"
import type { AlertConditionDetail, AlertFeedResponse, AlertMonitor, AlertMonitorsResponse, AlertTestRun, AlertTestRunStart } from "@/types/alerts.types"
import type {
  GithubObserved,
  GoogleDirectoryObserved,
  OffboardPlanInput,
  OnboardPlanInput,
  PeopleAccessActionsResponse,
  PeopleAccessActionType,
  PeopleAccessLifecyclePlanResponse,
  PeopleAccessPlanResponse,
  PeopleAccessRegistry,
  SlackObserved,
  SshObserved,
  AgentBoxAccess,
  BoxJob,
  BoxJobInput,
  LifecycleCheckItem,
  WordPressObserved,
  WordPressObservedRefresh,
} from "@/types/people-access.types"
import { readSettingsResponse } from "@/lib/newsroom"
import type { SitePopupSettings } from "@/types/cta.types"
import type { ApiActivityPage, ApiActivityParams, ApiActivitySummaryRow, ApiTokenScopes, CreateApiTokenPayload, MintedApiToken } from "@/types/api-tokens.types"
import { popupSettingsUpdatePayload } from "@/lib/cta-popup-delivery"
import type { HistogramData } from "@/types/log-histogram.types"

// Extend Axios config to include our CSRF retry flag
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _csrfRetry?: boolean
  }
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api"

interface ApiError {
  message?: string
  error?: string
  errors?: Record<string, string[]>
}

// Helper to extract error message from axios errors
export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    // FOR-349: every write on an archived board's task answers 403 board_archived.
    if (isBoardArchivedError(error)) return BOARD_ARCHIVED_MESSAGE
    const data = error.response?.data
    // Try various common error message formats
    if (data?.error) return data.error
    if (data?.message) return data.message
    if (data?.errors) {
      const firstError = Object.values(data.errors).flat()[0]
      if (firstError) return String(firstError)
    }
  }
  if (error instanceof Error) return error.message
  return "An unexpected error occurred"
}

/**
 * True when a request failed because its connector was never set up, rather
 * than because a configured one could not be reached.
 *
 * The observed People & Access connectors tag their 503s with a `reason` so the
 * two can be told apart: a Slack app nobody has installed yet is a blank to
 * fill in, not a fault, and rendering it in red alongside real outages would
 * teach people to ignore the panel.
 */
export function isNotConfiguredError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.data?.reason === "not_configured"
}

/**
 * The body of a failed response, for the endpoints that return something worth
 * rendering alongside their error status.
 *
 * Axios throws on 4xx/5xx, which normally means the body is discarded. The
 * People & Access apply endpoints answer 502 with the plan itself, each step
 * carrying whether it landed — an offboard that halted after suspending
 * somebody must show exactly that, not a generic failure toast.
 */
export function getApiErrorBody<T>(error: unknown): T | undefined {
  return axios.isAxiosError(error) ? (error.response?.data as T | undefined) : undefined
}

/**
 * ApiClient - Axios wrapper with Sanctum auth and CSRF handling
 *
 * Key behaviors:
 * - Automatically reads XSRF-TOKEN cookie and sets X-XSRF-TOKEN header
 * - Ensures CSRF cookie is fetched before POST/PUT/PATCH/DELETE
 * - Sets X-Active-License-Id header for multi-tenant data scoping
 * - Debug logging in development mode (visible in browser console)
 * - 401 unauthenticated → notifies the registered session-expired handler
 */

// FOR-194: a 401 used to hard-navigate to /login — a full page teardown that
// destroyed the editor mid-edit, and a background poll could fire it while
// the writer was typing. The auth provider registers a handler that shows an
// in-place re-auth dialog instead; the redirect survives only as a fallback
// for the moments before anything is registered.
let onUnauthenticated: (() => void) | null = null

export function setUnauthenticatedHandler(fn: (() => void) | null) {
  onUnauthenticated = fn
}
class ApiClient {
  private client: AxiosInstance
  private debugMode = import.meta.env.DEV // Only debug in development

  constructor() {
    // No axios-native XSRF handling (withXSRFToken): its cookie reader takes
    // the FIRST XSRF-TOKEN occurrence in document.cookie and overwrites the
    // header inside the adapter, so with a stale duplicate cookie it silently
    // resurrects the dead token. The request interceptor below is the single
    // writer, using the duplicate-aware reader in lib/csrf.ts.
    this.client = axios.create({
      baseURL: API_URL,
      withCredentials: true, // Enable cookie-based auth (Sanctum)
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })

    this.log('🔧 ApiClient initialized', {
      baseURL: API_URL,
      withCredentials: true,
      defaultHeaders: this.client.defaults.headers.common,
    })

    // Request interceptor for debugging and CSRF token handling
    this.client.interceptors.request.use(
      (config) => {
        // Manually read XSRF-TOKEN cookie and set as header
        // This is needed because Axios's automatic XSRF handling doesn't work with cross-domain setups
        const csrfToken = readXsrfCookie()
        if (csrfToken) {
          config.headers['X-XSRF-TOKEN'] = csrfToken
        }

        this.log('📤 Outgoing request', {
          method: config.method?.toUpperCase(),
          url: config.url,
          fullURL: config.baseURL + config.url,
          headers: config.headers,
          withCredentials: config.withCredentials,
          cookies: document.cookie,
          csrfTokenFromCookie: csrfToken ? 'Present' : 'Missing',
        })
        return config
      },
      (error) => {
        this.log('❌ Request error', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling and debugging
    this.client.interceptors.response.use(
      (response) => {
        this.log('✅ Response received', {
          status: response.status,
          url: response.config.url,
          headers: response.headers,
        })
        return response
      },
      async (error: AxiosError<ApiError>) => {
        const errorCode = error.response?.data?.code

        this.log('❌ Response error', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          errorCode,
          errorData: error.response?.data,
          requestHeaders: error.config?.headers,
          responseHeaders: error.response?.headers,
          cookies: document.cookie,
        })

        // Handle 419 CSRF token mismatch - refresh the token and retry once.
        // The retried request goes back through the request interceptor, which
        // stamps the freshly-fetched cookie value into X-XSRF-TOKEN.
        if (error.response?.status === 419 && error.config && !error.config._csrfRetry) {
          this.log('🔄 CSRF token expired, refreshing and retrying...')
          error.config._csrfRetry = true

          try {
            await refreshXsrfCookie()
          } catch (refreshError) {
            // Token refresh failed (network / API down) — a retry would just
            // 419 again with the same stale token, so surface the original error.
            this.log('❌ CSRF refresh failed, not retrying', refreshError)
            return Promise.reject(error)
          }

          this.log('🔄 Retrying request with fresh CSRF token', { url: error.config.url })
          return this.client.request(error.config)
        }

        // Only react when truly unauthenticated (not just missing license)
        if (error.response?.status === 401 && errorCode === 'unauthenticated') {
          // Prevent a loop - do nothing if already on the login page
          if (!window.location.pathname.includes('/login')) {
            if (onUnauthenticated) {
              onUnauthenticated()
            } else {
              window.location.href = "/login"
            }
          }
        }

        // For 403 errors (no licenses, access denied), let the UI handle it
        // Don't auto-redirect - show proper error messages

        return Promise.reject(error)
      }
    )

    // Don't initialize CSRF here - wait until after auth headers are set
  }

  private log(message: string, data?: any) {
    if (!this.debugMode) return
    console.groupCollapsed(`🔍 [API Debug] ${message}`)
    if (data) {
      console.log(data)
    }
    console.trace('Stack trace')
    console.groupEnd()
  }

  // CSRF cookie reading/refreshing lives in lib/csrf.ts (shared with
  // BaseApiClient — the cookie is shared browser state, so the logic must be
  // too). Before each mutation we only ensure the cookie exists; a 419
  // response is the signal for a forced refresh + single retry.
  private async ensureCsrf() {
    await ensureXsrfCookie()
  }

  setActiveLicense(licenseId: number, domain?: string) {
    this.log('🎫 Setting active license', {
      licenseId,
      domain,
      previousHeaders: { ...this.client.defaults.headers.common },
    })

    this.client.defaults.headers.common["X-Active-License-Id"] = licenseId.toString()
    if (domain) {
      this.client.defaults.headers.common["X-Site-Domain"] = domain
    }
    // NOTE: no CSRF reset needed — the XSRF cookie is session-scoped, not
    // license-scoped, so switching licenses doesn't invalidate it.

    this.log('✅ License headers set', {
      newHeaders: { ...this.client.defaults.headers.common },
    })
  }

  clearActiveLicense() {
    this.log('🗑️  Clearing active license')

    delete this.client.defaults.headers.common["X-Active-License-Id"]
    delete this.client.defaults.headers.common["X-Site-Domain"]

    this.log('✅ License cleared', {
      headers: { ...this.client.defaults.headers.common },
    })
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    this.log('🔍 GET request', { url, config })
    const response = await this.client.get<T>(url, config)
    return response.data
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    this.log('📝 POST request - ensuring CSRF', { url, data, config })
    await this.ensureCsrf()
    this.log('📝 POST request - CSRF ensured, sending request', {
      url,
      xsrfToken: readXsrfCookie() ? "Present" : "Missing",
      cookies: document.cookie,
    })
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    this.log('✏️  PUT request - ensuring CSRF', { url, data, config })
    await this.ensureCsrf()
    this.log('✏️  PUT request - CSRF ensured, sending request', {
      url,
      xsrfToken: readXsrfCookie() ? "Present" : "Missing",
    })
    const response = await this.client.put<T>(url, data, config)
    return response.data
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    this.log('🔧 PATCH request - ensuring CSRF', { url, data, config })
    await this.ensureCsrf()
    this.log('🔧 PATCH request - CSRF ensured, sending request', {
      url,
      xsrfToken: readXsrfCookie() ? "Present" : "Missing",
    })
    const response = await this.client.patch<T>(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    this.log('🗑️  DELETE request - ensuring CSRF', { url, config })
    await this.ensureCsrf()
    this.log('🗑️  DELETE request - CSRF ensured, sending request', {
      url,
      xsrfToken: readXsrfCookie() ? "Present" : "Missing",
    })
    const response = await this.client.delete<T>(url, config)
    return response.data
  }
}

// Export singleton instance
/** A board an external person is on, as Admin → Users lists it. */
/** A board a non-admin is a member of (Admin → Users). General shows with is_default. */
export interface BoardMembership {
  id: number
  key: string
  name: string
  role: string
  is_default?: boolean
}

/**
 * One board to put a person on. External people are viewer or editor (the API
 * caps them there); team people may also be manager. General is team-only.
 */
export interface BoardGrant {
  board_id: number
  role: "viewer" | "editor" | "manager"
}


export const apiClient = new ApiClient()

// Re-export types for convenience
export type { Workflow, WorkflowStep, Prompt, Topic, ContentItem } from "./types"

// =============================================================================
// SOURCE VERIFICATION TYPES (FOR-127 / FOR-131)
// =============================================================================
// These shape the verification-first content workflow. Section A items are
// Claude-verified claims (with source URLs + verdicts); Section B items are
// human-only checks (e.g. "phone number still current?"). The site object also
// carries `approved_domains` and `section_b_template` (queried via the existing
// site detail GET, mutated via the verification.update* methods below).

// ---------------------------------------------------------------------------
// Practice Area Rollout
// ---------------------------------------------------------------------------

// 'cancelled' = decided against. Excluded from the vs-control baseline and
// from demand-share denominators, since it is neither a fair counterfactual
// nor work still waiting to be done.
/** One ~300-word segment of an AI-detection result. Offsets index into the
 *  exact plain text that was submitted, which the API stores alongside. */
export interface AiDetectionWindow {
  text?: string
  label?: string
  ai_assistance_score?: number
  confidence?: string
  start_index?: number
  end_index?: number
}

export interface AiDetectionCheck {
  id: number
  status: 'pending' | 'running' | 'completed' | 'failed'
  provider: string
  provider_version?: string | null
  prediction?: 'AI' | 'Mixed' | 'Human' | null
  fraction_ai?: number | null
  fraction_ai_assisted?: number | null
  fraction_human?: number | null
  windows?: AiDetectionWindow[] | null
  dashboard_link?: string | null
  /** Hash of the body at check time; compare with current_body_hash for staleness. */
  body_hash?: string | null
  quote_ratio?: number | null
  word_count?: number
  error_message?: string | null
  checked_at?: string | null
  created_at?: string
}

export type PracticeRolloutStatus = 'not_started' | 'rewritten' | 'live' | 'cancelled'

export interface PracticeRolloutEvent {
  id: number
  event_type: string
  label: string
  occurred_on: string
  notes: string | null
}

export interface PracticeRolloutMetrics {
  impressions: number
  clicks: number
  // null when no day in the window reported a position
  position: number | null
  sessions: number | null
  days_with_data: number
}

export interface PracticeRolloutArea {
  id: number
  practice_area: string
  slug: string
  path: string
  url: string | null
  wp_post_id: number | null
  status: PracticeRolloutStatus
  live_at: string | null
  page_count: number
  // Distinct URLs under this area that Search Console has reported on. Zero
  // means there is nothing to expand.
  child_page_count: number
  notes: string | null
  metrics: PracticeRolloutMetrics
  events: PracticeRolloutEvent[]
}

export interface PracticeRolloutSubPage {
  path: string
  // null when Search Console has never reported this URL — render dashes, not
  // zeroes, since "not ranking" is not the same claim as "no impressions".
  impressions: number | null
  clicks: number | null
  position: number | null
  // Short label for outstanding work on this page, e.g. "moving under hub".
  flag: string | null
  // False for flagged work still living outside this area, so queued pages are
  // never presented as part of the tree the area's totals cover.
  in_tree: boolean
}

export interface PracticeRolloutSubPages {
  // Scheme + host to prefix each page path with when linking out.
  origin: string
  area: {
    id: number
    practice_area: string
    path: string
    status: PracticeRolloutStatus
    // Pages the area is known to contain; pages[] only covers the ones with
    // search data in the window, so the two can differ.
    page_count: number
  }
  window: { weeks: number; first_week: string | null; last_week: string | null }
  totals: { pages_with_data: number; flagged: number; impressions: number; clicks: number }
  pages: PracticeRolloutSubPage[]
}

export interface PracticeRolloutBoard {
  window: { start: string; end: string; days: number; excludes_trailing_days: number }
  summary: {
    total_areas: number
    live: number
    rewritten: number
    not_started: number
    cancelled: number
    // total_areas minus cancelled — the denominator for "N of M rebuilt"
    programme_areas: number
    total_pages: number
    total_impressions: number
    demand_share_live: number | null
    demand_share_rewritten: number | null
  }
  areas: PracticeRolloutArea[]
}

export interface PracticeRolloutTimeseries {
  area: {
    id: number
    practice_area: string
    status: PracticeRolloutStatus
    live_at: string | null
    path: string
  }
  series: Array<{
    date: string
    impressions: number | null
    clicks: number | null
    position: number | null
    sessions: number | null
  }>
  markers: Array<{ date: string; event_type: string; label: string }>
  // null until the area has gone live; 'too_early' until a few days have passed.
  comparison:
    | null
    | { status: 'too_early'; days_since_live: number }
    | {
        status: 'ready'
        window_days: number
        before: { start: string; end: string }
        after: { start: string; end: string }
        area: { before: PracticeRolloutMetrics; after: PracticeRolloutMetrics }
        control_group: {
          area_count: number
          before: PracticeRolloutMetrics
          after: PracticeRolloutMetrics
        }
      }
}

// The dashboard's client stats chart, one payload per window. Each block
// carries its own availability flags — "not connected" and "genuinely zero"
// are different states and the chart must never draw the first as the second.
export interface ClientStatsTimeseries {
  window: { start: string; end: string; days: number }
  gsc: {
    connected: boolean
    has_data: boolean
    // Days where collection failed are absent entirely (a gap), never zeros.
    series: Array<{ date: string; clicks: number; impressions: number; ctr: number; position: number | null }>
  }
  ga4: {
    connected: boolean
    has_data: boolean
    // GA4 sessions from every traffic source. Uncollected days are absent (a
    // gap) and the series ends yesterday — today is never a finished day.
    series: Array<{ date: string; sessions: number }>
  }
  leads: {
    available: boolean
    // Gap-filled: a quiet day is an explicit zero.
    series: Array<{ date: string; forms: number; calls: number; total: number }>
  }
  markers: Array<{
    date: string
    count: number
    label: string
    items: Array<{ id: number; title: string; content_type: string }>
  }>
}

export interface PracticeRolloutCohortSide {
  impressions: number
  clicks: number
  position: number | null
}

export interface PracticeRolloutCohort {
  window: { start: string; end: string; days: number; excludes_trailing_days: number }
  cohort: { area_count: number; page_count: number; areas: string[] }
  series: PracticeRolloutTimeseries['series']
  markers: PracticeRolloutTimeseries['markers']
  // null with no live areas; 'too_early' while every live area is under three settled days.
  // Each area is measured over equal windows around its own go-live (capped at 28 days a
  // side) and the windows are summed; the control ratio is the counterfactual change the
  // untouched areas would predict for the same mix of windows.
  comparison:
    | null
    | { status: 'too_early'; areas_too_early: number }
    | {
        status: 'ready'
        areas_included: number
        areas_too_early: number
        max_window_days: number
        aggregate: { before: PracticeRolloutCohortSide; after: PracticeRolloutCohortSide }
        control_group: {
          area_count: number
          impressions_ratio: number | null
          position_gain: number | null
        }
      }
}

// Impressions ratio of a rebuilt area divided by the same ratio for the areas
// still on their original pages over the identical dates. 1.0 = the rebuild
// changed nothing; 2.26 = the area held more than twice the demand it would
// have held untouched. Site-wide impressions have been falling all summer, so
// this multiplier — not the raw before/after — is the verdict.
export type PracticeRolloutVerdict = 'ahead' | 'in_line' | 'behind'

export interface PracticeRolloutVsControlArea {
  id: number
  practice_area: string
  slug: string
  path: string
  page_count: number
  live_at: string | null
  // True when older pages were 301'd into this area. Redirect equity moves over
  // weeks, so these areas can read flat for a while after go-live.
  has_redirect_consolidation: boolean
  status: 'ready' | 'too_early' | 'not_live' | 'no_data'
  days_since_live: number | null
  window?: {
    days: number
    before: { start: string; end: string }
    after: { start: string; end: string }
    shortened_by_history: boolean
  }
  impressions?: { before: number; after: number; ratio: number | null; vs_control: number | null }
  clicks?: { before: number; after: number }
  // delta and delta_vs_control are negative when the area gained rank, since
  // position 4 outranks position 9.
  position?: {
    before: number | null
    after: number | null
    delta: number | null
    delta_vs_control: number | null
  }
  control?: {
    area_count: number
    impressions_before: number
    impressions_after: number
    impressions_ratio: number | null
    position_delta: number | null
  }
  verdict?: PracticeRolloutVerdict | null
}

export interface PracticeRolloutVsControl {
  window_end: string
  settings: {
    min_post_days: number
    excludes_trailing_days: number
    history_starts_on: string | null
    ahead_at: number
    behind_at: number
  }
  areas: PracticeRolloutVsControlArea[]
  // null until at least one area clears min_post_days
  aggregate: null | {
    areas_included: number
    areas_too_early: number
    impressions: { before: number; after: number; ratio: number | null; vs_control: number | null }
    clicks: { before: number; after: number }
    position: { before: number | null; after: number | null; delta_vs_control: number | null }
    verdict: PracticeRolloutVerdict | null
  }
}

// ---------------------------------------------------------------------------
// Spanish translation drift
// ---------------------------------------------------------------------------

// not_live   — no Spanish page has been created for this pair yet
// twin_missing — one side 404s; it was deleted or the id is wrong
// stale      — the English page changed after the Spanish one was last touched
// in_sync    — both checked, Spanish no older than English
// unknown    — never successfully checked
export type EsTranslationState = 'not_live' | 'twin_missing' | 'stale' | 'in_sync' | 'unknown'

export interface EsTranslationPair {
  id: number
  label: string
  state: EsTranslationState
  en: {
    post_id: number
    path: string
    modified_at: string | null
    revisions_total: number | null
    // Edits to the English page since the Spanish one was written. Null when
    // the revisions endpoint could not be read — the dates still carry the
    // verdict, just without a count.
    revisions_since_es: number | null
    missing: boolean
  }
  es: {
    post_id: number | null
    path: string
    modified_at: string | null
    missing: boolean
  }
  drift_days: number | null
  last_checked_at: string | null
  last_error: string | null
}

export interface EsTranslationBoard {
  pairs: EsTranslationPair[]
  summary: {
    total: number
    in_sync: number
    stale: number
    not_live: number
    twin_missing: number
    unknown: number
  }
  // Oldest check across the pairs — the card is only as fresh as its least
  // fresh row.
  last_checked_at: string | null
  errors: number
}

export interface ChecklistItem {
  key: string
  label: string
  section: 'a' | 'b'
  status: 'pending' | 'checked' | 'flagged'
  source_url?: string
  source_domain?: string
  // null = no source attached yet; false = source present but off-allowlist;
  // true = source on the per-site approved domains list.
  domain_allowed?: boolean | null
  verdict?: 'supported' | 'contradicted' | 'unsubstantiated' | 'mixed'
  note?: string
  checked_by_user_id?: number | null
  checked_at?: string | null
}

export interface ChecklistResponse {
  section_a: ChecklistItem[]
  section_b: ChecklistItem[]
  section_a_completed_at: string | null
  section_b_completed_at: string | null
  // Server-computed approval gate. UI mirrors this on the Approve button —
  // when false, `blockers` lists the human-readable reasons (rendered in a tooltip).
  can_approve: boolean
  blockers: string[]
}

export interface ApprovedDomains {
  legal: string[]
  medical: string[]
  other?: string[]
}

// =============================================================================
// CONTENT CLUSTER + SITE PROTOCOL TYPES (FOR-127 Phase 2 / FOR-132)
// =============================================================================
export interface SiteProtocol {
  id: number
  // Brand-locked values that must appear verbatim across every page (phone,
  // email, byline, attorney bio, target city list). Backend injects these into
  // every prompt via the site protocol context.
  locked_fields: {
    phone?: string
    email?: string
    byline?: string
    attorney_bio_md?: string
    near_you_cities?: string[]
  } | null
  // URL pattern conventions for internal pillar/spoke linking. Each entry is a
  // named pattern pair (ex: { name: "Practice Areas", pattern_pillar:
  // "/practice-areas/{slug}", pattern_spoke: "/practice-areas/{pillar}/{slug}" }).
  internal_link_conventions: { name: string; pattern_pillar: string; pattern_spoke: string }[] | null
  // Site-specific legal exception notes injected into prompts (markdown).
  legal_exceptions_md: string | null
  // General tone/voice notes for prompts (markdown).
  house_style_md: string | null
  // Long verified fact packs. Split at headings; each article gets only the
  // sections that apply to its topic (API SiteReference).
  reference_md: string | null
  // Reverse-chronological list of lessons captured from previous content runs
  // (auto-generated during fact-check / verdict pipeline, or manually appended).
  // Surfaced read-only on the cluster dashboard timeline. Phase 3 / FOR-133.
  lessons_learned: LessonEntry[] | null
  updated_at: string
  updated_by: { id: number; name: string } | null
}

// =============================================================================
// FIRM PROFILE TYPES (FOR-139 / Phase 1 of FOR-138)
// =============================================================================
// Per-site, list-shaped trust scaffolding the page generator pulls into prompts.
// Distinct from SiteProtocol (singleton, locked-field config) — these are
// many-rows-per-site lists the firm grows over time. Reusable beyond the page
// generator: blog generation will pull case results too, and future CTA/widget
// blocks will render testimonials directly from the same source.

export interface CaseResult {
  id: number
  site_id: number
  title: string
  // Canonical decimal amount; null when bound by confidentiality.
  amount_usd: number | null
  // Display-time override ("Confidential settlement", "Over $3M", "$3.3M").
  // Backend's display_amount falls back to formatted amount_usd when blank.
  amount_label: string | null
  display_amount: string | null
  year: number | null
  practice_area: string | null
  description: string | null
  attorney_id: string | null
  display_order: number
  is_active: boolean
  // Featured rows are auto-included in every relevant generation regardless
  // of practice-area match.
  featured: boolean
  created_at: string
  updated_at: string
}

export interface Testimonial {
  id: number
  site_id: number
  client_initials: string
  client_location: string | null
  content: string
  rating: number | null
  practice_area: string | null
  attorney_id: string | null
  // Optional link to the case result this testimonial is about.
  case_result_id: number | null
  display_order: number
  is_active: boolean
  featured: boolean
  created_at: string
  updated_at: string
}

export interface CompetitorReference {
  id: number
  site_id: number
  // Bare host (no scheme, no path). Backend normalises on save, so a pasted
  // "https://www.example.com/pricing" comes back as "www.example.com".
  domain: string
  // Optional — a competitor can be added as a URL alone.
  firm_name: string | null
  // firm_name, or the domain when it's empty. Use this for display.
  display_name: string
  // AI Visibility: names that count as this competitor being named in an AI
  // answer. Empty means the answer is matched on firm_name.
  aliases?: { alias: string; case_sensitive: boolean }[]
  notes: string | null
  // Free-string practice-area tags. Empty array means "applies to all".
  default_practice_areas: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

// FOR-144 / Phase 3.5b — site-curated example pages the page generator
// auto-injects as house-style benchmarks (distinct from per-topic reference
// URLs which live on the brief and refresh per page).
export interface PageExample {
  id: number
  site_id: number
  title: string
  source_url: string | null
  content: string
  practice_area: string | null
  is_active: boolean
  // Featured rows ship in every page-generator run regardless of practice area.
  featured: boolean
  // Computed server-side from the content blob — the table list uses it
  // without re-counting client-side.
  word_count: number
  created_at: string
  updated_at: string
}

// =============================================================================
// PAGE BRIEF + CITATIONS + LESSONS TYPES (FOR-127 Phase 3 / FOR-133)
// =============================================================================
// PageBrief is a per-topic editorial brief that gates workflow runs when the
// topic.requires_page_brief flag is set. It must be in 'approved' status before
// the run-workflow mutation will fire — the UI mirrors this with an inline
// gate dialog. SourceCitation rows are produced by the verification pipeline
// and aggregate up to TopSourceStat per host on the cluster dashboard. LessonEntry
// is read-only on the dashboard timeline (lives inside SiteProtocol.lessons_learned).

// Single competitor reference URL on a PageBrief. The user pastes the URL;
// scrape state lives on the same row so the UI can render per-row badges.
// FOR-140 / Phase 2 of FOR-138.
export interface PageBriefReferenceUrl {
  url: string
  scraped_content?: string | null
  // 'pending' = added but not fetched yet, 'ok' = scrape succeeded,
  // 'failed' = scrape errored (manual_content fallback recommended).
  scrape_status?: 'pending' | 'ok' | 'failed' | null
  scraped_at?: string | null
  scrape_error?: string | null
  // User-pasted markdown that overrides the scraped version when the
  // competitor blocks scraping or returns junk.
  manual_content?: string | null
}

// Context Preview persisted selection state. All keys optional; null means
// "use default selection." Controls which Firm Profile rows / reference URLs
// / ambient sections get pulled into the system prompt for this brief's runs.
// FOR-141 / Phase 3 of FOR-138.
export interface PageBriefContextOverrides {
  case_result_ids?: number[]
  testimonial_ids?: number[]
  competitor_ref_ids?: number[]
  // Indices into the brief's reference_urls array (no per-row id since it's
  // a JSON column). Empty array means "include none of the references."
  reference_url_indices?: number[]
  include_protocol?: boolean
  include_cluster_assets?: boolean
  include_ahrefs?: boolean
}

export interface PageBrief {
  id: number
  topic_id: number
  title: string | null
  target_keyword: string | null
  byline_override: string | null
  // Internal link targets the article should weave in. Each entry pairs a
  // destination URL with the anchor text the writer should use.
  internal_link_targets: { url: string; anchor_text: string }[] | null
  overlap_notes: string | null
  additional_instructions: string | null
  // FOR-140 — competitor reference pages the page generator scrapes via Jina.
  reference_urls: PageBriefReferenceUrl[]
  // FOR-141 — Context Preview persisted selection. Null when defaults apply.
  context_overrides: PageBriefContextOverrides | null
  // 'draft' until reviewed; 'approved' enables the workflow run for topics
  // that require a brief.
  status: 'draft' | 'approved'
  approved_by: { id: number; name: string } | null
  approved_at: string | null
  generated_by_claude_at: string | null
  updated_at: string
}

// Response shape for POST /workflows/{id}/preview-context. FOR-141.
export interface ContextPreviewResponse {
  workflow_id: number
  topic_id: number | null
  content_item_id: number | null
  context: string
  estimated_tokens: number
  character_count: number
}

// Per-row outcome from POST /topics/bulk-import-pages. FOR-141.
export interface BulkImportPageRow {
  target_url?: string | null
  h1?: string | null
  title?: string | null
  keyword: string
  topic?: string | null
  word_count_range?: string | null
  type?: string | null
}

export interface BulkImportPagesResponse {
  success: boolean
  imported: number
  created: { row: number; topic_id: number; title: string; slug: string }[]
  error_count: number
  errors: { row: number; error: string }[]
}

export interface SourceCitation {
  id: number
  content_item_id: number
  url: string
  host: string
  title: string | null
  snippet: string | null
  // null when the verifier didn't reach a verdict (or hasn't run yet).
  verdict: 'supported' | 'contradicted' | 'unsubstantiated' | 'mixed' | null
  // null when no allowlist comparison was made (e.g. before settings configured).
  domain_allowed: boolean | null
  is_primary_source: boolean
  cited_at: string
}

export interface TopSourceStat {
  host: string
  count: number
  allowed_count: number
  contradicted_count: number
  last_used_at: string
}

export interface LessonEntry {
  id: string
  content_item_id: number
  content_title: string
  category: 'source_quality' | 'tone' | 'structure' | 'factual_correction' | 'other'
  lesson: string
  // True when captured automatically from a verification/fact-check run; false
  // when manually appended by an editor.
  auto_generated: boolean
  recorded_at: string
}

// =============================================================================
// ADMIN PERMISSIONS (GET /admin/admin-permissions)
export interface AdminPermissionRegistry {
  groups: Array<{
    title: string
    permissions: Array<{ key: string; label: string; description: string; owner_only: boolean }>
  }>
  /** The keys the owner may grant; owner-only keys never are. */
  grantable: string[]
}

// CLIENT HEALTH MATRIX + GOALS TYPES (Agency Hub Phase 1)
// =============================================================================
// The Client Health matrix is a per-license grid: one row per licensed client,
// one cell per delivery service (blog/glossary/pseo/news/page/gmb) plus Leads
// and Report columns. Each cell carries a `health` traffic-light computed by the
// backend against the client's target for the selected period. Goals are the
// weekly/monthly ritual objects grouped by scope (team / per-person / per-client).

// Traffic-light health used across every service/leads/report cell. `gray` means
// "not a configured service" (the client isn't paying for it), not "unknown".
export type HealthColor = "green" | "amber" | "red" | "gray"

// A single service cell in the matrix. `configured` false ⇒ health "gray" and the
// remaining fields are null/zero. `client_service_id` is the catalog row id (null
// when not configured) — used to target PUT/DELETE from the Manage Services dialog.
export interface ServiceCell {
  configured: boolean
  status: string | null
  published: number
  target: number | null
  target_period: "week" | "month" | null
  health: HealthColor
  client_service_id: number | null
}

// Leads column — a count with an optional target for the period.
export interface LeadsCell {
  count: number
  target: number | null
  health: HealthColor
}

// Report column — whether the recurring client report is scheduled and when it
// last went out. health "green" = sent this period, "amber" = due, "gray" = none.
export interface ReportCell {
  scheduled: boolean
  frequency: string | null
  last_sent_at: string | null
  health: HealthColor
}

// Manual "needs attention" flag an admin can toggle per client, with a note.
export interface ClientAttention {
  flagged: boolean
  note: string | null
  updated_at: string | null
  updated_by: { id: number; name: string } | null
}

// One client row. `services` is keyed by service slug; every slug in the matrix's
// top-level `services` array is always present here (configured or not).
export interface ClientHealthRow {
  site_id: number
  license_id: number
  client_name: string
  domain: string
  attention: ClientAttention
  services: Record<string, ServiceCell>
  leads: LeadsCell
  report: ReportCell
}

export interface ClientHealthPeriod {
  start: string
  end: string
  type: "week" | "month"
}

export interface ClientHealthMatrix {
  period: ClientHealthPeriod
  // Ordered service slugs — drives the matrix column order.
  services: string[]
  clients: ClientHealthRow[]
}

// A catalog row: a service the client is signed up for, with its target. Managed
// via the Manage Services dialog. `service` is one of the eight slugs (the six
// matrix services plus 'leads' and 'report').
export interface ClientService {
  id: number
  site_id: number
  service: string
  status: "active" | "paused"
  target_count: number | null
  target_period: "week" | "month"
  // Date-only ("YYYY-MM-DD") the client started on this service; null if unset.
  started_at: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

// One Mon–Fri cell on the Team page: recap submission + clocked hours for a
// member on a date. Fed by HireTalent webhooks merged with legacy ManagePH
// recap tasks.
export interface TeamDayCell {
  date: string
  weekday: string
  recap_at: string | null
  hours: number | null
}

export interface TeamMemberWeek {
  // Null when a HireTalent email doesn't match any Forge user.
  user: { id: number; name: string; avatar: string | null } | null
  email: string
  days: TeamDayCell[]
  goal: { id: number; title: string; status: GoalStatus } | null
}

// Where the Team roster comes from, so the page can badge it clearly.
// source 'forge_users' = Forge accounts (default); 'hiretalent' = synced roster,
// with environment 'staging' (test data) or 'production' (the real team).
export interface RosterSource {
  source: "forge_users" | "hiretalent"
  environment: "staging" | "production" | null
  synced_at: string | null
  // The HireTalent employer account this roster is pulled from, and where to
  // fix it if it's the wrong account.
  account_label: string | null
  manage_key_url: string | null
}

export interface TeamWeek {
  week: { start: string; end: string }
  roster_source: RosterSource
  members: TeamMemberWeek[]
}

// Admin-managed HireTalent connection. Every value can be saved in Forge, and
// what's saved overrides the server's HIRETALENT_* env vars — `*_source` says
// which of the two is currently answering.
export type HiretalentSettingSource = "database" | "env" | "unset"

export interface HiretalentSettings {
  configured: boolean
  base_url: string | null
  base_url_source: HiretalentSettingSource
  environment: "staging" | "production" | null
  api_key_set: boolean
  // Masked hint only (e.g. `htk_live_…F8lM5`) — the key is never returned.
  api_key_masked: string | null
  api_key_source: HiretalentSettingSource
  account_label: string | null
  account_label_source: HiretalentSettingSource
  manage_key_url: string | null
  roster_source_enabled: boolean
  roster_source_enabled_source: HiretalentSettingSource
  roster: { active: number; inactive: number; unmatched: number; synced_at: string | null }
  // Recaps and hours arrive by webhook: the address and events to register in
  // HireTalent, the whsec_ secret it shows (masked here), and the last delivery.
  webhook: {
    url: string
    events: string[]
    secret_set: boolean
    secret_masked: string | null
    secret_source: HiretalentSettingSource
    status: { last_received_at: string | null; last_ok_at: string | null; last_status: number | null; last_error: string | null }
  }
}

export interface HiretalentForgeUser {
  id: number
  name: string
  email: string
}

export interface HiretalentEmployee {
  id: number
  external_id: number | null
  name: string
  email: string
  title: string | null
  status: "active" | "inactive"
  match_status: "automatic" | "manual" | "unmatched"
  user: HiretalentForgeUser | null
}

export interface HiretalentConnectionTest {
  ok: boolean
  employee_count: number
  has_more: boolean
  sample: string[]
  error: string | null
}

export type GoalScope = "team" | "user" | "client"
export type GoalPeriod = "week" | "month"
export type GoalStatus = "on_track" | "at_risk" | "achieved" | "missed"

export interface Goal {
  id: number
  scope: GoalScope
  // Populated only for scope 'user'.
  user: { id: number; name: string; avatar: string | null } | null
  // Populated only for scope 'client'.
  site_id: number | null
  client_name: string | null
  title: string
  description: string | null
  period: GoalPeriod
  period_start: string
  period_end: string
  target_value: number | null
  current_value: number | null
  metric: string | null
  status: GoalStatus
  created_by: { id: number; name: string } | null
  created_at: string
  updated_at: string
}

// =============================================================================
// HUB OVERVIEW TYPES (Team & Client dashboard — Agency Hub)
// =============================================================================
// The Hub Overview is the admin landing dashboard: a KPI strip + a per-client
// "this week vs last week" section. This endpoint returns the KPI rollups and
// the client cards only — the team section is fed by the existing teamDays
// endpoint. Response is bare (no envelope), mirroring clientHealth.getMatrix.

// --- Infrastructure status (admin) ----------------------------------------
// `never_checked` is deliberately distinct from `down`: it means the probe has
// not run yet, which points at the scheduler rather than at the endpoint.
export type InfraStatus = "ok" | "warn" | "down" | "never_checked"

export interface InfraEndpoint {
  key: string
  label: string
  url: string
  owner: string | null
  // Business priority, 1 = matters most. Drives display order; the API already
  // returns them sorted, so don't re-sort in the UI.
  tier: number
  critical: boolean
  status: InfraStatus
  // True when nothing has refreshed this row recently. A stale row must never
  // be rendered as healthy — it is the closest this page can get to reporting
  // that the box hosting it has stopped working.
  stale: boolean
  http_status: number | null
  response_ms: number | null
  tls_expires_at: string | null
  tls_days_remaining: number | null
  tls_issuer: string | null
  error: string | null
  consecutive_failures: number
  last_ok_at: string | null
  status_changed_at: string | null
  checked_at: string | null
}

// `available: false` means the metric could not be read on this host, so the
// UI renders "–" rather than a misleading 0.
export interface InfraHost {
  load: { available: boolean; one?: number; five?: number; fifteen?: number; cores?: number; percent?: number | null }
  memory: { available: boolean; total_mb?: number; used_mb?: number; percent?: number }
  disk: { available: boolean; total_gb?: number; used_gb?: number; percent?: number }
  queue: {
    available: boolean
    pending?: number
    reserved?: number
    failed_recent?: number
    failed_total?: number
    window_hours?: number
    error?: string
  }
  php_version: string
  hostname: string | null
}

/**
 * The body of POST/PUT /admin/agents. `schedule: null` removes the agent's
 * single schedule row; an object upserts it and the API recomputes next_run_at.
 */
export interface AgentWritePayload {
  name: string
  slug?: string
  description?: string | null
  site_id?: number | null
  status?: AgentStatus
  trigger_type?: AgentTriggerType | null
  trigger_config?: Record<string, unknown> | null
  instructions?: string | null
  default_budget?: { max_turns?: number; max_minutes?: number } | null
  kind?: AgentKind
  role_title?: string | null
  avatar?: string | null
  color?: string | null
  tools?: string[] | null
  notify_emails?: string[] | null
  report_repository?: string | null
  report_branch?: string | null
  report_path?: string | null
  model?: string | null
  schedule?: {
    cron_expression: string
    timezone: string
    active: boolean
    brief?: string | null
  } | null
}

export interface InfrastructureStatus {
  overall: { status: "ok" | "warn" | "down"; reasons: string[] }
  endpoints: InfraEndpoint[]
  host: InfraHost
  thresholds: {
    tls_warn_days: number
    disk_warn_percent: number
    queue_pending_warn: number
    stale_after_minutes: number
  }
  generated_at: string
}

// Status pill on each client card. Drives label + color pair:
//  new            → "New / Setup Phase" (blue)
//  needs_attention→ "Needs Attention"   (amber)
//  improving      → "Improving"         (green)
//  stable         → "Stable"            (gray/secondary)
export type StatusPill = "new" | "needs_attention" | "improving" | "stable"

// One week-over-week metric on a client card. `available` false ⇒ the client has
// no data source for it (e.g. no Search Console) — the UI renders "–", not 0.
// `previous_prorated` is the previous week scaled to the days the current
// (in-progress) week covers — set on week-to-date metrics while
// `week.complete` is false, null otherwise. Compare against it when present so
// a Wednesday doesn't read as a collapse against seven full days.
export interface HubMetric {
  current: number
  previous: number
  previous_prorated: number | null
  available: boolean
}

// Same available/"–" semantics as HubMetric, but the numbers may be null even
// when the source is `available` (GA4 sessions before the window reports; GSC
// impressions/clicks the connection works for but Search Console hasn't
// reported yet). The UI renders "–" when unavailable or when current is null.
export interface HubNullableMetric {
  current: number | null
  previous: number | null
  previous_prorated: number | null
  available: boolean
}

export interface HubClientCard {
  site_id: number
  license_id: number
  client_name: string
  domain: string
  status_pill: StatusPill
  // Reuses the same manual attention shape the client-health matrix embeds; the
  // card surfaces `note` in its Notes box.
  attention: ClientAttention
  metrics: {
    // GSC pair — nullable while Search Console hasn't reported the window.
    impressions: HubNullableMetric
    clicks: HubNullableMetric
    // GA4 sessions — nullable current/previous, same rendering as impressions.
    sessions: HubNullableMetric
    leads: HubMetric
    blogs_published: HubMetric
    pages_published: HubMetric
  }
}

export interface HubOverview {
  // `days_elapsed` (1–7) is how much of the week the week-to-date metrics
  // cover; `complete` is days_elapsed === 7.
  week: { start: string; end: string; days_elapsed: number; complete: boolean }
  prev_week: { start: string; end: string }
  // GSC reporting lag window. When `lagged`, the Impressions/Clicks numbers on
  // every client card cover the last COMPLETED week (an in-progress week is
  // structurally partial because Search Console reports ~2–3 days late).
  // `start`/`end` bound it.
  gsc_window: { lagged: boolean; start: string; end: string }
  kpis: {
    team: { members: number; recaps_done: number; recaps_prev: number; hours_total: number; hours_prev: number }
    clients: { total: number; on_track: number; behind: number; needs_attention: number }
    reports: { sent: number; sent_prev: number }
    attention: { count: number }
  }
  clients: HubClientCard[]
}

// =============================================================================
// Google service-account access inventory (Search Console + GA4)
// =============================================================================
// Per-site data-flow status used by the admin overview's data-sources lights:
//  flowing_oauth  → data arriving via a per-client OAuth connection (green)
//  flowing_sa     → data arriving via the shared service account (green)
//  sa_visible     → the service account CAN see the property but it isn't wired
//                   yet — press Sync to connect (amber)
//  none           → the service account has no access; grant it manually (red)
export type GoogleSaStatus = "flowing_oauth" | "flowing_sa" | "sa_visible" | "none"

export interface GoogleSaSite {
  site_id: number
  domain: string
  auth_type: "oauth" | "service_account" | null
  connection_email: string | null
  gsc_status: GoogleSaStatus
  gsc_property: string | null
  ga4_status: GoogleSaStatus
  ga4_property: string | null
  // When ga4_status is `sa_visible`, the best-guess GA4 property the SA can see.
  ga4_candidate: string | null
  last_query_at: string | null
  // Deep links to the Search Console users page / GA4 admin, pre-scoped so the
  // admin can add the service-account email as a user.
  gsc_grant_url: string
  ga4_grant_url: string
}

export interface GoogleSaAccess {
  configured: boolean
  sa_email: string | null
  inventory_error: string | null
  checked_at: string
  sites: GoogleSaSite[]
}

// =============================================================================
// VERSION HISTORY
// =============================================================================
// Shared shape returned by every versioned resource (CTAs, workflows, site
// settings). The backend serves these from one helper, so the response is the
// same regardless of which resource asked — which is what lets a single
// EntityVersionHistory panel drive all of them.
// =============================================================================

/** One recorded change. `user` is null for edits made outside a request. */
export interface EntityVersion {
  id: number
  version: number
  is_snapshot: boolean
  created_at: string
  changed_fields?: string[]
  user?: { id: number; name: string; email: string } | null
}

export interface EntityVersionList {
  success: boolean
  current_version: number
  versions: EntityVersion[]
}

export interface EntityVersionAttributes {
  success: boolean
  version: number
  attributes: Record<string, any>
}

// =============================================================================
// API ENDPOINTS
// =============================================================================
// Organized by resource. Each section maps to a backend controller.
// Response types are inline for IDE autocomplete.
// Use with TanStack Query: useQuery({ queryFn: () => api.resource.method() })
// =============================================================================

export interface TeamResponsibilities {
  id: number
  name: string
  email: string
  avatar: string | null
  forge_access: string | null
  responsibilities: string[]
  updated_at: string | null
  updated_by: { id: number; name: string } | null
}

export const api = {
  // License/Auth - verify user has valid license for site
  license: {
    check: (licenseKey: string) =>
      apiClient.get<{ valid: boolean; client_name?: string; domain?: string }>("/license/check", {
        headers: { "X-License-Key": licenseKey },
      }),

    verify: () =>
      apiClient.get<{ valid: boolean; client_name: string; domain: string }>("/license/verify"),
  },

  // Workflows - AI content generation pipelines (see WorkflowController.php)
  // Workflows contain steps → each step runs a prompt → output goes to ContentItem
  workflows: {
    // Version history (Laravel Rewind). Covers the workflow record only —
    // `covers_steps` is false, the step chain is versioned separately.
    getVersions: (id: number) =>
      apiClient.get<EntityVersionList & { covers_steps: boolean }>(`/workflows/${id}/versions`),

    getVersion: (id: number, version: number) =>
      apiClient.get<EntityVersionAttributes>(`/workflows/${id}/versions/${version}`),

    restoreVersion: (id: number, version: number) =>
      apiClient.post<{ success: boolean; message: string; covers_steps: boolean; workflow: Workflow }>(
        `/workflows/${id}/versions/${version}/restore`
      ),

    list: (params?: { is_active?: boolean; is_page_generator?: boolean }) =>
      apiClient.get<{ data: Workflow[] }>("/workflows", { params }),

    get: (id: number) =>
      apiClient.get<{ workflow: Workflow }>(`/workflows/${id}`),

    create: (data: Partial<Workflow>) =>
      apiClient.post<{ workflow: Workflow }>("/workflows", data),

    update: (id: number, data: Partial<Workflow>) =>
      apiClient.put<{ workflow: Workflow }>(`/workflows/${id}`, data),

    delete: (id: number) =>
      apiClient.delete(`/workflows/${id}`),

    queueExecution: (id: number, data: { input_data: Record<string, unknown>; metadata?: unknown }) =>
      apiClient.post(`/workflows/${id}/queue`, data),

    queueBatch: (id: number, data: { topic_ids: number[] }) =>
      apiClient.post(`/workflows/${id}/queue-batch`, data),

    queueBatchEnhanced: (id: number, data: {
      topic_ids: number[]
      additional_instructions?: string
      replace_existing?: boolean
      additional_variables?: Record<string, string>
      perTopicVariables?: Record<number, Record<string, string>> // Per-topic variable map
    }) =>
      apiClient.post<{
        message: string
        queued_count: number
        topic_ids: number[]
      }>(`/workflows/${id}/queue-batch-enhanced`, data),

    getRuns: (id: number) =>
      apiClient.get<{ data: any[] }>(`/workflows/${id}/runs`),

    getRun: (workflowId: number, runId: number) =>
      apiClient.get<{ run: any }>(`/workflows/${workflowId}/runs/${runId}`),

    getRunDetails: (runId: number) =>
      apiClient.get<{ success: boolean; run: WorkflowRunDetails }>(`/workflow-runs/${runId}/details`),

    // Every run that produced (or is producing) a content item, newest first.
    // Reaches queued/failed runs too — generation_config.workflow_run_id is
    // only written on completion, so it can't be the only path in.
    getRunsForContent: (contentId: number) =>
      apiClient.get<{ data: ContentWorkflowRun[] }>(`/content/${contentId}/workflow-runs`),

    getAllRuns: () =>
      apiClient.get<{ data: any[] }>("/workflow-runs"),
  },

  // Topics - Content ideas/titles before they become articles
  // Status flow: pending → approved → queued → writing → content_created → published
  // Topics can be: AI-generated, CSV imported, or manually created
  topics: {
    list: (params?: {
      workflow_id?: number
      source?: string
      generation_id?: number
      page?: number
      per_page?: number
      category?: string
      content_type?: string
      // FOR-143 / Phase 3.5a — exclude content types (comma-separated). Used
      // by the "Articles" pill to hide page-typed topics. NULL content_type
      // is included in results (legacy topics don't disappear).
      exclude_content_type?: string
      status?: string
      statuses?: string[]  // For workflow stage tabs - filters by multiple statuses
      display_stage?: string  // Simplified 3-tab filter: pending/approved/rejected
      content_status?: string  // Sub-filter within approved tab: draft/review/published/failed/no_content
      tag_ids?: string
      // FOR-155 — server-side LIKE search across title + description so
      // results span all pages, not just the currently rendered one.
      search?: string
    }) =>
      apiClient.get<{
        data: Topic[]
        current_page: number
        last_page: number
        per_page: number
        total: number
        from: number
        to: number
      }>("/topics", { params }),

    // Get counts for each workflow stage tab
    getStageCounts: (params?: {
      category?: string
      content_type?: string
      generation_id?: number
    }) =>
      apiClient.get<{
        counts: Record<string, number>
      }>("/topics/stage-counts", { params }).then(res => res.counts),

    get: (id: number) =>
      apiClient.get<{ topic: Topic }>(`/topics/${id}`),

    create: (data: Partial<Topic> & { skip_similarity_check?: boolean }) =>
      apiClient.post<{
        success: boolean;
        topic: Topic;
        similar_topics?: Array<{
          id: number;
          title: string;
          category: string;
          status: string;
          similarity: number;
        }>;
      }>("/topics", data),

    // Check for similar topics before creating (uses AI embeddings)
    checkSimilar: (title: string, threshold?: number) =>
      apiClient.post<{
        has_similar: boolean;
        similar_topics: Array<{
          id: number;
          title: string;
          category: string;
          status: string;
          similarity: number;
        }>;
        embedding_enabled: boolean;
      }>("/topics/check-similar", { title, threshold }),

    update: (id: number, data: Partial<Topic>) =>
      apiClient.patch<{ topic: Topic }>(`/topics/${id}`, data),

    delete: (id: number) =>
      apiClient.delete(`/topics/${id}`),

    // Export topics as CSV (FOR-75)
    exportCsv: async (params?: {
      display_stage?: string
      category?: string
      content_type?: string
      tag_ids?: string
    }) => {
      const response = await apiClient.get<Blob>("/topics/export-csv", {
        params,
        responseType: "blob",
      })
      const url = URL.createObjectURL(response)
      const a = document.createElement("a")
      a.href = url
      a.download = "topics-export.csv"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    // Translate a topic title to English (FOR-76)
    translate: (id: number) =>
      apiClient.post<{ topic: Topic }>(`/topics/${id}/translate`),

    // Run competitor research for a topic (FOR-87)
    researchCompetitors: (id: number) =>
      apiClient.post<{ message: string; topic_id: number }>(`/topics/${id}/research`),

    // Topic generation history. The generator is gone; these rows still label
    // which batch a topic came from in the library's filter.
    getRecentGenerations: () =>
      apiClient.get<{
        generations: Array<{
          id: number
          quantity: number
          total_topics: number
          status: string
          created_at: string
          label: string
        }>
      }>("/topics/generate/recent"),

    getCategories: () =>
      apiClient.get<{
        categories: Array<{
          category: string
          category_slug: string
          topic_count: number
          avg_priority: number
        }>
      }>("/topics/categories"),

    uploadCsv: (file: File) => {
      const formData = new FormData()
      formData.append("file", file)
      return apiClient.post<{
        success: boolean
        headers: string[]
        row_count: number
        preview: Record<string, string>[]
        message: string
      }>("/topics/upload-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    },

    importCsv: (data: { file: File; title_column: string; category_column?: string; variable_mapping: Record<string, string>; workflow_id?: number }) => {
      const formData = new FormData()
      formData.append("file", data.file)
      formData.append("title_column", data.title_column)
      if (data.category_column) {
        formData.append("category_column", data.category_column)
      }
      if (data.workflow_id) {
        formData.append("workflow_id", data.workflow_id.toString())
      }
      // Send as bracket notation so Laravel parses it as an array
      Object.entries(data.variable_mapping).forEach(([key, value]) => {
        formData.append(`variable_mapping[${key}]`, value)
      })
      return apiClient.post<{ success: boolean; imported: number; errors: string[]; error_count: number }>("/topics/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    },

    bulkCreate: (topics: Array<{ title: string; category?: string }>) =>
      apiClient.post<{ success: boolean; imported: number; errors: string[]; error_count: number }>("/topics/bulk-create", { topics }),

    readyToWrite: (params?: {
      category?: string
      content_type?: string
      page?: number
      per_page?: number
    }) =>
      apiClient.get<{
        data: Topic[]
        current_page: number
        last_page: number
        per_page: number
        total: number
        from: number
        to: number
      }>("/topics/ready-to-write", { params }),

    checkDuplicates: (topicIds: number[]) =>
      apiClient.post<{
        duplicates: Record<number, {
          existing_id: number
          existing_title: string
          existing_status: string
          existing_slug: string
        }>
        checked_count: number
        duplicate_count: number
      }>("/topics/check-duplicates", { topic_ids: topicIds }),

    bulkUpdateStatus: (topicIds: number[], status: 'pending' | 'approved' | 'rejected' | 'content_created' | 'published', rejectionData?: {
      rejection_reason?: string
      rejection_reason_custom?: string
      rejection_blocklist?: boolean
    }) =>
      apiClient.post<{
        message: string
        updated_count: number
      }>("/topics/bulk-status", { topic_ids: topicIds, status, ...rejectionData }),

    bulkDelete: (topicIds: number[]) =>
      apiClient.post<{
        message: string
        deleted_count: number
      }>("/topics/bulk-delete", { topic_ids: topicIds }),

    runAutomation: (data: {
      topic_ids: number[]
      workflow_id: number
      auto_approve_content?: boolean
      auto_schedule_to_wordpress?: boolean
      schedule_config?: {
        posts_per_day?: number
        start_date?: string
        start_time?: string
        connection_type?: 'production' | 'staging'
        category_id?: number | null
      }
    }) =>
      apiClient.post<{
        message: string
        dispatched_count: number
        skipped_count: number
      }>("/topics/run-automation", data),

    scanDuplicates: (data: {
      topic_ids?: number[]
      generation_id?: number
      threshold?: number
    }) =>
      apiClient.post<{
        message: string
        topics_scanned: number
        duplicates_found: number
        duplicates: Array<{
          topic_id: number
          topic_title: string
          topic_status: string
          topic_category: string
          match_type: 'topic' | 'content' | 'wordpress' | 'batch_duplicate'
          match_id: number
          match_title: string
          similarity: number
          is_same_topic: boolean
        }>
        threshold: number
        existing_content_scan?: {
          available: boolean
          missing: string[]
        }
      }>("/topics/scan-duplicates", data),
  },

  // Content - Articles/content items with WordPress sync capabilities
  // Source can be: generated (from workflows), manual, wordpress (imported)
  // Supports: WP publish, version history (Rewind), featured image generation
  content: {
    list: (params?: { status?: string; statuses?: string[]; source?: string; type?: string; exclude_type?: string; search?: string; page?: number; per_page?: number; sort?: string; order?: 'asc' | 'desc' }) =>
      apiClient.get<{
        data: ContentItem[]
        total: number
        page: number
        per_page: number
        total_pages: number
      }>("/content", { params }),

    // Get counts for each workflow stage tab
    getStageCounts: (params?: { type?: string; exclude_type?: string; source?: string }) =>
      apiClient.get<{ counts: Record<string, number> }>("/content/stage-counts", { params }).then(res => res.counts),

    create: (data: { title: string; content_type?: string; source?: string; content_body?: string; meta_description?: string; skip_similarity_check?: boolean }) =>
      apiClient.post<{
        content: ContentItem
        similar_content?: Array<{ id: number; title: string; status: string; similarity: number }>
        has_similar?: boolean
      }>("/content", data),

    checkSimilar: (data: { title: string; body?: string; threshold?: number }) =>
      apiClient.post<{
        similar: Array<{ id: number; title: string; status: string; similarity: number }>
        has_similar: boolean
      }>("/content/check-similar", data),

    get: (id: number) =>
      apiClient.get<{ content: ContentItem }>(`/content/${id}`),

    // Lightweight generation-status probe — the editor polls this instead of
    // refetching the full article (body included) every 3 seconds.
    getStatus: (id: number) =>
      apiClient.get<{ success: boolean; id: number; status: string; error_message: string | null; updated_at: string }>(`/content/${id}/status`),

    // A/B generation: dispatch one topic/title through two workflows at once.
    // Creates two linked ContentItems (shared ab_group_id, variant labels
    // "A"/"B") and queues a generation job for each. The two workflow_ids must
    // be distinct UNLESS variant_b_instructions is provided — in which case the
    // same workflow may be passed twice ("same pipeline, extra guidance on B").
    // variant_b_instructions shapes variant B's generation (threaded as
    // _additional_instructions), not just stored.
    abGenerate: (data: {
      input_data: Record<string, unknown>
      workflow_ids: [number, number]
      variant_b_instructions?: string
      topic_id?: number
      metadata?: Record<string, unknown>
    }) =>
      apiClient.post<{
        message: string
        ab_group_id: string
        items: Array<{ content_item_id: number; variant_label: string; workflow_id: number; workflow_name: string }>
      }>("/workflows/ab-generate", data),

    // Both members of an A/B pair by their shared ab_group_id (for the compare
    // screen). Items come back ordered A then B, with full content_body.
    abGroup: (groupId: string) =>
      apiClient.get<{ success: boolean; ab_group_id: string; items: ContentItem[] }>(`/content/ab-group/${groupId}`),

    // Pick the winning variant of an A/B pair. Stamps ab_is_winner true on the
    // chosen item, false on the sibling, ab_decided_at on both. Idempotent —
    // re-calling with the other id switches the decision. Status is untouched.
    abWinner: (groupId: string, contentItemId: number) =>
      apiClient.post<{ success: boolean; ab_group_id: string; winner_id: number; items: ContentItem[] }>(
        `/content/ab-group/${groupId}/winner`,
        { content_item_id: contentItemId },
      ),

    // Resolve which client/license owns a content item, across ALL the user's
    // licenses (not scoped to the active one). Powers the cross-client
    // deep-link context switcher. `accessible: false` carries no client details.
    resolveSite: (id: number) =>
      apiClient.get<{
        success: boolean
        found: boolean
        accessible?: boolean
        license_id?: number
        site_id?: number
        client_name?: string
        domain?: string
      }>(`/content/${id}/site`),

    update: (id: number, data: Partial<ContentItem>) =>
      apiClient.put<{ content: ContentItem }>(`/content/${id}`, data),

    delete: (id: number) =>
      apiClient.delete(`/content/${id}`),

    createDerivative: (sourceArticleId: number, newTopic: string) =>
      apiClient.post<{ content: ContentItem }>(`/content/${sourceArticleId}/derivative`, {
        new_topic: newTopic,
      }),

    markPublished: (id: number, data: { wordpress_post_id: number }) =>
      apiClient.post(`/content/${id}/published`, data),

    // Approve an article (FOR-127). Backend enforces verification gate — returns
    // 422 with { message, blockers: string[] } when can_approve === false. The
    // verification-checklist UI catches that and toasts the blockers list.
    approve: (id: number) =>
      apiClient.post<{ content: ContentItem }>(`/content/${id}/approve`),

    schedule: (id: number, data: { scheduled_for: string }) =>
      apiClient.post(`/content/${id}/schedule`, data),

    getPublishingOptions: (id: number) =>
      apiClient.get<{
        success: boolean
        connections: Array<{
          type: 'production' | 'staging'
          url: string
          status: string
          // Same licence-block fields as WordPressConnection — resolve through
          // resolveConnectionStatus() rather than reading `status` directly.
          display_status?: string
          license_inactive?: boolean
          blocked_reason?: string | null
          connection_method?: 'app_password' | 'plugin'
          categories: Array<{ id: number; name: string; slug: string; count: number }>
          authors: Array<{ id: number; name: string; slug: string }>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          post_types: Array<Record<string, any>>
          timezone?: string
          last_synced: string | null
        }>
        content: {
          id: number
          title: string
          status: string
          wp_post_id: number | null
          wp_connection_type: string | null
        }
      }>(`/content/${id}/publishing-options`),

    publishToWordPress: (id: number, data: {
      connection_type: 'production' | 'staging'
      post_status?: 'draft' | 'publish' | 'pending' | 'future'
      category_id?: number | null
      author_id?: number | null
      immediate?: boolean
      scheduled_date?: string // ISO 8601 date for future posts
      post_type?: string // WordPress CPT rest_base (e.g., 'posts', 'pages', 'news')
      cta_ids?: number[] // CTA IDs to inject into content
    }) =>
      apiClient.post<{
        success: boolean
        message: string
        content: ContentItem
        wordpress?: {
          post_id: number
          url: string
        }
      }>(`/content/${id}/publish-to-wordpress`, data),

    uploadFeaturedImage: (id: number, file: File) => {
      const formData = new FormData()
      formData.append("image", file)
      return apiClient.post<{
        success: boolean
        url: string
        content: ContentItem
      }>(`/content/${id}/featured-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    },

    generateFeaturedImage: (
      id: number,
      data: {
        // Provider — defaults to 'replicate' on the backend for backwards
        // compatibility with old callers; the dialog should always send
        // `provider` explicitly.
        provider?: "openai" | "replicate"
        prompt: string
        // 'hero' (default — featured-image button) or 'inline' (slash command
        // / bubble-menu regenerate). Affects content_images row + R2 path
        // prefix. Server defaults to 'hero' when omitted.
        kind?: "hero" | "inline"
        // FOR-119 — bubble-menu Regenerate flow passes the old image's id.
        // On job completion, the new row's replaced_by_id points at it and
        // the old row is soft-deleted.
        replace_image_id?: number
        // OpenAI-specific
        size?: "1024x1024" | "1024x1536" | "1536x1024"
        quality?: "low" | "medium" | "high"
        // Replicate-specific
        model?: "flux-1.1-pro" | "flux-dev" | "flux-schnell" | "pro" | "dev" | "schnell"
        aspect_ratio?: "16:9" | "1:1" | "4:3" | "3:2" | "9:16"
        width?: number
        height?: number
      }
    ) =>
      // FOR-119 — the OpenAI path is now async. Backend creates a placeholder
      // ContentImage (status=pending), dispatches GenerateSingleImageJob, and
      // returns 202 with `image_id`. Caller polls listImages until the row's
      // status flips to 'completed' (then s3_url is filled in) or 'failed'.
      // The Replicate path is still synchronous and returns image_url + cost
      // directly. Both shapes are covered by the union below.
      apiClient.post<{
        success: boolean
        provider?: "openai" | "replicate"
        // OpenAI async response (status 202)
        image_id?: number
        status?: "pending" | "processing" | "completed" | "failed"
        kind?: "hero" | "inline"
        message?: string
        // Replicate sync response (status 200)
        image_url?: string
        model?: string
        size?: string
        quality?: string
        cost?: number
        duration_ms?: number
        prompt?: string
        // Error response
        error?: string
        code?: string
      }>(`/content/${id}/generate-featured-image`, data),

    // List ContentImages attached to an article (for the Images sidebar
    // panel — FOR-115/FOR-116). Returns current set + status; pass
    // include_replaced=true to include audit history.
    listImages: (id: number, includeReplaced = false) =>
      apiClient.get<{
        success: boolean
        image_generation_status: string | null
        image_generation_started_at: string | null
        image_generation_completed_at: string | null
        image_generation_error: string | null
        total_cost_cents: number
        images: Array<{
          id: number
          kind: "hero" | "inline" | "chart"
          prompt: string
          brief: Record<string, unknown> | null
          alt: string | null
          s3_url: string
          storage_path: string | null
          model: string
          size: string
          quality: string
          // FOR-119 — async lifecycle: pending (just dispatched) → processing
          // (worker picked up) → completed (s3_url is real) or failed
          // (error_message has detail). Existing rows backfill to 'completed'.
          status: "pending" | "processing" | "completed" | "failed"
          error_message: string | null
          is_pending: boolean
          // FOR-119 cost precision — `cost` (USD with up to 6 decimals) is the
          // source of truth post-migration. cost_cents and cost_dollars stay
          // for backward compat (legacy rows + components that round to cents).
          cost: number
          cost_cents: number
          cost_dollars: number
          input_tokens: number | null
          output_tokens: number | null
          splice_after_paragraph: number | null
          created_at: string
          updated_at: string
        }>
      }>(`/content/${id}/images${includeReplaced ? "?include_replaced=true" : ""}`),

    // Trigger full image-generation pipeline for an article. Replaces any
    // current images with newly generated ones (old rows soft-deleted).
    // Returns immediately; pipeline runs async, poll via listImages.
    regenerateImages: (
      id: number,
      data: {
        generate_hero?: boolean
        inline_count?: 0 | 1 | 2
        size?: "1024x1024" | "1024x1536" | "1536x1024"
        quality?: "low" | "medium" | "high"
      }
    ) =>
      apiClient.post<{
        success: boolean
        message: string
        content_item_id: number
        image_generation_status: string
        error?: string
      }>(`/content/${id}/regenerate-images`, data),

    // Soft-delete a single ContentImage (R2 object pruned by daily job).
    deleteImage: (id: number, imageId: number) =>
      apiClient.delete<{
        success: boolean
        message: string
        image_id: number
      }>(`/content/${id}/images/${imageId}`),

    // Per-site availability + options + cost estimates for the Generate
    // Featured Image dialog. Called when the dialog opens so it can render
    // provider tabs with availability badges and cost-preview chips.
    getImageProviders: (id: number) =>
      apiClient.get<{
        providers: {
          openai: {
            available: boolean
            reason: string | null
            label: string
            description: string
            default_model: string
            sizes: ("1024x1024" | "1024x1536" | "1536x1024")[]
            qualities: ("low" | "medium" | "high")[]
            default_size: "1024x1024" | "1024x1536" | "1536x1024"
            default_quality: "low" | "medium" | "high"
            estimated_costs: {
              low: number
              medium: number
              high: number
            }
          }
          replicate: {
            available: boolean
            reason: string | null
            label: string
            description: string
            default_model: string
            aspect_ratios: ("16:9" | "1:1" | "4:3" | "3:2" | "9:16")[]
            estimated_cost: number
          }
        }
      }>(`/content/${id}/image-providers`),

    // AI-detection scoring (Pangram). Measurement only — deliberately not
    // wired to any regeneration flow.
    detectAi: (id: number, force = false) =>
      apiClient.post<{
        success: boolean
        reused?: boolean
        check: AiDetectionCheck
      }>(`/content/${id}/ai-detection`, { force }),

    getAiDetections: (id: number) =>
      apiClient.get<{
        success: boolean
        checks: AiDetectionCheck[]
        current_body_hash: string
        current_quote_ratio: number
        configured: boolean
      }>(`/content/${id}/ai-detections`),

    // Fact-Checking (AI-powered claim verification)
    factCheck: (id: number) =>
      apiClient.post<{
        success: boolean
        fact_check: {
          id: number
          status: "pending" | "running" | "completed" | "failed"
          claims: Array<{
            id: number
            original_text: string
            claim: string
            category: string
            checkworthy: boolean
            verdict?: "supported" | "contradicted" | "mixed" | "unsubstantiated"
            confidence?: number
            severity?: "critical" | "moderate" | "minor"
            reasoning?: string
            sources?: Array<{ title: string; url: string; snippet: string }>
            suggested_correction?: string | null
          }> | null
          summary: {
            overall_score: number
            total_claims: number
            checkworthy: number
            supported: number
            contradicted: number
            mixed: number
            unsubstantiated: number
            skipped: number
            recommendation: string
          } | null
          cost: string
          error_message: string | null
          checked_at: string | null
          created_at: string
        }
      }>(`/content/${id}/fact-check`),

    getFactChecks: (id: number) =>
      apiClient.get<{
        success: boolean
        fact_checks: Array<{
          id: number
          status: "pending" | "running" | "completed" | "failed"
          claims: Array<{
            id: number
            original_text: string
            claim: string
            category: string
            checkworthy: boolean
            verdict?: "supported" | "contradicted" | "mixed" | "unsubstantiated"
            confidence?: number
            severity?: "critical" | "moderate" | "minor"
            reasoning?: string
            sources?: Array<{ title: string; url: string; snippet: string }>
            suggested_correction?: string | null
          }> | null
          summary: {
            overall_score: number
            total_claims: number
            checkworthy: number
            supported: number
            contradicted: number
            mixed: number
            unsubstantiated: number
            skipped: number
            recommendation: string
          } | null
          cost: string
          error_message: string | null
          checked_at: string | null
          created_at: string
        }>
      }>(`/content/${id}/fact-checks`),

    // Shareable preview links
    enableSharing: (id: number) =>
      apiClient.post<{ success: boolean; data: { share_token: string; share_url: string }; message: string }>(`/content/${id}/share`),

    disableSharing: (id: number) =>
      apiClient.delete<{ success: boolean; message: string }>(`/content/${id}/share`),

    viewSharedPreview: (token: string) =>
      apiClient.get<{
        success: boolean
        data: {
          title: string
          content_body: string
          excerpt?: string
          meta_description?: string
          featured_image?: string
          content_type?: string
          word_count?: number
          updated_at: string
          site_name?: string
          logo_url?: string | null
        }
      }>(`/preview/${token}`),

    // WordPress Sync Operations
    verifySyncStatus: (id: number) =>
      apiClient.get<{
        success: boolean
        sync_status: "not_synced" | "synced" | "modified" | "wp_modified" | "conflict" | "orphaned"
        exists: boolean
        local_modified: boolean
        wp_modified: boolean
        has_conflict: boolean
        local: {
          title: string
          content_preview: string
          modified_at: string | null
        }
        wordpress?: {
          title: string
          content_preview: string
          status: string
          modified_at: string
          url: string
        }
        content: ContentItem
      }>(`/content/${id}/verify-sync`),

    pullFromWordPress: (id: number) =>
      apiClient.post<{
        success: boolean
        message: string
        content: ContentItem
        changes: {
          title: boolean
          content: boolean
          excerpt: boolean
        }
      }>(`/content/${id}/pull-from-wordpress`),

    deleteFromWordPress: (id: number) =>
      apiClient.delete<{
        success: boolean
        message: string
        content: ContentItem
      }>(`/content/${id}/delete-from-wordpress`),

    republishToWordPress: (id: number, data: {
      connection_type: "production" | "staging"
      post_status?: "draft" | "publish" | "pending"
      category_id?: number | null
    }) =>
      apiClient.post<{
        success: boolean
        message: string
        content: ContentItem
        wordpress: {
          post_id: number
          url: string
        }
      }>(`/content/${id}/republish-to-wordpress`, data),

    // Version history (Laravel Rewind)
    getVersions: (id: number) =>
      apiClient.get<{
        success: boolean
        current_version: number
        versions: Array<{
          id: number
          version: number
          is_snapshot: boolean
          created_at: string
          user_id: number | null
        }>
      }>(`/content/${id}/versions`),

    getVersion: (id: number, version: number) =>
      apiClient.get<{
        success: boolean
        version: number
        attributes: Record<string, any>
      }>(`/content/${id}/versions/${version}`),

    compareVersions: (id: number, fromVersion: number) =>
      apiClient.get<{
        success: boolean
        from_version: number
        to_version: number
        from: { title: string; content_body: string; meta_description: string }
        to: { title: string; content_body: string; meta_description: string }
      }>(`/content/${id}/versions-compare`, { params: { from: fromVersion } }),

    restoreVersion: (id: number, version: number) =>
      apiClient.post<{
        success: boolean
        message: string
        content: ContentItem
      }>(`/content/${id}/versions/${version}/restore`),

    getAnalytics: (id: number, params?: { period_type?: string; start_date?: string; end_date?: string }) =>
      apiClient.get<{
        success: boolean
        analytics: AnalyticsSnapshot[]
        period: { type: string; start: string; end: string }
      }>(`/content/${id}/analytics`, { params }),

    bulkCreate: (articles: Array<{ title: string; content_type?: string; status?: string }>) =>
      apiClient.post<{ success: boolean; created: number; errors: string[]; error_count: number }>("/content/bulk-create", { articles }),


    // Content Comments (FOR-80)
    getComments: (id: number) =>
      apiClient.get<Array<{
        id: number
        content_item_id: number
        user_id: number
        body: string
        created_at: string
        updated_at: string
        user: { id: number; name: string; email: string; avatar: string | null }
      }>>(`/content/${id}/comments`),

    addComment: (id: number, body: string) =>
      apiClient.post<{
        id: number
        content_item_id: number
        user_id: number
        body: string
        created_at: string
        user: { id: number; name: string; email: string; avatar: string | null }
      }>(`/content/${id}/comments`, { body }),

    deleteComment: (contentId: number, commentId: number) =>
      apiClient.delete(`/content/${contentId}/comments/${commentId}`),


  },

  // Prompts
  prompts: {
    list: () =>
      apiClient.get<{ data: any[] }>("/prompts"),

    get: (id: number) =>
      apiClient.get<{ prompt: any }>(`/prompts/${id}`),

    create: (data: any) =>
      apiClient.post<{ prompt: any }>("/prompts", data),

    update: (id: number, data: any) =>
      apiClient.put<{ prompt: any }>(`/prompts/${id}`, data),

    delete: (id: number) =>
      apiClient.delete(`/prompts/${id}`),
  },

  // Analytics (comprehensive)
  analytics: {
    overview: (params?: { start_date?: string; end_date?: string }) =>
      apiClient.get<{
        success: boolean
        overview: {
          total_content: { current: number; previous: number; change: number }
          total_topics: { current: number; previous: number; change: number }
          topic_generations: { current: number }
          success_rate: { current: number; previous: number; change: number }
          error_rate: { current: number; errors: number }
          avg_execution_time: { current: number; previous: number; change: number }
          total_cost: { current: number; previous: number; change: number }
          total_api_calls: { current: number; previous: number; change: number }
          total_tokens: { current: number; input: number; output: number; previous: number; change: number }
        }
      }>("/analytics/overview", { params }),

    contentLifecycle: (params?: { start_date?: string; end_date?: string }) =>
      apiClient.get<{
        success: boolean
        content_lifecycle: {
          funnel: { total: number; drafted: number; in_review: number; approved: number; scheduled: number; published: number; failed: number }
          velocity: Array<{ date: string; count: number }>
          word_count_by_status: Array<{ status: string; avg_words: number; min_words: number; max_words: number; count: number }>
          by_source: Array<{ source: string; count: number }>
          avg_time_to_publish_hours: number
          quality_metrics: {
            avg_word_count: number
            avg_internal_links: number
            avg_external_links: number
            avg_headings: number
            total_generation_cost: number
            avg_generation_cost: number
          }
          pending_review_count: number
        }
      }>("/analytics/content-lifecycle", { params }),

    workflowPerformance: (params?: { start_date?: string; end_date?: string }) =>
      apiClient.get<{
        success: boolean
        workflow_performance: {
          stats: Array<{
            id: number
            name: string
            content_type: string
            total_runs: number
            completed_runs: number
            failed_runs: number
            avg_duration: number
            min_duration: number
            max_duration: number
            total_cost: number
            total_tokens: number
            success_rate: number
            avg_cost: number
          }>
          top_performers: Array<any>
          most_used: Array<{ id: number; name: string; times_used: number; last_used_at: string; content_type: string }>
          runs_over_time: Array<{ date: string; runs: number; completed: number; failed: number }>
          recent_failures: Array<{ id: number; workflow_name: string; error_message: string; created_at: string }>
        }
      }>("/analytics/workflow-performance", { params }),

    costIntelligence: (params?: { start_date?: string; end_date?: string }) =>
      apiClient.get<{
        success: boolean
        cost_intelligence: {
          cost_per_content: Array<{ content_type: string; count: number; avg_cost: number; min_cost: number; max_cost: number; total_cost: number }>
          daily_costs: Array<{ date: string; cost: number; calls: number }>
          projected_monthly_cost: number
          avg_daily_cost: number
          model_comparison: Array<{ provider: string; model: string; calls: number; total_cost: number; avg_cost: number; avg_duration: number }>
          expensive_operations: Array<{ source_type: string; total_cost: number; calls: number; avg_cost: number }>
          this_week_cost: number
          last_week_cost: number
          week_over_week_change: number
        }
      }>("/analytics/cost-intelligence", { params }),

    wordPressSyncHealth: () =>
      apiClient.get<{
        success: boolean
        wordpress_sync: {
          synced_count: number
          not_synced_count: number
          orphaned_count: number
          with_errors_count: number
          needs_sync_count: number
          wp_posts_by_status: Array<{ status: string; count: number }>
          wp_posts_by_type: Array<{ post_type: string; count: number }>
          recent_syncs: Array<any>
          with_featured_images: number
          total_published: number
          featured_image_rate: number
        }
      }>("/analytics/wordpress-sync"),


    topicsStats: (params?: { start_date?: string; end_date?: string }) =>
      apiClient.get<{
        success: boolean
        topics_stats: {
          by_category: Array<{ category: string; count: number }>
          by_content_type: Array<{ content_type: string; count: number }>
          by_status: Array<{ status: string; count: number }>
          by_source: Array<{ source: string; count: number }>
          recent_generations: Array<any>
        }
      }>("/analytics/topics-stats", { params }),
  },

  // Publishing Schedule - Automation Pipeline
  publishingSchedule: {
    list: (params?: {
      generation_id?: number
      status?: string
      from?: string
      to?: string
    }) =>
      apiClient.get<{
        timeline: Array<{
          date: string
          date_formatted: string
          is_today: boolean
          is_past: boolean
          items: Array<{
            id: number
            title: string
            status: string
            scheduled_at: string
            scheduled_time: string
            scheduled_date: string
            content_type: string
            word_count: number | null
            topic: {
              id: number
              title: string
              category: string
            } | null
            wp_connection_type: string | null
            wp_category_id: number | null
            automation_metadata: Record<string, unknown> | null
            topic_generation_id: number | null
          }>
        }>
        stats: {
          total_scheduled: number
          total_publishing: number
          total_published: number
          today_count: number
          this_week_count: number
        }
      }>("/publishing-schedule", { params }),

    reschedule: (id: number, scheduled_at: string) =>
      apiClient.patch<{
        message: string
        content_item: Record<string, unknown>
      }>(`/publishing-schedule/${id}`, { scheduled_at }),

    cancel: (id: number) =>
      apiClient.delete<{ message: string }>(`/publishing-schedule/${id}`),

    bulkSchedule: (data: {
      content_item_ids: number[]
      posts_per_day: number
      start_date?: string
      start_time?: string
      connection_type?: 'production' | 'staging'
      category_id?: number | null
    }) =>
      apiClient.post<{
        message: string
        scheduled_items: Array<Record<string, unknown>>
      }>("/publishing-schedule/bulk", data),

    preview: (data: {
      item_count: number
      posts_per_day: number
      start_date?: string
      start_time?: string
    }) =>
      apiClient.post<{
        item_count: number
        posts_per_day: number
        schedule: Array<{
          position: number
          date: string
          date_formatted: string
          time: string
        }>
        summary: {
          total_days: number
          first_post: string
          last_post: string
        }
      }>("/publishing-schedule/preview", data),
  },

  // Unified Queue System
  queue: {
    // Get queue statistics with category breakdown
    stats: () =>
      apiClient.get<{
        overall: {
          queued: number
          processing: number
          completed_today: number
          failed: number
          cost_today: number
        }
        by_category: Record<string, {
          queued: number
          processing: number
          completed_today: number
          failed: number
          cost_today: number
        }>
        pause_states: Record<string, {
          is_paused: boolean
          paused_at: string | null
          paused_by: string | null
        }>
        processing_job: {
          uuid: string
          display_name: string
          progress_percent: number
          status_message: string
        } | null
        worker_running: boolean
        stale_after_seconds: number
      }>("/queue/stats"),

    // Get jobs list with optional filtering
    jobs: (params?: { category?: string; status?: string; priority?: string; page?: number; per_page?: number }) =>
      apiClient.get<{
        data: Array<{
          id: number
          uuid: string
          job_type: string
          job_category: string
          display_name: string
          display_metadata: Record<string, any> | null
          status: string
          priority: string
          is_paused: boolean
          progress_percent: number
          status_message: string | null
          total_cost: number
          total_input_tokens: number
          total_output_tokens: number
          error_message: string | null
          attempts: number
          max_attempts: number
          queued_at: string | null
          started_at: string | null
          completed_at: string | null
          created_at: string
          can_view_output: boolean
          can_retry: boolean
          can_delete: boolean
          can_cancel: boolean
          output_type: string | null
          output_id: number | null
        }>
        pagination: {
          current_page: number
          per_page: number
          total: number
          last_page: number
          from: number
          to: number
        }
        queue_info: {
          worker_running: boolean
        }
      }>("/queue/jobs", { params }),

    // Get job details by UUID
    getJob: (uuid: string) =>
      apiClient.get<{
        job: {
          id: number
          uuid: string
          job_type: string
          job_category: string
          display_name: string
          display_metadata: Record<string, any> | null
          status: string
          priority: string
          is_paused: boolean
          progress_percent: number
          status_message: string | null
          total_cost: number
          total_input_tokens: number
          total_output_tokens: number
          error_message: string | null
          stack_trace: string | null
          attempts: number
          max_attempts: number
          queued_at: string | null
          started_at: string | null
          completed_at: string | null
          created_at: string
          payload: Record<string, any> | null
          result: Record<string, any> | null
          child_jobs: any[]
          usage_logs: any[]
          can_view_output: boolean
          can_retry: boolean
          can_delete: boolean
          can_cancel: boolean
          output_type: string | null
          output_id: number | null
        }
      }>(`/queue/jobs/${uuid}`),

    // Retry a failed job
    retry: (uuid: string) =>
      apiClient.post<{ success: boolean; message: string }>(`/queue/jobs/${uuid}/retry`),

    // Cancel a running or queued job
    cancel: (uuid: string) =>
      apiClient.post<{ success: boolean; message: string }>(`/queue/jobs/${uuid}/cancel`),

    deleteJob: (uuid: string) =>
      apiClient.delete<{ success: boolean; message: string }>(`/queue/jobs/${uuid}`),

    clearFailed: (category?: string) =>
      apiClient.delete<{ success: boolean; message: string; deleted_count: number }>("/queue/jobs/failed", { params: { category } }),

    resetStale: () =>
      apiClient.post<{ success: boolean; message: string; reset_count: number }>("/queue/reset-stale"),

    // Pause queue for a scope
    pause: (scope: string) =>
      apiClient.post<{
        success: boolean
        message: string
        pause_states: Record<string, { is_paused: boolean; paused_at: string | null; paused_by: string | null }>
      }>("/queue/pause", { scope }),

    // Resume queue for a scope
    resume: (scope: string) =>
      apiClient.post<{
        success: boolean
        message: string
        pause_states: Record<string, { is_paused: boolean; paused_at: string | null; paused_by: string | null }>
      }>("/queue/resume", { scope }),

    // Retry all failed jobs in a category
    retryCategory: (category: string) =>
      apiClient.post<{ success: boolean; message: string; retried_count: number }>(`/queue/retry-category/${category}`),

    // Retry all failed jobs
    retryAll: () =>
      apiClient.post<{ success: boolean; message: string }>("/queue/retry-all"),

    // Get live logs for a job by UUID
    getJobLogs: (uuid: string) =>
      apiClient.get<{
        logs: Array<{
          timestamp: string
          level: string
          message: string
          context?: Record<string, any>
        }>
        job_uuid: string
        workflow_run_id: number | null
        status: string
        started_at: string | null
        completed_at: string | null
        duration_seconds: number | null
        progress_percent: number
        display_name: string | null
      }>(`/queue/jobs/${uuid}/logs`),

    // Legacy endpoints for backward compatibility
    retryRun: (runId: number) =>
      apiClient.post<{ success: boolean; message: string }>(`/queue/runs/${runId}/retry`),

    cancelRun: (runId: number) =>
      apiClient.post<{ success: boolean; message: string }>(`/queue/runs/${runId}/cancel`),

    // Get logs for a workflow run
    getJobDetails: (runId: number) =>
      apiClient.get<{
        logs: Array<{
          timestamp: string
          level: string
          message: string
          context?: Record<string, any>
        }>
        run_id: number
        workflow_id: number
        status: string
      }>(`/queue/runs/${runId}/logs`),
  },

  // Client Profile
  profile: {
    get: () =>
      apiClient.get<{ profile: any }>("/profile"),

    save: (data: any) =>
      apiClient.post<{ profile: any }>("/profile", data),

    getContext: () =>
      apiClient.get<{ context: string }>("/profile/context"),

    autofill: (data: { url: string }) =>
      apiClient.post<{ success: boolean; profile: any }>("/profile/autofill", data),

    deletionPreview: () =>
      apiClient.get<{
        success: boolean
        client_name: string
        domain: string
        counts: Record<string, number>
        total_records: number
      }>("/profile/deletion-preview"),

    destroyAllData: (confirmationName: string, confirmationPhrase: string) =>
      apiClient.delete<{ success: boolean; message?: string; error?: string }>("/profile/destroy-all", {
        data: {
          confirmation_name: confirmationName,
          confirmation_phrase: confirmationPhrase,
        },
      }),

    uploadLogo: (file: File) => {
      const formData = new FormData()
      formData.append("logo", file)
      return apiClient.post<{ success: boolean; logo_url: string; profile: any }>("/profile/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    },

    deleteLogo: () =>
      apiClient.delete<{ success: boolean; message: string; profile?: any }>("/profile/logo"),
  },

  // Site (alias for profile for compatibility)
  site: {
    get: () =>
      apiClient.get<{ site: { business_name?: string; industry?: string; url?: string } }>("/profile").then((response) => ({
        site: {
          business_name: response.profile?.business_name,
          industry: response.profile?.industry,
          url: response.profile?.website,
        },
      })),
  },

  // Settings & API Keys
  settings: {
    // Version history (Laravel Rewind). Only the allowlisted settings columns
    // are tracked — `tracked_settings` names them. API keys are never recorded.
    getVersions: () =>
      apiClient.get<EntityVersionList & { tracked_settings: string[] }>("/settings/versions"),

    getVersion: (version: number) =>
      apiClient.get<EntityVersionAttributes>(`/settings/versions/${version}`),

    restoreVersion: (version: number) =>
      apiClient.post<{ success: boolean; message: string; tracked_settings: string[] }>(
        `/settings/versions/${version}/restore`
      ),

    get: () =>
      apiClient.get<{ settings: any }>("/settings"),

    update: (data: any) =>
      apiClient.patch<{ settings: any }>("/settings", data),

    updateApiKeys: (data: Record<string, string>) =>
      apiClient.post<{ message: string }>("/settings/api-keys", data),

    validateApiKey: (data: { provider: string; api_key: string }) =>
      apiClient.post<{ valid: boolean; message?: string }>("/settings/api-keys/validate", data),

    removeApiKey: (provider: string) =>
      apiClient.delete(`/settings/api-keys?provider=${provider}`),

    getDomainRating: () =>
      apiClient.get<{ domain_rating: number | null; change: number | null; logo_url: string | null; updated_at: string | null }>("/settings/domain-rating"),

    getModels: () =>
      apiClient.get<{ models: any }>("/settings/models"),

    // Embeddings/Similarity Detection
    getEmbeddingsStatus: () =>
      apiClient.get<{
        enabled: boolean;
        ready: boolean;
        services: {
          openai_embeddings: { configured: boolean; description: string; source: string | null };
          qdrant: { configured: boolean; description: string };
        };
        features: {
          topic_similarity: { available: boolean; description: string };
          news_deduplication: { available: boolean; description: string };
        };
        missing: string[];
      }>("/settings/embeddings"),

    toggleEmbeddings: (enabled: boolean) =>
      apiClient.post<{ success: boolean; message: string; enabled: boolean; ready: boolean }>("/settings/embeddings", { enabled }),

    getEmbeddingsStats: () =>
      apiClient.get<{
        enabled: boolean;
        ready: boolean;
        services: {
          openai_embeddings: { configured: boolean; source: string | null };
          qdrant: { configured: boolean };
        };
        missing: string[];
        stats: {
          topics: { total: number; embedded: number; pending: number; outdated: number };
          content: { total: number; embedded: number; pending: number; outdated: number };
          news: { total: number; embedded: number; pending: number; outdated: number };
          wordpress_posts: { total: number; embedded: number; pending: number; outdated: number };
        };
        embedding_version?: {
          current: string;
          description: string;
        };
        costs: {
          total: number;
          total_calls: number;
          last_7_days: number;
          last_7_days_calls: number;
        };
        recent_activity: {
          topics: Array<{ id: number; title: string; embedded_at: string | null }>;
          news: Array<{ id: number; title: string; embedded_at: string | null }>;
          wordpress_posts: Array<{ id: number; title: string; embedded_at: string | null }>;
        };
      }>("/settings/embeddings/stats"),

    embedWordPressPosts: () =>
      apiClient.post<{ success: boolean; message: string; pending: number }>("/settings/embeddings/wordpress-posts"),

    embedNewsArticles: () =>
      apiClient.post<{ success: boolean; message: string; pending: number }>("/settings/embeddings/news-articles"),

    embedTopics: () =>
      apiClient.post<{ success: boolean; message: string; pending: number }>("/settings/embeddings/topics"),

    embedContentItems: () =>
      apiClient.post<{ success: boolean; message: string; pending: number }>("/settings/embeddings/content-items"),

    reembedAll: (type: 'topics' | 'content' | 'news' | 'wordpress_posts' | 'all') =>
      apiClient.post<{
        success: boolean
        message: string
        cleared: { topics?: number; content?: number; news?: number; wordpress_posts?: number }
      }>("/settings/embeddings/reembed", { type }),

    // Notification Settings (email alerts)
    notifications: {
      get: () =>
        apiClient.get<{
          settings: {
            id: number
            site_id: number
            batch_complete: boolean
            publish_failed: boolean
            news_ready: boolean
            autopilot_summary: boolean
            weekly_digest: boolean
            email_recipients: string[] | null
          }
        }>("/settings/notifications"),

      update: (data: {
        batch_complete?: boolean
        publish_failed?: boolean
        news_ready?: boolean
        autopilot_summary?: boolean
        weekly_digest?: boolean
        email_recipients?: string[] | null
      }) =>
        apiClient.patch<{
          success: boolean
          message: string
          settings: {
            id: number
            site_id: number
            batch_complete: boolean
            publish_failed: boolean
            news_ready: boolean
            autopilot_summary: boolean
            weekly_digest: boolean
            email_recipients: string[] | null
          }
        }>("/settings/notifications", data),
    },
  },

  // Version & Changelog
  version: {
    get: () =>
      apiClient.get<{ version: string; changelog: string }>("/version"),
  },

  // Health / deploy info
  health: {
    get: () =>
      apiClient.get<{
        status: string
        timestamp: string
        version: string
        git: { hash: string | null; date: string | null; message: string | null }
      }>("/health"),
  },

  // WordPress Connections
  wordpress: {
    getConnections: () =>
      apiClient.get<{ production: WordPressConnection | null; staging: WordPressConnection | null }>("/wordpress"),

    getConnection: (type: "production" | "staging") =>
      apiClient.get<WordPressConnection>(`/wordpress/${type}`),

    testConnection: (data: { url: string; username: string; app_password: string }) =>
      apiClient.post<{
        success: boolean
        error?: string
        site?: { name: string; description: string; url: string }
        user?: { id: number; name: string; slug: string }
        wp_version?: string
      }>("/wordpress/test", data),

    saveConnection: (data: {
      type: "production" | "staging"
      url: string
      username: string
      app_password: string
      default_post_type?: string
      default_post_status?: string
      default_author_id?: number
      auto_featured_image?: boolean
    }) =>
      apiClient.post<{
        success: boolean
        message: string
        connection: WordPressConnection
        sync_errors?: Record<string, string>
      }>("/wordpress", data),

    updateConnection: (type: "production" | "staging", data: {
      default_post_type?: string
      default_post_status?: string
      default_author_id?: number
      auto_featured_image?: boolean
    }) =>
      apiClient.patch<{ success: boolean; message: string; connection: WordPressConnection }>(`/wordpress/${type}`, data),

    syncConnection: (type: "production" | "staging") =>
      apiClient.post<{
        success: boolean
        message: string
        connection: WordPressConnection
        errors?: Record<string, string>
      }>(`/wordpress/${type}/sync`),

    updateAutoSync: (type: "production" | "staging", data: {
      auto_sync_enabled: boolean
      auto_sync_frequency: string
    }) =>
      apiClient.put<{
        success: boolean
        message: string
        connection: WordPressConnection
        next_sync_at?: string
      }>(`/wordpress/${type}/auto-sync`, data),

    deleteConnection: (type: "production" | "staging") =>
      apiClient.delete<{ success: boolean; message: string }>(`/wordpress/${type}`),

    // Publishing Defaults per Post Type
    getPublishingDefaults: (type: "production" | "staging") =>
      apiClient.get<{
        publishing_defaults: Record<string, PublishingDefaults>
        post_types: WordPressPostType[]
        categories: WordPressCategory[]
        users: Array<{ id: number; name: string; slug: string }>
        post_statuses: string[]
      }>(`/wordpress/${type}/publishing-defaults`),

    updatePublishingDefaults: (type: "production" | "staging", data: {
      post_type: string
      status?: string
      author_id?: number | null
      category_ids?: number[]
      tag_ids?: number[]
    }) =>
      apiClient.put<{
        success: boolean
        message: string
        publishing_defaults: Record<string, PublishingDefaults>
      }>(`/wordpress/${type}/publishing-defaults`, data),

    bulkUpdatePublishingDefaults: (type: "production" | "staging", defaults: Record<string, PublishingDefaults>) =>
      apiClient.put<{
        success: boolean
        message: string
        publishing_defaults: Record<string, PublishingDefaults>
      }>(`/wordpress/${type}/publishing-defaults/bulk`, { defaults }),

    // Plugin-based connections (for sites with security restrictions)
    plugin: {
      generateKey: (data: { type: "production" | "staging"; url: string }) =>
        apiClient.post<{
          success: boolean
          message: string
          connection_key: string
          connection: WordPressConnection
          instructions: string[]
        }>("/wordpress/plugin/generate-key", data),

      testConnection: (data: { url: string; connection_key: string }) =>
        apiClient.post<{
          success: boolean
          error?: string
          hint?: string
          site?: { name: string; description: string; url: string; wp_version?: string }
          status?: { connected: boolean; connected_at?: string }
        }>("/wordpress/plugin/test", data),

      saveConnection: (data: {
        type: "production" | "staging"
        url: string
        connection_key: string
      }) =>
        apiClient.post<{
          success: boolean
          message: string
          connection: WordPressConnection
        }>("/wordpress/plugin", data),

      regenerateKey: (type: "production" | "staging") =>
        apiClient.post<{
          success: boolean
          message: string
          connection_key: string
          connection: WordPressConnection
        }>(`/wordpress/${type}/regenerate-key`),
    },

    // Upgrade from app_password to plugin
    upgradeToPlugin: (type: "production" | "staging") =>
      apiClient.post<{
        success: boolean
        message: string
        connection_key: string
        url: string
        instructions: string[]
      }>(`/wordpress/${type}/upgrade-to-plugin`),

    testPluginUpgrade: (type: "production" | "staging") =>
      apiClient.post<{
        success: boolean
        message?: string
        error?: string
        hint?: string
        connection?: WordPressConnection
        site?: { name: string; description: string }
      }>(`/wordpress/${type}/test-plugin-upgrade`),

    // CMS Management
    cms: {
      getStats: (type: "production" | "staging") =>
        apiClient.get<CmsStatsResponse>(`/wordpress/${type}/cms/stats`),

      // Posts
      getPosts: (type: "production" | "staging", filters?: CmsPostFilters) =>
        apiClient.get<CmsPostsResponse>(`/wordpress/${type}/cms/posts`, { params: filters }),

      getPost: (type: "production" | "staging", id: number) =>
        apiClient.get<{ data: CmsSyncedPost; wp_edit_url: string; wp_public_url: string }>(`/wordpress/${type}/cms/posts/${id}`),

      getPostByWpId: (type: "production" | "staging", wpPostId: number) =>
        apiClient.get<{ data: CmsSyncedPost; wp_edit_url: string; wp_public_url: string }>(`/wordpress/${type}/cms/posts/wp/${wpPostId}`),

      updatePost: (type: "production" | "staging", id: number, data: { content: string }) =>
        apiClient.put<{ success: boolean; message: string; data: CmsSyncedPost; error?: string }>(`/wordpress/${type}/cms/posts/${id}`, data),

      getSyncPreview: (type: "production" | "staging") =>
        apiClient.get<{
          success: boolean
          data: {
            full_sync: { posts: number; pages: number; total: number }
            incremental_sync: { posts: number; pages: number; total: number; available: boolean }
            last_synced_at: string | null
          }
        }>(`/wordpress/${type}/cms/posts/sync/preview`),

      getActiveSyncJob: (type: "production" | "staging") =>
        apiClient.get<{
          active: boolean
          job: {
            uuid: string
            status: string
            progress_percent: number
            status_message: string | null
            display_name: string
            created_at: string
          } | null
        }>(`/wordpress/${type}/cms/posts/sync/active`),

      syncPosts: (type: "production" | "staging", options?: { incremental?: boolean }) =>
        apiClient.post<{ success: boolean; message: string; job_uuid: string | null; sync_type: string }>(`/wordpress/${type}/cms/posts/sync`, options),

      syncPostsNow: (type: "production" | "staging") =>
        apiClient.post<{ success: boolean; message: string; synced: number; errors: any[] }>(`/wordpress/${type}/cms/posts/sync-now`),

      clearAndResync: (type: "production" | "staging") =>
        apiClient.post<{ success: boolean; message: string; synced: number; errors: any[] }>(`/wordpress/${type}/cms/posts/clear-and-resync`),

      // Media
      getMedia: (type: "production" | "staging", filters?: CmsMediaFilters) =>
        apiClient.get<CmsMediaResponse>(`/wordpress/${type}/cms/media`, { params: filters }),

      uploadMedia: (type: "production" | "staging", file: File) => {
        const formData = new FormData()
        formData.append("file", file)
        return apiClient.post<{ success: boolean; message: string; data: CmsMediaItem }>(`/wordpress/${type}/cms/media`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      },

      deleteMedia: (type: "production" | "staging", mediaId: number) =>
        apiClient.delete<{ success: boolean; message: string }>(`/wordpress/${type}/cms/media/${mediaId}`),

      // Categories
      getCategories: (type: "production" | "staging") =>
        apiClient.get<{ data: WordPressCategory[] }>(`/wordpress/${type}/cms/categories`),

      createCategory: (type: "production" | "staging", data: { name: string; slug?: string; description?: string; parent?: number }) =>
        apiClient.post<{ success: boolean; message: string; data: any; categories: WordPressCategory[] }>(`/wordpress/${type}/cms/categories`, data),

      updateCategory: (type: "production" | "staging", categoryId: number, data: { name?: string; slug?: string; description?: string; parent?: number }) =>
        apiClient.put<{ success: boolean; message: string; data: any; categories: WordPressCategory[] }>(`/wordpress/${type}/cms/categories/${categoryId}`, data),

      deleteCategory: (type: "production" | "staging", categoryId: number) =>
        apiClient.delete<{ success: boolean; message: string; categories: WordPressCategory[] }>(`/wordpress/${type}/cms/categories/${categoryId}`),

      // Tags
      getTags: (type: "production" | "staging") =>
        apiClient.get<{ data: WordPressTag[] }>(`/wordpress/${type}/cms/tags`),

      createTag: (type: "production" | "staging", data: { name: string; slug?: string; description?: string }) =>
        apiClient.post<{ success: boolean; message: string; data: any; tags: WordPressTag[] }>(`/wordpress/${type}/cms/tags`, data),

      updateTag: (type: "production" | "staging", tagId: number, data: { name?: string; slug?: string; description?: string }) =>
        apiClient.put<{ success: boolean; message: string; data: any; tags: WordPressTag[] }>(`/wordpress/${type}/cms/tags/${tagId}`, data),

      deleteTag: (type: "production" | "staging", tagId: number) =>
        apiClient.delete<{ success: boolean; message: string; tags: WordPressTag[] }>(`/wordpress/${type}/cms/tags/${tagId}`),
    },
  },

  // Link Suggestions (AI-powered)
  // Supports both published WordPress posts (post_id) and unpublished content items (content_item_id)
  links: {
    suggestInternal: (data: {
      post_id?: number;
      // WordPress post id (from link coverage). When given without `content`,
      // the server resolves the post body itself (stored content or connector
      // getPost) and returns 422 if it can't.
      wp_post_id?: number;
      content_item_id?: number;
      content?: string;
      count?: number;
      connection_type?: "production" | "staging";
    }) =>
      apiClient.post<SuggestInternalResponse>("/links/suggest-internal", data),

    suggestExternal: (data: {
      post_id?: number;
      content_item_id?: number;
      content: string;
      count?: number;
    }) =>
      apiClient.post<{
        suggestions: LinkSuggestion[];
        citations?: any[];
        stats?: LinkSuggestStats;
        error?: string;
      }>("/links/suggest-external", data),

    // Saved suggestions CRUD - supports both WordPress posts and content items.
    // Rows come back grouped by status: pending (`suggestions`), `accepted`,
    // `applied` (pushed to WordPress) and `dismissed`.
    getSuggestions: (postId: number) =>
      apiClient.get<LinkSuggestionsResponse>(`/links/suggestions/${postId}`),

    getSuggestionsForContent: (contentItemId: number) =>
      apiClient.get<LinkSuggestionsResponse>(`/links/suggestions/content/${contentItemId}`),

    // Resolve a WordPress post id → internal id server-side. The link-coverage
    // feature only has wp_post_id, so it uses this instead of getSuggestions.
    getSuggestionsByWpId: (wpPostId: number) =>
      apiClient.get<LinkSuggestionsResponse>(`/links/suggestions/wp/${wpPostId}`),

    // Persist freshly generated suggestions. Pass EVERY field the generate
    // call returned (sentence_context / reason / confidence / ranking_version)
    // — the cards render them — and `content` so the server can record a hash
    // of the article the suggestions were generated from.
    saveSuggestions: (data: {
      post_id?: number;
      // WordPress post id — resolved to the internal id server-side. Use this
      // (not post_id) when you only have the wp_post_id from link coverage.
      wp_post_id?: number;
      content_item_id?: number;
      content?: string;
      suggestions: Array<{
        anchor_text: string;
        target_url: string;
        target_title?: string | null;
        context?: string | null;
        sentence_context?: string | null;
        reason?: string | null;
        confidence?: LinkConfidence | null;
        ranking_version?: string | null;
        type: "internal" | "external";
      }>;
    }) =>
      apiClient.post<{
        saved: LinkSuggestion[];
        count: number;
        skipped: number;
        // Why the rest were not added — LinkSuggestionStore's reason keys
        // (destination_already_suggested, sentence_already_suggested,
        // occurrence_already_suggested, previously_dismissed).
        skipped_reasons?: Record<string, number>;
      }>("/links/suggestions", data),

    // DB-only status change — this never writes to WordPress. On the Internal
    // Links page an "Accept" that must reach the live site goes through
    // bulkApply({ suggestion_ids: [...], preview: false }) instead.
    updateSuggestion: (id: string, status: "pending" | "accepted" | "dismissed") =>
      apiClient.patch<{ success: boolean; suggestion: { id: string; status: LinkSuggestionStatus } }>(
        `/links/suggestions/${id}`,
        { status }
      ),

    // Remove one already-applied link from the live post, keeping the anchor
    // text, and return the row to `pending`. Only valid for status 'pushed';
    // anything else comes back 422 with an `error`.
    undoSuggestion: (id: string | number) =>
      apiClient.post<{ success: boolean; suggestion: { id: string; status: LinkSuggestionStatus } }>(
        `/links/suggestions/${id}/undo`
      ),

    deleteSuggestion: (id: string) =>
      apiClient.delete<{ success: boolean }>(`/links/suggestions/${id}`),

    markPushed: (suggestionIds: number[]) =>
      apiClient.post<{ success: boolean; count: number }>("/links/suggestions/mark-pushed", {
        suggestion_ids: suggestionIds,
      }),

    // --- Internal-link coverage (per-site retroactive tool) ---
    // Paginated table of every blog post with its internal/external/inbound
    // link counts + suggestion status. `search` filters by title (server-side).
    coverage: (params?: {
      page?: number
      per_page?: number
      sort?: LinkCoverageSort
      filter?: LinkCoverageFilter
      search?: string
    }) =>
      apiClient.get<LinkCoverageResponse>("/links/coverage", { params }),

    // Kick off a background rescan that recomputes link counts across the site.
    coverageScan: () =>
      apiClient.post<{ queued: boolean }>("/links/coverage/scan"),

    // Queue AI suggestion generation for many posts at once (go-back tool).
    //
    // The response is the ONLY place ids that never made it into the batch are
    // reported: `unknown_ids` (no such post on this site) and
    // `already_running_ids` (a job is mid-flight). Progress is then polled
    // from suggestRuns() — the queue call itself returns immediately, long
    // before any job runs.
    //
    // `force` is for background/automatic runs only; an explicit selection is
    // always generated for server-side, so the page never sends it.
    bulkSuggest: (data: { wp_post_ids: number[]; count?: number; force?: boolean }) =>
      apiClient.post<BulkSuggestResponse>("/links/bulk-suggest", data),

    // Run status for an explicit set of posts — the batch progress source for
    // the Internal Links page, which needs articles that aren't on the page of
    // the coverage table currently being looked at.
    //
    // Every requested id comes back as a key; null means never run (or not a
    // post of this site). The endpoint caps at 200 ids per call, so a large
    // batch is chunked here rather than silently truncated. The query string is
    // written out by hand because PHP wants `wp_post_ids[]=1&wp_post_ids[]=2`
    // and this axios client has no paramsSerializer configured.
    suggestRuns: async (wpPostIds: number[]): Promise<LinkSuggestRunsResponse> => {
      const chunks: number[][] = []
      for (let i = 0; i < wpPostIds.length; i += 200) {
        chunks.push(wpPostIds.slice(i, i + 200))
      }

      const responses = await Promise.all(
        chunks.map((chunk) =>
          apiClient.get<LinkSuggestRunsResponse>(
            `/links/suggest-runs?${chunk.map((id) => `wp_post_ids[]=${encodeURIComponent(id)}`).join("&")}`
          )
        )
      )

      return { runs: Object.assign({}, ...responses.map((r) => r.runs ?? {})) }
    },

    applyRuns: async (wpPostIds: number[]): Promise<LinkApplyRunsResponse> => {
      const chunks: number[][] = []
      for (let i = 0; i < wpPostIds.length; i += 200) {
        chunks.push(wpPostIds.slice(i, i + 200))
      }

      const responses = await Promise.all(
        chunks.map((chunk) =>
          apiClient.get<LinkApplyRunsResponse>(
            `/links/apply-runs?${chunk.map((id) => `wp_post_ids[]=${encodeURIComponent(id)}`).join("&")}`
          )
        )
      )

      return { runs: Object.assign({}, ...responses.map((r) => r.runs ?? {})) }
    },

    // Wipe the review queue so the next run starts clean: deletes pending,
    // rejected and stale internal suggestions and clears `suggest_run` on the
    // affected posts. Links already accepted/applied are KEPT — this never
    // touches the live site. Pass `all: true` for the whole site.
    resetSuggestions: (data: { wp_post_ids?: number[]; all?: boolean }) =>
      apiClient.post<{ deleted: number; posts: number }>("/links/suggestions/reset", data),

    // Preview stays synchronous. Bulk apply returns queued/run_ids for applyRuns();
    // one explicit suggestion_id keeps the synchronous single-card response.
    //
    // Target either whole posts (`wp_post_ids` — every pending/accepted
    // internal suggestion on them) or exact cards (`suggestion_ids` — how a
    // single-card Accept reaches WordPress). One of the two is required.
    //
    // Every suggestion handed in gets an outcome: `changes` (inserted, with
    // the surrounding sentence) or `skipped` with a reason, and a post-level
    // `error` when WordPress itself refused. links_added: 0 with a populated
    // `skipped` is a normal, non-success result — never render it as one.
    bulkApply: (data: {
      wp_post_ids?: number[]
      suggestion_ids?: number[]
      preview: boolean
    }) =>
      apiClient.post<{
        queued?: boolean
        post_count?: number
        queued_ids?: number[]
        already_running_ids?: number[]
        not_queued_ids?: number[]
        run_ids?: Record<number, string>
        previews?: BulkApplyPreview[]
        applied?: BulkApplyResult[]
        error?: string
      }>("/links/bulk-apply", data),
  },

  // Newsroom — the review lane for machine-drafted news.
  //
  // approve() is the only method here that writes to a live client site, and
  // it does so immediately: there is no staging step between this call and a
  // published article. Every caller must go through a confirmation that names
  // the destination domain.
  newsroom: {
    // One lane at a time — build params with buildQueueParams().
    queue: (params: Record<string, string | number>) =>
      apiClient.get<NewsroomQueueResponse>("/newsroom/queue", { params }),

    // Every lane in one grouped query. Lanes are always present as explicit
    // zeroes, so "none" is never confused with "key absent". Includes
    // `drafting`, which a per-lane query cannot see.
    counts: () =>
      apiClient.get<{ counts: Record<string, number> }>("/newsroom/queue/counts"),

    // The full review payload: draft, the source item it came from, gate
    // results and the decision trail.
    get: (id: number) =>
      apiClient.get<{ item: NewsroomItemDetail }>(`/newsroom/items/${id}`),

    // Everything the pipeline did: every attempt, its gate results, its cost,
    // and the prompt it was given. Deliberately its own request — a stored
    // prompt embeds the source articles, so a reviewer working a queue should
    // never load one by default.
    process: (id: number) =>
      apiClient.get<{ process: NewsroomProcess }>(`/newsroom/items/${id}/process`),

    // A login-free preview link for the person who has to approve the article.
    // Served from the same /preview/{token} route as a content-item preview, so
    // there is one kind of preview link rather than two.
    share: (id: number) =>
      apiClient.post<{ share_token: string; share_url: string }>(
        `/newsroom/items/${id}/share`,
      ),

    unshare: (id: number) => apiClient.delete(`/newsroom/items/${id}/share`),

    // PUBLISHES to the live client site.
    //
    // A 200 can still carry a `warning`: the publish succeeded, but the
    // connector's NewsArticle schema marking could not be confirmed. The
    // story is live either way, so that is a warning on a published item,
    // never a failure.
    approve: (id: number) =>
      apiClient.post<{ item: NewsroomItemDetail; warning?: string | null }>(
        `/newsroom/items/${id}/approve`,
      ),

    // Reason is required by the API, not merely encouraged.
    reject: (id: number, reason: string) =>
      apiClient.post<{ item: NewsroomItemDetail }>(`/newsroom/items/${id}/reject`, { reason }),

    // Unpublishes and redacts an already-approved item.
    takedown: (id: number, reason: string) =>
      apiClient.post<{ item: NewsroomItemDetail }>(`/newsroom/items/${id}/takedown`, { reason }),

    correction: (id: number, correctionText: string) =>
      apiClient.post<{ item: NewsroomItemDetail }>(`/newsroom/items/${id}/correction`, {
        correction_text: correctionText,
      }),

    // Site-scoped. `vertical` comes back read-only; buildSettingsPayload()
    // is what decides which fields may be written.
    //
    // BOTH VERBS WRAP THEIR BODY IN `settings` — unwrapped here so the rest of
    // the UI sees the flat NewsroomSettings it is typed against. Declaring the
    // flat type on the raw call was not merely cosmetic: the tab spreads the
    // result over its defaults, so every real field stayed unread, the vertical
    // read as unassigned, the webhook URL never rendered, and a save wrote the
    // empty defaults back over the client's beat and byline.
    settings: {
      get: () =>
        apiClient
          .get<{ settings: NewsroomSettings }>("/newsroom/settings")
          .then(readSettingsResponse),

      update: (data: NewsroomSettingsPayload) =>
        apiClient
          .put<{ settings: NewsroomSettings }>("/newsroom/settings", data)
          .then(readSettingsResponse),
    },
  },

  // Transcriptions (Video/Audio to SEO Articles)
  transcriptions: {
    list: (params?: TranscriptionFilters) =>
      apiClient.get<{ data: Transcription[]; total: number; current_page: number; per_page: number; last_page: number }>("/transcriptions", { params }),

    stats: () =>
      apiClient.get<TranscriptionStats>("/transcriptions/stats"),

    get: (id: number) =>
      apiClient.get<Transcription>(`/transcriptions/${id}`),

    createFromYouTube: (url: string) =>
      apiClient.post<Transcription>("/transcriptions", { youtube_url: url }),

    createFromUpload: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return apiClient.post<Transcription>("/transcriptions", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    },

    delete: (id: number) =>
      apiClient.delete<{ success: boolean }>(`/transcriptions/${id}`),

    extractTopics: (id: number, count?: number) =>
      apiClient.post<{ message: string; transcription_id: number }>(`/transcriptions/${id}/extract-topics`, { count }),

    getTopics: (id: number) =>
      apiClient.get<TranscriptionTopic[]>(`/transcriptions/${id}/topics`),

    createTopic: (transcriptionId: number, topicId: number, workflowId?: number) =>
      apiClient.post<{ topic: Topic; extracted_topic: TranscriptionTopic }>(
        `/transcriptions/${transcriptionId}/topics/${topicId}/create`,
        { workflow_id: workflowId }
      ),

    skipTopic: (transcriptionId: number, topicId: number) =>
      apiClient.post<TranscriptionTopic>(`/transcriptions/${transcriptionId}/topics/${topicId}/skip`),

    generate: (id: number, data: GenerateFromTranscriptionRequest) =>
      apiClient.post<{ message: string; topic_id: number; workflow_id: number }>(`/transcriptions/${id}/generate`, data),

    retry: (id: number) =>
      apiClient.post<{ success: boolean; message: string; transcription: Transcription }>(`/transcriptions/${id}/retry`),

    // Settings
    settings: {
      get: () =>
        apiClient.get<TranscriptionSettings>("/transcription-settings"),

      update: (data: UpdateTranscriptionSettingsRequest) =>
        apiClient.put<TranscriptionSettings>("/transcription-settings", data),
    },
  },

  // CTAs (Call-to-Action Management)
  ctas: {
    // Version history (Laravel Rewind). Restoring deliberately leaves
    // `is_active` alone — see preserved_on_restore in the response.
    getVersions: (id: number) =>
      apiClient.get<EntityVersionList & { preserved_on_restore: string[] }>(`/ctas/${id}/versions`),

    getVersion: (id: number, version: number) =>
      apiClient.get<EntityVersionAttributes>(`/ctas/${id}/versions/${version}`),

    restoreVersion: (id: number, version: number) =>
      apiClient.post<{ success: boolean; message: string; preserved_on_restore: string[]; cta: Cta }>(
        `/ctas/${id}/versions/${version}/restore`
      ),

    list: (params?: { type?: string; category?: string; is_active?: boolean }) =>
      apiClient.get<Cta[]>("/ctas", { params }),

    get: (id: number) =>
      apiClient.get<Cta>(`/ctas/${id}`),

    getBySlug: (slug: string) =>
      apiClient.get<Cta>(`/ctas/slug/${slug}`),

    create: (data: CtaInput) =>
      apiClient.post<Cta>("/ctas", data),

    update: (id: number, data: Partial<CtaInput>) =>
      apiClient.put<Cta>(`/ctas/${id}`, data),

    delete: (id: number) =>
      apiClient.delete<{ message: string }>(`/ctas/${id}`),

    duplicate: (id: number) =>
      apiClient.post<Cta>(`/ctas/${id}/duplicate`),

    toggleActive: (id: number) =>
      apiClient.post<Cta>(`/ctas/${id}/toggle-active`),

    forWordPress: () =>
      apiClient.get<{ ctas: Cta[] }>("/ctas/wordpress"),

    // Site-wide popup delivery policy. This is intentionally separate from a
    // CTA's site_settings because it coordinates all eligible popups.
    getPopupSettings: () =>
      apiClient.get<{ popup_settings: SitePopupSettings }>("/ctas/popup-settings"),

    updatePopupSettings: (popup_settings: SitePopupSettings) =>
      apiClient.put<{ popup_settings: SitePopupSettings }>(
        "/ctas/popup-settings",
        popupSettingsUpdatePayload(popup_settings),
      ),

    // Upload an image for use in a CTA (main image, background, etc.). Multipart
    // field "image", max 5MB (jpeg/png/gif/webp). Returns the hosted URL to drop
    // into content.image_url. Laravel returns 422 on validation errors.
    uploadImage: (file: File) => {
      const formData = new FormData()
      formData.append("image", file)
      return apiClient.post<{ url: string }>("/ctas/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    },

    // Stats endpoints
    getAllStats: (days?: number, include?: string) =>
      apiClient.get<CtaStatsResponse>("/ctas/stats", { params: { days, include } }),

    getStats: (id: number, params?: { start_date?: string; end_date?: string; days?: number }) =>
      apiClient.get<CtaDetailedStats>(`/ctas/${id}/stats`, { params }),

    // Leads (email capture)
    getLeads: (id: number, params?: { per_page?: number; page?: number }) =>
      apiClient.get<Paginated<CtaLead>>(`/ctas/${id}/leads`, { params }),

    // include_junk shows test/internal leads, which are hidden by default.
    // The response always carries junk_total so the caller can say how many
    // are being held back.
    getAllLeads: (params?: {
      cta_id?: number
      source?: string
      per_page?: number
      page?: number
      include_junk?: boolean
      // archived=true shows ONLY archived leads; default view excludes them
      archived?: boolean
      date_from?: string
      date_to?: string
      classification_category?: string
      classification_subtype?: string
      classification_state?: string
      classification_min_confidence?: number
    }) => apiClient.get<import("./types").PaginatedLeads>("/ctas/leads", { params }),
    archiveLead: (id: number) =>
      apiClient.post<{ success: boolean; archived_at: string | null }>(`/ctas/leads/${id}/archive`),
    unarchiveLead: (id: number) =>
      apiClient.post<{ success: boolean; archived_at: string | null }>(`/ctas/leads/${id}/unarchive`),
    // Permanent — cta_leads has no soft deletes
    deleteLead: (id: number) =>
      apiClient.delete<{ success: boolean }>(`/ctas/leads/${id}`),
  },

  // Lead analytics. The API returns pre-aggregated buckets for one date window
  // — never lead rows — so the Stats tab does no reducing in the browser.
  leadStats: {
    get: (params?: {
      days?: number
      start_date?: string
      end_date?: string
      cta_id?: number
      source?: string
      include_junk?: boolean
      /** 'all' charts the site's whole history, from its earliest lead. */
      range?: "all" | "today"
      classification_category?: string
      classification_subtype?: string
      classification_state?: string
      classification_min_confidence?: number
      /**
       * The reader's IANA zone. Only the arrival cuts use it, and an
       * unrecognised name falls back to UTC server-side rather than failing
       * the request.
       */
      tz?: string
    }) => apiClient.get<import("./types").LeadStats>("/leads/stats", { params }),
  },

  // The lead bucket: one count per Monday–Sunday week in the reader's zone,
  // this week's arrival times, and whether any capture path is switched on.
  leadWeekly: {
    get: (params?: { weeks?: number; tz?: string }) =>
      apiClient.get<import("./types").LeadWeekly>("/leads/weekly", { params }),
  },

  // Gated-content leads (source "wp_gate") are pulled from the site's own gate
  // plugins on a schedule, never pushed, so the list can trail reality by
  // minutes. `run` performs that pull for the current site right now and is
  // cooled down server-side (429 with retry_after) to one call a minute.
  leadGateSync: {
    status: () =>
      apiClient.get<{ data: import("./types").LeadGateSyncStatus }>("/leads/sync-wp-gates"),
    run: () =>
      apiClient.post<{ data: import("./types").LeadGateSyncResult }>("/leads/sync-wp-gates"),
  },

  leadClassifier: {
    get: () => apiClient.get<import("./types").LeadClassifierState>("/leads/classifier"),
    update: (settings: {
      enabled?: boolean
      auto_classify?: boolean
      profile?: string
      model?: string
      daily_limit?: number
      sources?: string[]
    }) => apiClient.patch<import("./types").LeadClassifierState>("/leads/classifier", settings),
    backfill: (limit = 250) =>
      apiClient.post<{
        examined: number
        scheduled: number
        remaining_unclassified: number
      }>("/leads/classifier/backfill", { limit }),
  },

  // Inbound lead webhook — client-site form plugins (WS Form etc.) POST
  // submissions to the tokenised URL and they land on /leads. The URL comes
  // from the API; never assemble it from the (guessable) site id.
  leadsWebhook: {
    get: () => apiClient.get<import("./types").LeadWebhookState>("/leads/webhook"),
    save: (enabled: boolean) =>
      apiClient.post<import("./types").LeadWebhookState>("/leads/webhook", { enabled }),
    rotate: () => apiClient.post<import("./types").LeadWebhookState>("/leads/webhook/rotate"),
  },

  // Outbound lead destinations — the mirror of leadsWebhook above. Captured
  // leads are forwarded to external platforms (GoHighLevel, Zapier, a CRM).
  // Site-scoped through the X-Active-License-Id header, so no site id here.
  leadDestinations: {
    list: () =>
      apiClient.get<{ destinations: import("./types").LeadDestination[] }>("/leads/destinations"),

    create: (data: {
      name: string
      url: string
      method?: "POST" | "PUT"
      headers?: Record<string, string>
      signing_enabled?: boolean
      include_junk?: boolean
    }) =>
      apiClient.post<{ destination: import("./types").LeadDestination }>(
        "/leads/destinations",
        data,
      ),

    update: (
      id: number,
      data: {
        name?: string
        url?: string
        method?: "POST" | "PUT"
        headers?: Record<string, string>
        signing_enabled?: boolean
        include_junk?: boolean
        enabled?: boolean
      },
    ) =>
      apiClient.patch<{ destination: import("./types").LeadDestination }>(
        `/leads/destinations/${id}`,
        data,
      ),

    delete: (id: number) =>
      apiClient.delete<{ success: boolean }>(`/leads/destinations/${id}`),

    // Synchronous: the response carries the endpoint's actual status and body.
    test: (id: number) =>
      apiClient.post<import("./types").LeadDestinationTestResult>(
        `/leads/destinations/${id}/test`,
      ),

    deliveries: (id: number, params?: { per_page?: number; page?: number }) =>
      apiClient.get<import("./types").Paginated<import("./types").LeadDelivery>>(
        `/leads/destinations/${id}/deliveries`,
        { params },
      ),

    redeliver: (id: number, deliveryId: number) =>
      apiClient.post<{ scheduled: true }>(
        `/leads/destinations/${id}/deliveries/${deliveryId}/redeliver`,
      ),

    // dry_run:true reports how many leads are eligible without sending anything.
    // The endpoint DEFAULTS dry_run to true, so a caller that omits it gets a
    // count and queues nothing - sending requires an explicit dry_run:false.
    // (This comment previously said the opposite, which is how the Send button
    // shipped omitting the flag and silently never sent.)
    backfill: (id: number, data: { limit?: number; dry_run?: boolean }) =>
      apiClient.post<import("./types").LeadDestinationBackfillResult>(
        `/leads/destinations/${id}/backfill`,
        data,
      ),
  },

  // Components (reusable content blocks embedded via [forge_component slug="..."])
  components: {
    // Site-scoped list. Creation/editing happens through the CLI
    // (`php artisan component:create`), so the UI only reads.
    list: () =>
      apiClient.get<Component[]>("/components"),

    // Returns the fully rendered, self-contained HTML for a component — the
    // exact markup the connector injects into the published page.
    //
    // Use this for previews. The sibling /components/slug/{slug} route is the
    // WordPress-facing one: it authenticates on the X-Forge-Site-Id and
    // X-Forge-Connection-Key header pair the connector sends, which a browser
    // session does not have, so calling it from the UI 400s on a missing site id.
    get: (id: number) =>
      apiClient.get<Component & { html: string }>(`/components/${id}`),

    getBySlug: (slug: string) =>
      apiClient.get<RenderedComponent>(`/components/slug/${slug}`),

    create: (data: ComponentInput) =>
      apiClient.post<Component>("/components", data),

    update: (id: number, data: Partial<ComponentInput>) =>
      apiClient.put<Component>(`/components/${id}`, data),

    delete: (id: number) =>
      apiClient.delete<{ message: string }>(`/components/${id}`),
  },

  // Post Summaries (AI-generated summaries for WordPress posts)
  postSummaries: {
    list: (params?: { per_page?: number; page?: number }) =>
      apiClient.get<{ data: PostSummary[]; meta: any }>("/post-summaries", { params }),

    get: (id: number) =>
      apiClient.get<PostSummary>(`/post-summaries/${id}`),

    generate: (data: { wp_post_id: number; post_content?: string }) =>
      apiClient.post<{ message: string }>("/post-summaries/generate", data),

    bulkGenerate: (data: { wp_post_ids: number[] }) =>
      apiClient.post<{ message: string }>("/post-summaries/bulk-generate", data),

    update: (id: number, data: { summary_markdown: string }) =>
      apiClient.put<PostSummary>(`/post-summaries/${id}`, data),

    delete: (id: number) =>
      apiClient.delete<{ message: string }>(`/post-summaries/${id}`),

    push: (id: number) =>
      apiClient.post<PostSummary>(`/post-summaries/${id}/push`),

    getSettings: () =>
      apiClient.get<SummarySettings>("/post-summaries/settings"),

    updateSettings: (data: Partial<SummarySettings>) =>
      apiClient.patch<SummarySettings>("/post-summaries/settings", data),

    previewPrompt: () =>
      apiClient.get<PromptPreview>("/post-summaries/preview-prompt"),
  },

  // Google Integrations (GSC + GA4)
  integrations: {
    // Get current Google connection status
    getGoogleConnection: () =>
      apiClient.get<{ connected: boolean; connection: GoogleConnection | null }>("/integrations/google"),

    // Get OAuth authorization URL
    getAuthUrl: () =>
      apiClient.get<{ auth_url: string }>("/integrations/google/auth-url"),

    // Handle OAuth callback
    handleCallback: (code: string, state: string) =>
      apiClient.post<{ success: boolean; connection: GoogleConnection }>("/integrations/google/callback", { code, state }),

    // Disconnect Google account
    disconnectGoogle: () =>
      apiClient.delete<{ success: boolean; message: string }>("/integrations/google"),

    // List available properties after OAuth
    listGoogleProperties: () =>
      apiClient.get<{
        gsc_sites: Array<{ site_url: string; permission_level: string }>;
        ga4_properties: Array<{ property_id: string; display_name: string; account_name?: string }>;
      }>("/integrations/google/properties"),

    // Save selected properties — pass null to explicitly clear a property, omit to leave unchanged
    saveGoogleProperties: (data: { gsc_property?: string | null; gsc_site_url?: string | null; ga4_property?: string | null; ga4_property_name?: string | null }) =>
      apiClient.post<{ success: boolean; message: string; connection: GoogleConnection }>("/integrations/google/properties", data),

    // Ahrefs Rank Tracker integration — selects which Ahrefs project drives
    // the Keyword Position Changes / Top Pages / Newly Gained Keywords report modules.
    getAhrefsConnection: () =>
      apiClient.get<{
        connected: boolean;
        connection: {
          project_id: number;
          project_name: string | null;
          project_url: string | null;
          country: string;
          keyword_count: number | null;
          connected_at: string | null;
          last_synced_at: string | null;
        } | null;
      }>("/integrations/ahrefs"),

    listAhrefsProjects: () =>
      apiClient.get<{
        projects: Array<{ project_id: number; project_name: string; url: string; keyword_count: number }>;
      }>("/integrations/ahrefs/projects"),

    connectAhrefs: (data: { project_id: number; project_name?: string; project_url?: string; country?: string; keyword_count?: number }) =>
      apiClient.post<{
        connected: boolean;
        connection: {
          project_id: number;
          project_name: string | null;
          project_url: string | null;
          country: string;
          keyword_count: number | null;
          connected_at: string | null;
        };
      }>("/integrations/ahrefs/connect", data),

    disconnectAhrefs: () =>
      apiClient.delete<{ connected: boolean; message?: string }>("/integrations/ahrefs"),

    // Slack Integration
    getSlackConnection: () =>
      apiClient.get<{
        slack: {
          connected: boolean;
          enabled: boolean;
          channel: string | null;
          team_name: string | null;
          notification_settings: {
            article_generated: boolean;
            news_generated: boolean;
            article_published: boolean;
            job_failed: boolean;
            daily_summary: boolean;
          };
        };
      }>("/integrations/slack"),

    getSlackAuthUrl: () =>
      apiClient.get<{ auth_url: string }>("/integrations/slack/auth-url"),

    updateSlackSettings: (data: {
      notifications_enabled?: boolean;
      notification_settings?: {
        article_generated?: boolean;
        news_generated?: boolean;
        article_published?: boolean;
        job_failed?: boolean;
        daily_summary?: boolean;
      };
    }) =>
      apiClient.put<{
        success: boolean;
        slack: {
          connected: boolean;
          enabled: boolean;
          channel: string | null;
          team_name: string | null;
          notification_settings: {
            article_generated: boolean;
            news_generated: boolean;
            article_published: boolean;
            job_failed: boolean;
            daily_summary: boolean;
          };
        };
      }>("/integrations/slack/settings", data),

    testSlackConnection: () =>
      apiClient.post<{ success: boolean; message?: string; error?: string }>("/integrations/slack/test"),

    disconnectSlack: () =>
      apiClient.delete<{ success: boolean; message: string }>("/integrations/slack"),

    // DataForSEO status (platform-wide, read-only)
    checkDataforseoStatus: () =>
      apiClient.get<{ connected: boolean }>("/integrations/dataforseo/status"),

    // CallRail
    getCallRailStatus: () =>
      apiClient.get<{
        connected: boolean
        enabled: boolean
        connected_at: string | null
        last_synced_at: string | null
        activity?: import("./webhook-activity").WebhookActivity
        settings: {
          account_id: string | null
          account_name: string | null
          report_recipients: string[]
          report_day: string
          report_frequency: string
        }
      }>("/integrations/callrail"),

    testCallRail: (data: { api_key: string }) =>
      apiClient.post<{
        valid: boolean
        message: string
        accounts?: { id: number; name: string }[]
      }>("/integrations/callrail/test", data),

    connectCallRail: (data: { api_key: string; account_id: string; account_name?: string }) =>
      apiClient.post<{ success: boolean }>("/integrations/callrail/connect", data),

    updateCallRailSettings: (data: { report_recipients?: string[]; report_day?: string; report_frequency?: string }) =>
      apiClient.patch<{ success: boolean; settings: any }>("/integrations/callrail/settings", data),

    disconnectCallRail: () =>
      apiClient.delete<{ success: boolean }>("/integrations/callrail"),

    getEnabledIntegrations: () =>
      apiClient.get<{ integrations: { provider: string; enabled: boolean; connected: boolean }[] }>("/integrations"),
  },

  // Usage & Analytics
  usage: {
    // Get usage summary
    summary: (params?: { period?: "today" | "week" | "month" | "all" }) =>
      apiClient.get<{
        period: string;
        summary: {
          total_calls: number;
          total_input_tokens: number;
          total_output_tokens: number;
          total_tokens: number;
          total_cost: number;
          avg_response_time_ms: number;
        };
        by_model: Array<{
          provider: string;
          model: string;
          calls: number;
          cost: number;
        }>;
        daily: Array<{
          date: string;
          calls: number;
          cost: number;
        }>;
      }>("/usage", { params }),

    // Get recent API call logs
    logs: (params?: { per_page?: number }) =>
      apiClient.get<{
        data: Array<{
          id: number;
          site_id: number;
          source_type: string | null;
          source_id: number | null;
          provider: string;
          model: string;
          input_tokens: number;
          output_tokens: number;
          cost: number;
          duration_ms: number | null;
          status: string;
          error_message: string | null;
          created_at: string;
        }>;
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
      }>("/usage/logs", { params }),

    // Get AI provider status (health, errors, usage)
    // Combines proactive health checks (hourly pings) with recent API call history
    providerStatus: () =>
      apiClient.get<{
        overall_status: "healthy" | "warning" | "error";
        critical_errors: Array<{
          provider: string;
          type: string;
          message: string;
        }>;
        providers: {
          [key: string]: {
            status: "healthy" | "warning" | "error";
            error_type: string | null;
            error_message: string | null;
            last_success_at: string | null;
            last_health_check: number | null; // Response time in ms from last health ping
            calls_24h: {
              total: number;
              success: number;
              failed: number;
            };
            cost_30d: number;
          };
        };
        total_cost_30d: number;
        last_health_check: string | null; // ISO timestamp of last health check run
      }>("/usage/provider-status"),

    // Manually trigger health check of all AI providers
    refreshHealthCheck: () =>
      apiClient.post<{
        success: boolean;
        message: string;
        checked_at: string;
        providers: {
          [key: string]: {
            status: "healthy" | "error";
            error: string | null;
            error_type: string | null;
            response_time_ms: number;
          };
        };
      }>("/usage/health-check"),
  },

  // Activity Heatmap
  activityHeatmap: {
    getActivity: (days?: number) =>
      apiClient.get<{
        data: Record<string, number>;
        total: number;
        active_days: number;
        days: number;
      }>("/activity-heatmap", { params: days ? { days } : undefined }),

    getCommits: (days?: number) =>
      apiClient.get<{
        data: Record<string, number>;
        total: number;
        active_days: number;
        days: number;
        error?: string;
      }>("/activity-heatmap/commits", { params: days ? { days } : undefined }),
  },

  // Logs - View job and API call history
  logs: {
    // kind=api: series error/success; kind=jobs: failed/running/completed.
    // Takes the same filters as apiLogs / jobLogs respectively.
    histogram: (params: { kind: "api" | "jobs"; hours: number } & Record<string, string | number>) =>
      apiClient.get<HistogramData>("/logs/histogram", { params }),

    stats: (hours = 24) =>
      apiClient.get<{
        period_hours: number;
        jobs: { total: number; completed: number; failed: number; processing: number; total_cost: number };
        api_calls: { total: number; success: number; errors: number; total_input_tokens: number; total_output_tokens: number; total_cost: number; avg_duration_ms: number };
      }>("/logs/stats", { params: { hours } }),

    filterOptions: () =>
      apiClient.get<{
        providers: string[];
        models: string[];
        source_types: string[];
        job_categories: string[];
      }>("/logs/filters"),

    jobLogs: (params?: { hours?: number; category?: string; status?: string; search?: string; page?: number; per_page?: number }) =>
      apiClient.get<{ data: any[]; current_page: number; last_page: number; total: number }>("/logs/jobs", { params }),

    jobLogDetail: (uuid: string) =>
      apiClient.get<{ job: any; api_logs: any[] }>(`/logs/jobs/${uuid}`),

    apiLogs: (params?: { hours?: number; provider?: string; model?: string; source_type?: string; status?: string; page?: number; per_page?: number }) =>
      apiClient.get<{ data: any[]; current_page: number; last_page: number; total: number }>("/logs/api-calls", { params }),

    apiLogDetail: (id: number) =>
      apiClient.get<any>(`/logs/api-calls/${id}`),
  },

  // Similarity Search - Content Cannibalization Detection
  similarity: {
    // Check if similarity search is enabled for this site
    status: () =>
      apiClient.get<{
        enabled: boolean;
        embeddings_enabled: boolean;
      }>("/similarity/status"),

    // Search for similar content across all types (topics, content, news, wordpress)
    search: (data: { query: string; threshold?: number; limit?: number }) =>
      apiClient.post<{
        enabled: boolean;
        results: Array<{
          type: 'topic' | 'content' | 'news' | 'wordpress';
          id: number;
          title: string;
          status: string;
          category: string | null;
          similarity: number;
          url: string | null;
          created_at: string | null;
        }>;
        total: number;
        query?: string;
        threshold?: number;
        error?: string;
      }>("/similarity/search", data),

    // Run cannibalization audit on all embedded content
    audit: (data: { threshold?: number; content_types?: Array<'wordpress' | 'topics' | 'content' | 'news'> }) =>
      apiClient.post<{
        enabled: boolean;
        clusters: Array<{
          item_a: {
            type: 'wordpress' | 'topic' | 'content' | 'news';
            id: number;
            title: string;
            status: string;
            url: string | null;
          };
          item_b: {
            type: 'wordpress' | 'topic' | 'content' | 'news';
            id: number;
            title: string;
            status: string;
            url: string | null;
          };
          similarity: number;
          severity: 'high' | 'moderate';
        }>;
        summary: {
          total_issues: number;
          high: number;
          moderate: number;
          items_scanned: number;
        };
        threshold: number;
        audit_id?: number;
        created_at?: string;
        error?: string;
      }>("/similarity/audit", data),

    // Get audit history for the site
    auditHistory: () =>
      apiClient.get<{
        audits: Array<{
          id: number;
          threshold: number;
          content_types: string[];
          summary: {
            total_issues: number;
            high: number;
            moderate: number;
            items_scanned: number;
          };
          status: 'running' | 'completed' | 'failed';
          created_at: string;
        }>;
      }>("/similarity/audits"),

    // Get the most recent completed audit
    latestAudit: () =>
      apiClient.get<{
        audit: {
          id: number;
          enabled: boolean;
          threshold: number;
          content_types: string[];
          clusters: Array<{
            item_a: {
              type: 'wordpress' | 'topic' | 'content' | 'news';
              id: number;
              title: string;
              status: string;
              url: string | null;
            };
            item_b: {
              type: 'wordpress' | 'topic' | 'content' | 'news';
              id: number;
              title: string;
              status: string;
              url: string | null;
            };
            similarity: number;
            severity: 'high' | 'moderate';
          }>;
          summary: {
            total_issues: number;
            high: number;
            moderate: number;
            items_scanned: number;
          };
          created_at: string;
        } | null;
      }>("/similarity/audits/latest"),

    // Get a specific audit by ID
    getAudit: (id: number) =>
      apiClient.get<{
        id: number;
        enabled: boolean;
        threshold: number;
        content_types: string[];
        clusters: Array<{
          item_a: {
            type: 'wordpress' | 'topic' | 'content' | 'news';
            id: number;
            title: string;
            status: string;
            url: string | null;
          };
          item_b: {
            type: 'wordpress' | 'topic' | 'content' | 'news';
            id: number;
            title: string;
            status: string;
            url: string | null;
          };
          similarity: number;
          severity: 'high' | 'moderate';
        }>;
        summary: {
          total_issues: number;
          high: number;
          moderate: number;
          items_scanned: number;
        };
        status: 'running' | 'completed' | 'failed';
        error_message?: string;
        created_at: string;
      }>(`/similarity/audits/${id}`),
  },

  // Autopilot - Automated content generation and publishing pipeline
  // Controls the full lifecycle: topic selection → workflow execution → WP publishing
  // State flow: disabled → enabled → (paused) → disabled
  autopilot: {
    // Settings
    getSettings: () =>
      apiClient.get<{
        settings: AutopilotSettings
        // The agency people on the client's license, assignee first.
        // from_license false = nobody is, and the fallback (Justin) gets them.
        alert_recipients: { people: { id: number; name: string }[]; from_license: boolean }
        review_rules: AutopilotReviewRule[]
      }>("/autopilot/settings"),

    updateSettings: (data: UpdateAutopilotSettingsRequest) =>
      apiClient.patch<{ settings: AutopilotSettings; workflow: unknown }>("/autopilot/settings", data),

    validateSettings: () =>
      apiClient.post<AutopilotValidation>("/autopilot/settings/validate"),

    // Control - enable/disable/pause/resume the autopilot system
    enable: () =>
      apiClient.post<{ success: boolean; settings: AutopilotSettings; warnings: string[]; schedule_items_created: number }>("/autopilot/enable"),

    disable: () =>
      apiClient.post<{ success: boolean; settings: AutopilotSettings }>("/autopilot/disable"),

    pause: (reason?: string) =>
      apiClient.post<{ success: boolean; settings: AutopilotSettings }>("/autopilot/pause", { reason }),

    resume: () =>
      apiClient.post<{ success: boolean; settings: AutopilotSettings }>("/autopilot/resume"),

    // Force an immediate run cycle (useful for testing)
    forceRun: () =>
      apiClient.post<{ success: boolean; schedule_item: AutopilotScheduleItem }>("/autopilot/force-run"),

    // Schedule - upcoming and past publish slots
    getSchedule: (params?: AutopilotScheduleParams) =>
      apiClient.get<{
        schedule: AutopilotScheduleItem[]
        total: number
      }>("/autopilot/schedule", { params }),

    generateSchedule: (data?: GenerateScheduleRequest) =>
      apiClient.post<{
        message: string
        items_created: number
        schedule: AutopilotScheduleItem[]
      }>("/autopilot/schedule/generate", data),

    cancelScheduledItem: (id: number) =>
      apiClient.delete<{ success: boolean; item: AutopilotScheduleItem }>(`/autopilot/schedule/${id}`),

    retryScheduledItem: (id: number) =>
      apiClient.post<{ success: boolean; item: AutopilotScheduleItem }>(`/autopilot/schedule/${id}/retry`),

    // Monitoring - real-time status, activity feed, stats
    getStatus: () =>
      apiClient.get<AutopilotStatus>("/autopilot/status"),

    getActivity: (params?: AutopilotActivityParams) =>
      apiClient.get<{
        activity: AutopilotActivityLogEntry[]
      }>("/autopilot/activity", { params }),

    getStats: (params?: AutopilotStatsParams) =>
      apiClient.get<AutopilotStats>("/autopilot/stats", { params }),

    healthCheck: () =>
      apiClient.get<{
        status: string
        enabled: boolean
        warnings: string[]
        available_topics: number
        articles_today: number
        daily_limit: number
        consecutive_failures: number
        last_run_at: string | null
        next_run_at: string | null
      }>("/autopilot/health-check"),
  },

  admin: {
    /**
     * Bring one site's connector up to the current release.
     *
     * The site fetches the package itself, so this says "update", not
     * "install this". It exists because a site whose WP-cron has stopped
     * never learns an update is available — it keeps serving and keeps
     * answering Forge while every scheduled job on it is dead.
     */
    pushConnectorUpdate: (siteId: number) =>
      apiClient.post<{
        success: boolean
        updated: boolean
        verified: boolean
        reason: string | null
        previous_version: string | null
        current_version: string | null
      }>(`/admin/sites/${siteId}/connector/update`, {}),

    // Overview dashboard
    overview: (params?: { period?: string }) =>
      apiClient.get<{
        period: string
        summary: {
          total_cost: number
          content_cost: number
          news_cost: number
          image_cost: number
          dataforseo_cost: number
          content_count: number
          news_count: number
          workflow_count: number
          active_sites: number
          active_users: number
        }
        sites: Array<{
          id: number
          domain: string
          client_name: string
          license_id: number
          content: { count: number; cost: number }
          news: { count: number; cost: number }
          images: { count: number; cost: number }
          dataforseo?: { cost: number }
          wp_connection?: {
            method: "app_password" | "plugin"
            status: string
            plugin_version: string | null
          } | null
          workflows: number
          total_cost: number
          last_active: string | null
        }>
        plugin_telemetry?: {
          // Ordered newest-first; status is computed server-side against
          // latest_version with version_compare semantics. Each version row
          // carries its member sites for the drill-down dialog.
          plugin_versions: Array<{
            version: string
            count: number
            status: "latest" | "outdated" | "ahead" | "unknown"
            sites: Array<{
              site_id: number
              domain: string | null
              name: string | null
              connection_status: string
              last_heartbeat_at: string | null
              /** Null on connectors predating cron reporting — not the same
               *  as healthy, and shown as unknown rather than as zero. */
              cron_overdue_events?: number | null
              cron_oldest_overdue_at?: string | null
            }>
          }>
          /** What Forge is serving. First-hand since releases are hosted here;
           *  with the source repo private this is the only place the changelog
           *  and publish date are visible. Newest first. */
          releases?: Array<{
            version: string
            size_bytes: number
            sha256: string
            git_sha: string | null
            published_at: string
            changelog: string | null
            sites_on_this_version: number
          }>
          wp_versions?: Array<{
            version: string
            count: number
            sites: Array<{
              site_id: number
              domain: string | null
              name: string | null
              connection_status: string
              last_heartbeat_at: string | null
            }>
          }>
          php_versions?: Array<{
            version: string
            count: number
            sites: Array<{
              site_id: number
              domain: string | null
              name: string | null
              connection_status: string
              last_heartbeat_at: string | null
            }>
          }>
          // Legacy shapes (still sent) — superseded by wp_versions/php_versions.
          wp_version_distribution: Record<string, number>
          php_version_distribution: Record<string, number>
          total_plugin_connections: number
          healthy_connections: number
          stale_connections: number
          outdated_count: number
          ahead_count: number
          latest_version: string
        }
      }>("/admin/overview", { params }),

    // Site detail
    siteDetail: (id: number, params?: { period?: string }) =>
      apiClient.get<{
        period: string
        site: {
          id: number
          domain: string
          client_name: string
          license_status: string
          last_seen_at: string | null
          wp_version: string | null
          plugin_version: string | null
          latest_plugin_version: string
          plugin_status: "latest" | "outdated" | "ahead" | null
          api_keys: Record<string, boolean>
        }
        totals: { calls: number; cost: number; tokens: number }
        by_provider: Array<{ provider: string; calls: number; cost: number; tokens: number }>
        by_date: Array<{ date: string; calls: number; cost: number; tokens: number }>
        recent_content: Array<{ id: number; title: string; status: string; created_at: string }>
        recent_workflows: Array<{ id: number; workflow_id: number; status: string; created_at: string }>
        recent_news: Array<{ id: number; title: string; status: string; created_at: string }>
      }>(`/admin/sites/${id}`, { params }),

    // User management
    users: (params?: { include_inactive?: boolean }) =>
      apiClient.get<{
        users: Array<{
          id: number
          name: string
          email: string
          avatar: string | null
          role: string
          is_active: boolean
          api_access_enabled?: boolean
          prompt_log_enabled?: boolean
          outreach_enabled?: boolean
          events_access_enabled?: boolean
          /** The only workspaces this person has; null only for admins and the owner (everything). */
          workspaces?: string[] | null
          /** Studio profiles granted to this person by name. */
          studio_profile_ids?: number[]
          /** Outside the agency: only the boards in `boards` (docs/external-users.md in the API). */
          is_external?: boolean
          /** Every non-admin's boards (General included when they are on it) and their role on each. */
          boards?: BoardMembership[]
          /** The admin pages this person may open: every key for the owner, the granted ones for an admin, [] otherwise. */
          admin_permissions?: string[]
          /** Whether the signed-in person may edit, deactivate or delete this row (the owner: anyone; an admin: non-admins only). */
          can_manage?: boolean
          last_login_at: string | null
          created_at: string
          licenses: Array<{ id: number; key: string; client_name: string; domain: string; assigned_at: string }>
        }>
        /** The roles the signed-in person may assign: owner only for an owner. */
        role_options?: string[]
      }>("/admin/users", { params }),

    createUser: (data: { email: string; name?: string; role: string; workspaces?: string[] | null; studio_profile_ids?: number[]; is_external?: boolean; boards?: BoardGrant[]; send_invite?: boolean }) =>
      apiClient.post("/admin/users", data),

    /** Email someone their invitation to sign in (again). */
    inviteUser: (id: number) =>
      apiClient.post<{ success: boolean; message: string }>(`/admin/users/${id}/invite`),

    updateUser: (id: number, data: { name?: string; email?: string; role?: string; is_active?: boolean; api_access_enabled?: boolean; prompt_log_enabled?: boolean; outreach_enabled?: boolean; events_access_enabled?: boolean; workspaces?: string[] | null; studio_profile_ids?: number[]; is_external?: boolean; boards?: BoardGrant[] }) =>
      apiClient.put(`/admin/users/${id}`, data),

    deleteUser: (id: number) =>
      apiClient.delete(`/admin/users/${id}`),

    /** The admin permission registry for the owner's checkbox editor (owner only). */
    adminPermissionRegistry: () =>
      apiClient.get<{ data: AdminPermissionRegistry }>("/admin/admin-permissions").then((r) => r.data),

    /** Replace an admin's admin permissions (owner only; grantable keys only). */
    updateAdminPermissions: (id: number, permissions: string[]) =>
      apiClient.put<{ success: boolean; user: { id: number; admin_permissions: string[] } }>(`/admin/users/${id}/admin-permissions`, { permissions }),

    toggleUserActive: (id: number) =>
      apiClient.patch<{ success: boolean; message: string; user: { id: number; name: string; email: string; is_active: boolean } }>(`/admin/users/${id}/toggle-active`),

    assignLicense: (data: { user_id: number; license_id: number }) =>
      apiClient.post("/admin/users/assign-license", data),

    removeLicense: (data: { user_id: number; license_id: number }) =>
      apiClient.post("/admin/users/remove-license", data),

    // API activity: every request made with a personal API token, newest
    // first. Returns the page whole because the caller needs `meta` too.
    apiActivity: (params?: ApiActivityParams) =>
      apiClient.get<ApiActivityPage>("/admin/api-activity", { params }),

    // One row per user who holds a token: how many, how busy, how often refused.
    apiActivitySummary: () =>
      apiClient.get<{ data: ApiActivitySummaryRow[] }>("/admin/api-activity/summary").then(r => r.data),

    // Same filters as apiActivity; series denied/error/ok.
    apiActivityHistogram: (params?: ApiActivityParams) =>
      apiClient.get<HistogramData>("/admin/api-activity/histogram", { params }),

    userStats: (params?: { period?: string }) =>
      apiClient.get<{
        user_stats: Record<string, { user_id: number; activity: { content_count: number; workflow_count: number; cost: number } }>
      }>("/admin/user-stats", { params }),

    // The client dashboard's four stat cards summed across every site, for the
    // "12 here / 340 everywhere" line. Takes the same start_date/end_date the
    // dashboard sends to /analytics/overview so both halves of a card describe
    // the same window. Admin-only server-side — never call it for a client.
    dashboardTotals: (params?: { start_date?: string; end_date?: string }) =>
      apiClient.get<{
        success: boolean
        totals: {
          total_content: number
          pending_review: number
          total_cost: number
          published: number
        }
      }>("/admin/dashboard-totals", { params }),

    // License management
    licenses: () =>
      apiClient.get<{
        licenses: Array<{
          id: number
          key: string
          client_name: string
          domain: string
          status: string
          sites_count: number
          users_count: number
          users: Array<{ id: number; name: string; email: string }>
          created_at: string
        }>
      }>("/admin/licenses"),

    createLicense: (data: { client_name: string; expires_at?: string }) =>
      apiClient.post("/admin/licenses", data),

    updateLicense: (id: number, data: { client_name?: string; status?: string }) =>
      apiClient.put(`/admin/licenses/${id}`, data),

    deleteLicense: (id: number) =>
      apiClient.delete(`/admin/licenses/${id}`),

    // Broadcast
    getBroadcast: () =>
      apiClient.get<{ broadcast: { id: number; message: string; type: string; is_dismissible: boolean; link_url: string | null; link_text: string | null } | null }>("/admin/broadcast"),

    saveBroadcast: (data: { message: string; type: string; is_dismissible?: boolean; link_url?: string; link_text?: string }) =>
      apiClient.post("/admin/broadcast", data),

    clearBroadcast: () =>
      apiClient.delete("/admin/broadcast"),

    // Platform Settings - Slack intake history (the task bot's audit trail)
    getSlackIntakeEvents: (params?: { kind?: string; page?: number; per_page?: number }) =>
      apiClient.get<Paginated<{
        id: number
        kind: string
        slack_team_id: string
        channel_id: string
        slack_user_id: string
        user_id: number | null
        user?: { id: number; name: string } | null
        message_ts: string
        thread_ts: string | null
        text_excerpt: string | null
        result: Record<string, unknown> | null
        created_at: string
      }>>("/admin/slack-intake/events", { params }),

    // Platform Settings - DataForSEO
    getDataforseoSettings: () =>
      apiClient.get<{
        connected: boolean
        login: string | null
        balance: number | null
        total_deposited: number | null
        rate_limit_per_minute: number | null
        error?: string
      }>("/admin/platform-settings/dataforseo"),

    updateDataforseoSettings: (data: { login: string; password: string }) =>
      apiClient.put<{
        connected: boolean
        login: string | null
        balance: number | null
        error?: string
      }>("/admin/platform-settings/dataforseo", data),

    deleteDataforseoSettings: () =>
      apiClient.delete("/admin/platform-settings/dataforseo"),

    // Platform Settings - HireTalent Employer API (the Team page's roster source)
    getHiretalentSettings: () =>
      apiClient.get<HiretalentSettings>("/admin/platform-settings/hiretalent"),

    getHiretalentEmployees: () =>
      apiClient.get<{ employees: HiretalentEmployee[]; users: HiretalentForgeUser[] }>(
        "/admin/platform-settings/hiretalent/employees"
      ),

    linkHiretalentEmployee: (id: number, userId: number) =>
      apiClient.put<HiretalentEmployee>(`/admin/platform-settings/hiretalent/employees/${id}/user-link`, {
        user_id: userId,
      }),

    unlinkHiretalentEmployee: (id: number) =>
      apiClient.delete<HiretalentEmployee>(`/admin/platform-settings/hiretalent/employees/${id}/user-link`),

    updateHiretalentSettings: (data: {
      base_url?: string
      api_key?: string
      account_label?: string
      manage_key_url?: string
      roster_source_enabled?: boolean
      webhook_secret?: string
    }) =>
      apiClient.put<HiretalentSettings & { test: HiretalentConnectionTest }>(
        "/admin/platform-settings/hiretalent",
        data
      ),

    deleteHiretalentSettings: () =>
      apiClient.delete<HiretalentSettings>("/admin/platform-settings/hiretalent"),

    syncHiretalentRoster: () =>
      apiClient.post<
        HiretalentSettings & {
          result: { synced: number; matched: number; unmatched: number; marked_inactive: number; skipped: number }
        }
      >("/admin/platform-settings/hiretalent/sync"),

    // Platform Settings - Global AI budget cap (kill switch for all paid AI calls)
    getBudget: () =>
      apiClient.get<{
        enabled: boolean
        cap: number
        spent: number
        remaining: number
        exceeded: boolean
        month: string
      }>("/admin/platform-settings/budget"),

    updateBudget: (data: { cap?: number; enabled?: boolean }) =>
      apiClient.put<{
        enabled: boolean
        cap: number
        spent: number
        remaining: number
        exceeded: boolean
        month: string
      }>("/admin/platform-settings/budget", data),

    getBudgetAudit: () =>
      apiClient.get<{
        entries: Array<{
          id: number
          user: string
          action: string
          old_value: { cap_usd: number; enabled: boolean } | null
          new_value: { cap_usd: number; enabled: boolean } | null
          created_at: string | null
        }>
      }>("/admin/platform-settings/budget/audit"),

    // Google service-account access inventory. Pass recheck=true to re-query
    // Google live (the Recheck button); omit for the ~10-min cached copy.
    googleSaAccess: (recheck?: boolean) =>
      apiClient.get<GoogleSaAccess>("/admin/google-sa/access", {
        params: recheck ? { recheck: 1 } : undefined,
      }),

    // Wire every service-account-visible unconnected site, then refresh weekly
    // snapshots. Slow-ish (multiple Google + GSC queries). 422 { error } when
    // the service account isn't configured.
    googleSaSync: () =>
      apiClient.post<{ output: string }>("/admin/google-sa/sync"),

    // Repository Registry — the allowlist of repositories Forge may read.
    // Registering one here is what makes it linkable from a site's
    // Integrations tab; nothing else can widen what the token reaches.
    listGithubRepositories: () =>
      apiClient.get<{ repositories: GithubRegistryRepository[] }>("/admin/github-repositories"),

    // 422s when the agency token cannot read the repository or it is already
    // registered — both messages name the cause, so show them verbatim.
    addGithubRepository: (data: { repo_full_name: string }) =>
      apiClient.post<{ repository: GithubRegistryRepository }>("/admin/github-repositories", data),

    // 422 { message } while any site still links it — those must be unlinked
    // first, so removing from the registry can never orphan a site's report.
    removeGithubRepository: (id: number) =>
      apiClient.delete<{ message?: string }>(`/admin/github-repositories/${id}`),

    // People & Access — the agency-ops registry, read-only. Declared grants
    // are policy, not observed provider access, and there is deliberately no
    // write endpoint: access changes happen in the registry repo (or later
    // through gated connectors). 503 { message } when the GitHub fetch fails.
    peopleAccess: () =>
      apiClient.get<PeopleAccessRegistry>("/admin/people-access"),

    // The observed half of the same panel: what Google Workspace actually
    // reports, for diffing against the declared registry above. 503 { message }
    // when the connector is unconfigured or Google is unreachable — an empty
    // roster would read as "nobody has access", so it is never served as one.
    peopleAccessObserved: () =>
      apiClient.get<GoogleDirectoryObserved>("/admin/people-access/google/observed"),

    // Drafts an access change WITHOUT touching Google. 422 { message } when the
    // action would change nothing, the account does not exist, or the target is
    // the account the connector itself signs in as; 503 when Google is
    // unreachable — all four name the cause, so show them verbatim.
    createPeopleAccessPlan: (data: { action: PeopleAccessActionType; email: string }) =>
      apiClient.post<PeopleAccessPlanResponse>("/admin/people-access/google/plans", data),

    // Executes a drafted change. The API re-matches confirm_email against the
    // plan's target and refuses a plan that is not still pending (422), then
    // verifies the write against a fresh read — a 502 means the change did not
    // land, and the returned plan is already recorded as failed.
    applyPeopleAccessPlan: (id: number, confirmEmail: string) =>
      apiClient.post<PeopleAccessPlanResponse>(`/admin/people-access/google/plans/${id}/apply`, {
        confirm_email: confirmEmail,
      }),

    // A colleague's directory photo, as a URL for an <img src> — loaded by the
    // browser on the same Sanctum cookie as everything else, like the invoice
    // PDF above. Google's own thumbnailPhotoUrl needs the connector's delegated
    // credential, so the bytes are proxied by the API rather than that
    // credential being handed to a browser. 204 for somebody with no photo,
    // which fires the image's error event; callers fall back to initials.
    peopleAccessPhotoUrl: (email: string) =>
      `${API_URL}/admin/people-access/google/photo?email=${encodeURIComponent(email)}`,

    // Drafts a full offboard WITHOUT touching Google, and returns the ordered
    // steps it will take. 422 { message } for the usual refusals, plus
    // 422 { message, reason: "missing_scope", missing_scopes } when a step the
    // caller opted into needs a scope Google has not granted the connector —
    // that message names the scope and the Admin console path, so show it
    // verbatim. Deleting without a `drive_transfer_to` is refused for the same
    // reason: the files go with the account. Steps that are refused rather than
    // refusing the plan (sign-out, and the licence release when its scope is
    // ungranted) come back inside plan.steps already marked `refused`.
    createOffboardPlan: (data: OffboardPlanInput) =>
      apiClient.post<PeopleAccessLifecyclePlanResponse>(
        "/admin/people-access/google/offboard-plans",
        data
      ),

    // Runs the steps in order and stops at the first failure. A 502 means the
    // sequence halted — the returned plan already records which steps landed
    // and which never ran, so render plan.steps rather than a single verdict.
    applyOffboardPlan: (id: number, confirmEmail: string) =>
      apiClient.post<PeopleAccessLifecyclePlanResponse>(
        `/admin/people-access/google/offboard-plans/${id}/apply`,
        { confirm_email: confirmEmail }
      ),

    // 422 { message } when the address is on a domain the Workspace does not
    // use, already exists, or is the connector's own account.
    createOnboardPlan: (data: OnboardPlanInput) =>
      apiClient.post<PeopleAccessLifecyclePlanResponse>(
        "/admin/people-access/google/onboard-plans",
        data
      ),

    // The one call in Forge that returns a secret. `temporary_password` is
    // generated at apply time, sent once, and stored nowhere — not by the API,
    // and not by the query cache, which is why the caller must keep gcTime at 0
    // and never write the result anywhere that outlives the dialog.
    applyOnboardPlan: (id: number, confirmEmail: string) =>
      apiClient.post<PeopleAccessLifecyclePlanResponse>(
        `/admin/people-access/google/onboard-plans/${id}/apply`,
        { confirm_email: confirmEmail }
      ),

    // Every access change Forge has made, newest first — standalone suspends
    // and lifecycle steps alike. Read-only history; the API caps limit at 200.
    peopleAccessActions: (limit = 50) =>
      apiClient.get<PeopleAccessActionsResponse>("/admin/people-access/google/actions", {
        params: { limit },
      }),

    // Who GitHub says can reach the repositories in the admin allowlist above.
    // Read-only — revoking a collaborator stays in GitHub's own UI. Repos the
    // token cannot read are skipped; losing all of them is a 503, because an
    // empty list here would read as "nobody can reach our code".
    peopleAccessGithubObserved: () =>
      apiClient.get<GithubObserved>("/admin/people-access/github/observed"),

    // Who Slack lets into the agency workspace, from a dedicated read-only bot
    // token. 503 { message, reason } — `reason: "not_configured"` means the
    // token was never set, which the UI shows as not connected, not an error.
    peopleAccessSlackObserved: () =>
      apiClient.get<SlackObserved>("/admin/people-access/slack/observed"),

    // The SSH key inventory the dev box publishes into the agency-ops repo,
    // fingerprints only. An inventory that has never been published comes back
    // as an empty list; 503 is reserved for a repo that could not be read.
    peopleAccessSshObserved: () =>
      apiClient.get<SshObserved>("/admin/people-access/ssh/observed"),

    // Agent box accounts: what miami-agent-box reports, the per-person
    // onboarding checklist, and jobs its root poller claims within a minute.
    // An admin may act only on non-admins; the API enforces it.
    peopleAccessBox: () =>
      apiClient.get<AgentBoxAccess>("/admin/people-access/box"),
    createBoxJob: (input: BoxJobInput) =>
      apiClient.post<{ job: BoxJob }>("/admin/people-access/box/jobs", input),
    cancelBoxJob: (id: number) =>
      apiClient.post<{ job: BoxJob }>(`/admin/people-access/box/jobs/${id}/cancel`, {}),
    setBoxChecklistItem: (userId: number, input: { item: string; done: boolean; note?: string }) =>
      apiClient.put<{ checklist: LifecycleCheckItem[] }>(`/admin/people-access/box/checklist/${userId}`, input),
    linkBoxAccount: (userId: number, linuxUser: string | null) =>
      apiClient.put<{ box_username: string | null }>("/admin/people-access/box/link", {
        user_id: userId,
        linux_user: linuxUser,
      }),

    // Who each client's WordPress says can edit its content. Unlike the three
    // above this makes no outbound call — the fleet is sixteen installs on ten
    // hosts, so the listing serves Forge's cache of the ordinary content sync.
    // A connection whose list has never been fetched comes back with an empty
    // `users` and a null `usersSyncedAt`, which is not the same as no editors.
    //
    // Clients whose Forge licence has lapsed are left out by default and
    // counted in `hiddenDisabled`. Forge stopped calling their sites, so those
    // lists are frozen at the last sync and the people on them are not who the
    // agency works with today. Pass includeDisabled for the offboarding-review
    // case; they come back carrying `licenseActive: false`.
    peopleAccessWordPressObserved: (includeDisabled = false) =>
      apiClient.get<WordPressObserved>("/admin/people-access/wordpress/observed", {
        params: includeDisabled ? { include_disabled: 1 } : undefined,
      }),

    // Re-reads one site and returns its refreshed block. Writes only Forge's
    // own database — nothing here changes WordPress. 503 { message, reason }
    // where `reason: "license_inactive"` means Forge refused to call the site
    // rather than the site refusing to answer; on any failure the cached list
    // is left standing, so keep showing it.
    refreshPeopleAccessWordPress: (connectionId: number) =>
      apiClient.post<WordPressObservedRefresh>("/admin/people-access/wordpress/refresh", {
        connection_id: connectionId,
      }),
  },

  // Onboarding - Getting started checklist for new users
  onboarding: {
    status: () =>
      apiClient.get<OnboardingStatus>("/onboarding/status"),

    dismiss: () =>
      apiClient.post<{ success: boolean }>("/onboarding/dismiss"),

    skipStep: (step: string) =>
      apiClient.post<{ success: boolean; skipped_steps: string[] }>("/onboarding/skip-step", { step }),
  },

  // Reports - Performance report generation with Google Slides
  reports: {
    list: (params?: { page?: number; per_page?: number }) =>
      apiClient.get<{
        data: Array<{
          id: number
          site_id: number
          name: string
          date_start: string
          date_end: string
          comparison_start: string
          comparison_end: string
          status: 'pending' | 'generating' | 'completed' | 'failed'
          pdf_path: string | null
          error_message: string | null
          created_at: string
          updated_at: string
        }>
        current_page: number
        last_page: number
        total: number
      }>("/reports", { params }),

    get: (id: number) =>
      apiClient.get<{
        id: number
        name: string
        date_start: string
        date_end: string
        comparison_start: string
        comparison_end: string
        status: 'pending' | 'generating' | 'completed' | 'failed'
        pdf_path: string | null
        raw_data: Record<string, unknown> | null
        ai_insights: Record<string, unknown> | null
        error_message: string | null
        created_at: string
      }>(`/reports/${id}`),

    generate: (data: {
      date_start: string
      date_end: string
      comparison_start: string
      comparison_end: string
      name?: string
      sections?: string[]
    }) =>
      apiClient.post<{
        id: number
        name: string
        status: string
      }>("/reports/generate", data),

    download: async (id: number) => {
      // apiClient.get with responseType blob returns the Blob directly (response.data)
      const blob = await apiClient.get<Blob>(`/reports/${id}/download`, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `report-${id}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    delete: (id: number) =>
      apiClient.delete(`/reports/${id}`),

    sendToSlack: (id: number) =>
      apiClient.post<{ success: boolean; message: string; error?: string }>(`/reports/${id}/slack`),

    schedule: {
      get: () =>
        apiClient.get<{
          enabled: boolean
          frequency: "weekly" | "biweekly"
          period: "7d" | "14d" | "30d"
          report_type: "quick_stats" | "pdf" | "both"
          save_to_dashboard: boolean
          recipients: string[]
          send_day: number
          slack_channel: string | null
          last_sent_at: string | null
          next_send_at: string | null
        }>("/reports/schedule"),

      save: (data: {
        enabled: boolean
        frequency: "weekly" | "biweekly"
        period: "7d" | "14d" | "30d"
        report_type: "quick_stats" | "pdf" | "both"
        save_to_dashboard: boolean
        recipients: string[]
        send_day: number
        slack_channel: string | null
      }) =>
        apiClient.post("/reports/schedule", data),
    },
  },

  // Site Repositories — GitHub repos whose commits feed the report's git_activity section
  siteRepositories: {
    list: () =>
      apiClient.get<{ repositories: SiteRepository[] }>("/site-repositories"),

    // The admin-managed allowlist, readable by any team member — it is the
    // option list for the picker on the site's Integrations tab.
    registry: () =>
      apiClient.get<{ repositories: RegistryRepositoryOption[] }>("/site-repositories/registry"),

    // 422s when the agency GitHub token cannot read the repository, or when a
    // non-admin names one that is not on the registry. Either message names the
    // reason and what to do about it, so surface it rather than a generic
    // failure. Admins bypass the registry check and register the repo instead.
    create: (data: CreateSiteRepositoryRequest) =>
      apiClient.post<{ repository: SiteRepository }>("/site-repositories", data),

    update: (id: number, data: UpdateSiteRepositoryRequest) =>
      apiClient.patch<{ repository: SiteRepository }>(`/site-repositories/${id}`, data),

    remove: (id: number) =>
      apiClient.delete<{ message?: string }>(`/site-repositories/${id}`),
  },

  // Site Activity — the WordPress connector's audit trail: who touched the
  // site and whether it was us. Requires connector >= 1.15.0 to report at all.
  // Agentic Alerts — user-defined monitors over Forge's own signals.
  //
  // Conditions are long-lived rows that change state in place, so the feed is
  // re-read rather than appended to. Nothing here writes to a client site.
  alerts: {
    // Build params with buildFeedParams — "all" is expressed by omitting the
    // key, never by sending the literal string.
    feed: (params: Record<string, string | number>) =>
      apiClient.get<AlertFeedResponse>("/alerts/feed", { params }),

    // The full condition: evidence, the judge's write-up including what it
    // could NOT establish, and the event history.
    condition: (id: number) =>
      apiClient.get<{ condition: AlertConditionDetail }>(`/alerts/conditions/${id}`),

    ack: (id: number) =>
      apiClient.post<{ condition: AlertConditionDetail }>(`/alerts/conditions/${id}/ack`),

    monitors: {
      list: () => apiClient.get<AlertMonitorsResponse>("/alerts/monitors"),

      create: (data: Record<string, unknown>) =>
        apiClient.post<{ monitor: AlertMonitor }>("/alerts/monitors", data),

      // Seeded monitors accept only `enabled` and threshold overrides — see
      // buildSeededMonitorPayload.
      update: (id: number, data: Record<string, unknown>) =>
        apiClient.put<{ monitor: AlertMonitor }>(`/alerts/monitors/${id}`, data),

      // Custom monitors only; the API refuses to delete a seeded one.
      remove: (id: number) => apiClient.delete<{ message: string }>(`/alerts/monitors/${id}`),

      // Starts a QUEUED test run — the replay makes real model calls and
      // takes minutes, longer than a request can hold open — and answers 202
      // with a run id. Poll testRun until status is complete or failed.
      test: (id: number) => apiClient.post<AlertTestRunStart>(`/alerts/monitors/${id}/test`),

      testRun: (id: number, runId: string) =>
        apiClient.get<AlertTestRun>(`/alerts/monitors/${id}/test/${runId}`),
    },
  },

  siteActivity: {
    // Newest first. Build params with buildActivityParams — the default view
    // asks for external activity only, which is an explicit filter rather
    // than an omission.
    list: (params: Record<string, string | number | boolean>) =>
      apiClient.get<{
        events: SiteActivityEvent[]
        hosts: string[]
        pagination: SiteActivityPagination
      }>("/site-activity", { params }),

    // Everything the connector captured for one event, including the raw
    // context object and the full actor (os user, session, IP).
    get: (id: number) =>
      apiClient.get<{ event: SiteActivityEventDetail }>(`/site-activity/${id}`),

    summary: () =>
      apiClient.get<SiteActivitySummary>("/site-activity/summary"),

    // Same filters as list (page params are ignored); series flagged/external/forge.
    histogram: (params: Record<string, string | number | boolean>) =>
      apiClient.get<HistogramData>("/site-activity/histogram", { params }),
  },

  // Projects — the optional sprint/experiment layer above task sections
  projects: {
    list: (params?: { status?: string; site_id?: number; board?: string }) =>
      apiClient.get<{ data: Project[] }>("/projects", { params }).then(r => r.data),
    // The detail endpoint eager-loads the project's tasks; the list one does not.
    get: (id: number) =>
      apiClient.get<{ data: Project & { tasks: Task[] } }>(`/projects/${id}`).then(r => r.data),
    create: (data: { name: string; description?: string | null; status?: string; health?: string | null; site_id?: number | null; board_id?: number | null; lead_id?: number | null; start_date?: string | null; target_date?: string | null; collaborator_ids?: number[] }) =>
      apiClient.post<{ data: Project }>("/projects", data).then(r => r.data),
    update: (id: number, data: Record<string, unknown>) =>
      apiClient.put<{ data: Project }>(`/projects/${id}`, data).then(r => r.data),
    delete: (id: number) =>
      apiClient.delete<{ message?: string }>(`/projects/${id}`),
    // "Mark done": the envelope carries both the project and the verdict the
    // celebration screen shows, so it is deliberately NOT unwrapped here.
    complete: (id: number) =>
      apiClient.post<{ data: Project; completion: import("./project-completion").ProjectCompletion }>(`/projects/${id}/complete`),
    // Send every project the board is showing, not just the moved one —
    // `position` is a single global column the list view reads in ascending
    // order, so a partial renumber interleaves with rows still at 0.
    reorder: (projects: { id: number; position: number }[]) =>
      apiClient.post<{ message: string }>("/projects/reorder", { projects }),
  },

  // Tasks — internal project management (replaces Asana)
  tasks: {
    list: (params?: Record<string, string | number | undefined>) =>
      apiClient.get<{ data: Task[] }>("/tasks", { params }).then(r => r.data),
    // Sending per_page switches /tasks to a paginated body. `list` above would
    // discard the meta, which is exactly what a caller asking for a window
    // needs — the total is no longer the length of what it was handed.
    listPage: (params: Record<string, string | number | undefined>) =>
      apiClient.get<{ data: Task[]; meta: { current_page: number; per_page: number; total: number; last_page: number; has_more: boolean } }>("/tasks", { params }),
    // `ref` is a numeric id or a key (FOR-123, or a key the task had before it moved).
    get: (ref: number | string) =>
      apiClient.get<{ data: Task }>(`/tasks/${encodeURIComponent(String(ref))}`).then(r => r.data),
    create: (data: { title: string; description?: string; priority?: string; site_id?: number | null; content_item_id?: number | null; section?: string; project_id?: number | null; assignee_id?: number | null; due_date?: string | null; due_at?: string | null; recurring_interval?: string | null; parent_id?: number | null; board?: string; status?: string; tag_ids?: number[] }) =>
      apiClient.post<{ data: Task }>("/tasks", data).then(r => r.data),
    update: (id: number, data: Record<string, unknown>) =>
      apiClient.put<{ data: Task }>(`/tasks/${id}`, data).then(r => r.data),
    delete: (id: number) =>
      apiClient.delete(`/tasks/${id}`).then(r => r.data),
    complete: (id: number) =>
      apiClient.post<{ data: Task }>(`/tasks/${id}/complete`).then(r => r.data),
    reopen: (id: number) =>
      apiClient.post<{ data: Task }>(`/tasks/${id}/reopen`).then(r => r.data),
    reorder: (tasks: { id: number; position: number }[]) =>
      apiClient.post("/tasks/reorder", { tasks }).then(r => r.data),
    bulk: (taskIds: number[], action: string, data?: { assignee_id?: number | null; section?: string | null; tag_ids?: number[] }) =>
      apiClient.post<{ message: string; affected: number; skipped_blocked?: number[] }>("/tasks/bulk", { task_ids: taskIds, action, ...data }).then(r => r.data),
    importCsv: (file: File) => {
      const formData = new FormData()
      formData.append("file", file)
      return apiClient.post<{ success: boolean; imported: number; skipped: number; errors: string[]; error_count: number }>("/tasks/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    },
    // Both answer `{ data: {...} }`; the envelope is opened exactly once, here,
    // and callers get the flat preferences object (see task-notification-preferences.ts).
    getNotificationPreferences: (): Promise<TaskNotificationPreferences> =>
      apiClient.get<TaskNotificationPreferencesBody>("/tasks/notification-preferences").then(readTaskNotificationPreferences),
    updateNotificationPreferences: (prefs: Partial<TaskNotificationPreferences>): Promise<TaskNotificationPreferences> =>
      apiClient.put<TaskNotificationPreferencesBody>("/tasks/notification-preferences", prefs).then(readTaskNotificationPreferences),
    // Per-board and per-section email settings (Tasks → Settings). Same
    // envelope rule: opened once, in readTaskEmailPreferences.
    getEmailPreferences: (): Promise<TaskEmailPreferences> =>
      apiClient.get<TaskEmailPreferencesBody>("/me/task-notification-preferences").then(readTaskEmailPreferences),
    updateEmailPreferences: (body: TaskEmailPreferencesPut): Promise<TaskEmailPreferences> =>
      apiClient.put<TaskEmailPreferencesBody>("/me/task-notification-preferences", body).then(readTaskEmailPreferences),
  },

  taskSections: {
    list: () =>
      apiClient.get<{ data: TaskSection[] }>("/task-sections").then(r => r.data),
    create: (data: { name: string; emoji?: string | null; color?: string | null }) =>
      apiClient.post<TaskSection>("/task-sections", data).then(r => r.data),
    update: (id: number, data: { name?: string; emoji?: string | null; color?: string | null; position?: number }) =>
      apiClient.put<TaskSection>(`/task-sections/${id}`, data).then(r => r.data),
    delete: (id: number) =>
      apiClient.delete(`/task-sections/${id}`).then(r => r.data),
    reorder: (sections: { id: number; position: number }[]) =>
      apiClient.post("/task-sections/reorder", { sections }).then(r => r.data),
  },

  taskComments: {
    list: (taskId: number) =>
      apiClient.get<{ data: TaskComment[] }>(`/tasks/${taskId}/comments`).then(r => r.data),
    create: (taskId: number, body: string) =>
      apiClient.post<{ data: TaskComment }>(`/tasks/${taskId}/comments`, { body }).then(r => r.data),
    delete: (taskId: number, commentId: number) =>
      apiClient.delete(`/tasks/${taskId}/comments/${commentId}`).then(r => r.data),
  },

  taskActivities: {
    list: (taskId: number) =>
      apiClient.get<{ data: TaskActivity[] }>(`/tasks/${taskId}/activities`).then(r => r.data),
  },

  taskAttachments: {
    list: (taskId: number) =>
      apiClient.get<{ data: TaskAttachment[] }>(`/tasks/${taskId}/attachments`).then(r => r.data),
    /** `onProgress` gets the fraction sent, 0 to 1, when the browser reports it. */
    upload: (taskId: number, file: File, onProgress?: (fraction: number) => void) => {
      const formData = new FormData()
      formData.append('file', file)
      return apiClient.post<{ data: TaskAttachment }>(`/tasks/${taskId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (event) => { if (event.total) onProgress(event.loaded / event.total) }
          : undefined,
      }).then(r => r.data)
    },
    delete: (taskId: number, attachmentId: number) =>
      apiClient.delete(`/tasks/${taskId}/attachments/${attachmentId}`).then(r => r.data),
  },

  taskDeliverables: {
    list: (taskId: number) =>
      apiClient.get<{ data: TaskDeliverable[] }>(`/tasks/${taskId}/deliverables`).then(r => r.data),
    create: (taskId: number, data: { title: string; description?: string }) =>
      apiClient.post<{ data: TaskDeliverable }>(`/tasks/${taskId}/deliverables`, data).then(r => r.data),
    update: (taskId: number, deliverableId: number, data: { title?: string; description?: string | null; status?: string }) =>
      apiClient.put<{ data: TaskDeliverable }>(`/tasks/${taskId}/deliverables/${deliverableId}`, data).then(r => r.data),
    delete: (taskId: number, deliverableId: number) =>
      apiClient.delete(`/tasks/${taskId}/deliverables/${deliverableId}`).then(r => r.data),
    toggle: (taskId: number, deliverableId: number) =>
      apiClient.post<{ data: TaskDeliverable }>(`/tasks/${taskId}/deliverables/${deliverableId}/toggle`).then(r => r.data),
    reorder: (taskId: number, deliverables: { id: number; position: number }[]) =>
      apiClient.post(`/tasks/${taskId}/deliverables/reorder`, { deliverables }).then(r => r.data),
  },

  // Related and duplicate links; blocking links stay in taskDependencies.
  taskRelations: {
    list: (taskId: number) =>
      apiClient.get<{ data: TaskRelation[] }>(`/tasks/${taskId}/relations`).then(r => r.data),
    // `related_task` takes a key (FOR-12) or an id; answers the full list.
    add: (taskId: number, type: "related" | "duplicate_of" | "duplicated_by", relatedTask: string | number) =>
      apiClient.post<{ data: TaskRelation[] }>(`/tasks/${taskId}/relations`, { type, related_task: String(relatedTask) }).then(r => r.data),
    remove: (taskId: number, relationId: number) =>
      apiClient.delete(`/tasks/${taskId}/relations/${relationId}`),
  },

  // Boards the caller can see, General first (docs/boards-api.md in the API repo).
  boards: {
    // Archived boards are left out unless asked for: "only" lists them alone, "all" both (FOR-349).
    list: (archived?: "only" | "all") =>
      apiClient.get<{ data: Board[] }>("/boards", archived ? { params: { archived: archived === "only" ? 1 : "all" } } : undefined).then(r => r.data),
    // One board with its members (and their roles) and labels.
    get: (key: string) =>
      apiClient.get<{ data: Board }>(`/boards/${encodeURIComponent(key)}`).then(r => r.data),
    // Admin. A members-only board makes its creator the owner.
    create: (data: { key: string; name: string; color?: string | null; visibility?: BoardVisibility; member_ids?: number[] }) =>
      apiClient.post<{ data: Board }>("/boards", data).then(r => r.data),
    // Managers rename; only owners change visibility.
    // Managers and owners archive or unarchive (never General); an archived board refuses other changes.
    update: (key: string, data: { name?: string; color?: string | null; logo_url?: string | null; visibility?: BoardVisibility; archived?: boolean }) =>
      apiClient.put<{ data: Board }>(`/boards/${encodeURIComponent(key)}`, data).then(r => r.data),
    // Owners only. Moves it to the recycle bin; refused (422) while it still has tasks.
    remove: (key: string) =>
      apiClient.delete<{ message: string }>(`/boards/${encodeURIComponent(key)}`),
    // Replaces the member list; roles at or below the caller's own only (422 otherwise).
    syncMembers: (key: string, members: { user_id: number; role: BoardRole }[]) =>
      apiClient.put<{ data: BoardMember[] }>(`/boards/${encodeURIComponent(key)}/members`, { members }).then(r => r.data),
  },

  taskDependencies: {
    list: (taskId: number) =>
      apiClient.get<{ data: { blocked_by: Task[]; blocks: Task[] } }>(`/tasks/${taskId}/dependencies`).then(r => r.data),
    add: (taskId: number, blockingTaskId: number) =>
      apiClient.post(`/tasks/${taskId}/dependencies`, { blocking_task_id: blockingTaskId }).then(r => r.data),
    remove: (taskId: number, blockingTaskId: number) =>
      apiClient.delete(`/tasks/${taskId}/dependencies/${blockingTaskId}`).then(r => r.data),
  },

  projectCollaborators: {
    list: (projectId: number) =>
      apiClient.get<{ data: import("@/types/project.types").ProjectPerson[] }>(`/projects/${projectId}/collaborators`).then(r => r.data),
    sync: (projectId: number, userIds: number[]) =>
      apiClient.post<{ data: import("@/types/project.types").ProjectPerson[] }>(`/projects/${projectId}/collaborators`, { user_ids: userIds }).then(r => r.data),
  },

  taskCollaborators: {
    list: (taskId: number) =>
      apiClient.get<{ data: { id: number; name: string; email: string; avatar: string | null }[] }>(`/tasks/${taskId}/collaborators`).then(r => r.data),
    sync: (taskId: number, userIds: number[]) =>
      apiClient.post<{ data: { id: number; name: string; email: string; avatar: string | null }[] }>(`/tasks/${taskId}/collaborators`, { user_ids: userIds }).then(r => r.data),
  },

  // Tags — shared tagging system for tasks and topics
  tags: {
    // `board` scopes to one board's task labels; without it the API answers
    // General's labels plus site tags, as it always has.
    list: (params?: { site_id?: number; board?: string }) =>
      apiClient.get<{ data: Tag[] }>("/tags", { params }).then(r => r.data),
    create: (data: { name: string; color?: string; site_id?: number | null; board?: string }) =>
      apiClient.post<{ data: Tag }>("/tags", data).then(r => r.data),
    update: (id: number, data: { name?: string; color?: string }) =>
      apiClient.put<{ data: Tag }>(`/tags/${id}`, data).then(r => r.data),
    delete: (id: number) =>
      apiClient.delete(`/tags/${id}`).then(r => r.data),
    syncTaskTags: (taskId: number, tagIds: number[]) =>
      apiClient.post<{ data: Tag[] }>(`/tasks/${taskId}/tags`, { tag_ids: tagIds }).then(r => r.data),
    syncTopicTags: (topicId: number, tagIds: number[]) =>
      apiClient.post<{ data: Tag[] }>(`/topics/${topicId}/tags`, { tag_ids: tagIds }).then(r => r.data),
    syncCtaTags: (ctaId: number, tagIds: number[]) =>
      apiClient.post<{ data: Tag[] }>(`/ctas/${ctaId}/tags`, { tag_ids: tagIds }).then(r => r.data),
  },

  // Personal API tokens (admins and users granted API access): scoped bearer
  // tokens for scripts, MCP servers and agent sessions.
  apiTokens: {
    list: () => apiClient.get<{ data: ApiToken[] }>("/api-tokens").then(r => r.data),
    // The grantable domains, scopes and presets for the current user.
    scopes: () => apiClient.get<{ data: ApiTokenScopes }>("/api-tokens/scopes").then(r => r.data),
    // Body is { name, preset } or { name, scopes }; the plaintext token comes back once.
    create: (payload: CreateApiTokenPayload) =>
      apiClient.post<{ data: MintedApiToken }>("/api-tokens", payload).then(r => r.data),
    revoke: (id: number) => apiClient.delete<{ data: ApiToken[] }>(`/api-tokens/${id}`).then(r => r.data),
  },

  // Users list — for task assignment dropdowns
  users: {
    list: () =>
      apiClient.get<{ data: Array<{ id: number; name: string; email: string; avatar: string | null }> }>("/users").then(r => r.data),
    // Who owns what (Admin → Roles). Readable by everyone; edited by admins.
    responsibilities: () =>
      apiClient.get<{ data: TeamResponsibilities[] }>("/users/responsibilities").then(r => r.data),
    updateResponsibilities: (id: number, responsibilities: string[]) =>
      apiClient.put<{ data: TeamResponsibilities }>(`/admin/users/${id}/responsibilities`, { responsibilities }).then(r => r.data),
  },

  // CallRail weekly reports
  callrail: {
    listReports: () =>
      apiClient.get<{ data: any[] }>("/callrail/reports"),

    getReport: (id: number) =>
      apiClient.get<{ report: any; previous_metrics: any }>(`/callrail/reports/${id}`),

    generateReport: (params?: { start_date?: string; end_date?: string; comparison_start?: string; comparison_end?: string; send_email?: boolean }) =>
      apiClient.post<{ success: boolean; message: string }>("/callrail/reports/generate", params ?? {}),
  },

  // Practice Area Rollout — per-site dashboard tracking a staged rebuild of a
  // site's practice areas and whether each rollout actually moved search
  // performance. Gated server-side by the `practice_rollout` site_integration,
  // so every endpoint 404s for sites without it.
  // The dashboard's client stats chart — stored GSC dailies, junk-filtered
  // lead counts and publish markers in one payload.
  clientStats: {
    timeseries: (days = 90) =>
      apiClient.get<ClientStatsTimeseries>(`/client-stats/timeseries?days=${days}`),
  },

  practiceRollout: {
    getBoard: (days = 30) =>
      apiClient.get<PracticeRolloutBoard>(`/practice-rollout?days=${days}`),

    getTimeseries: (id: number, days = 90) =>
      apiClient.get<PracticeRolloutTimeseries>(`/practice-rollout/${id}/timeseries?days=${days}`),

    getCohort: (days = 90) =>
      apiClient.get<PracticeRolloutCohort>(`/practice-rollout/cohort?days=${days}`),

    getVsControl: () => apiClient.get<PracticeRolloutVsControl>("/practice-rollout/vs-control"),

    getPages: (id: number, weeks = 8) =>
      apiClient.get<PracticeRolloutSubPages>(`/practice-rollout/${id}/pages?weeks=${weeks}`),
  },

  // Spanish translation drift — English pages against their hand-authored
  // Spanish twins. Gated by the same practice_rollout site integration as the
  // rollout board, so it 404s for every other client.
  esTranslations: {
    get: () => apiClient.get<EsTranslationBoard>("/es-translations"),

    // Calls the client's WordPress site, so it is slower than a read and
    // locked server-side against concurrent runs.
    refresh: () =>
      apiClient.post<
        EsTranslationBoard & {
          refreshed: boolean
          checked?: number
          failed?: number
          error?: string | null
        }
      >("/es-translations/refresh", {}),

    logEvent: (id: number, payload: { event_type: string; occurred_on: string; notes?: string }) =>
      apiClient.post<{ event: PracticeRolloutEvent; area_status: string }>(
        `/practice-rollout/${id}/events`,
        payload
      ),

    updateArea: (id: number, payload: { status?: string; notes?: string | null; page_count?: number }) =>
      apiClient.patch<{ area: { id: number; status: string; notes: string | null; page_count: number } }>(
        `/practice-rollout/${id}`,
        payload
      ),
  },

  // Claim Settlements — class action settlement pages, enabled per site by the
  // `claim_settlements` site_integration. Site-scoped by X-Active-License-Id
  // like everything else, so no site id travels in the path.
  //
  // approve is the hard human publish gate and the only call here that reaches
  // the public site. It refuses with 422 + { blockers } when the record is not
  // shippable; read that body with getApiErrorBody<SettlementApproveBlockers>.
  claimSettlements: {
    // status filters on pipeline_status. "Needs data" spans two stages, so the
    // list page sends them comma-separated.
    list: (params?: SettlementListParams) =>
      apiClient.get<SettlementListResponse>("/claim-settlements", { params }),

    stats: () => apiClient.get<SettlementStats>("/claim-settlements/stats"),

    get: (id: number) => apiClient.get<SettlementDetail>(`/claim-settlements/${id}`),

    // Always lands as an unverified draft. A 409 carries the `existing` record;
    // read it with getApiErrorBody<SettlementDuplicateBody>.
    create: (body: SettlementCreatePayload) =>
      apiClient.post<SettlementDetail>("/claim-settlements", body),

    // Starts reading a page in the background and answers with an id to poll.
    // Saves nothing. A read does not fit in one request: the web tier cuts at
    // 30 seconds and a fetch plus a model call can take longer.
    extract: (url: string) =>
      apiClient.post<SettlementExtractState>("/claim-settlements/extract", { url }),

    extractStatus: (id: string) =>
      apiClient.get<SettlementExtractState>(`/claim-settlements/extract/${id}`),

    update: (id: number, body: SettlementUpdatePayload) =>
      apiClient.put<SettlementDetail>(`/claim-settlements/${id}`, body),

    // PUBLISHES to the live client site. A 200 can still carry warnings: the
    // page is live either way, so those are notes on a published item.
    approve: (id: number, note?: string) =>
      apiClient.post<SettlementApproveResponse>(`/claim-settlements/${id}/approve`, { note }),

    unpublish: (id: number, note?: string) =>
      apiClient.post<SettlementUnpublishResponse>(`/claim-settlements/${id}/unpublish`, { note }),

    // What needs a person: missing catalog cases, duplicates, drafts, payouts,
    // deadlines, reviews, images and both pipelines' health. Read-only.
    attention: (refreshCatalog = false) =>
      apiClient.get<SettlementAttention>("/claim-settlements/attention", {
        params: refreshCatalog ? { refresh_catalog: 1 } : undefined,
      }),

    catalog: (params?: { search?: string; unlinked?: boolean; refresh?: boolean }) =>
      apiClient.get<SettlementCatalogResponse>("/claim-settlements/catalog", {
        params: {
          search: params?.search || undefined,
          unlinked: params?.unlinked ? 1 : undefined,
          refresh: params?.refresh ? 1 : undefined,
        },
      }),

    // Creates an unverified DRAFT for a catalog case; never publishes. 409 when
    // a page already covers the case.
    draftFromCatalog: (catalogId: string) =>
      apiClient.post<SettlementAttentionRow>("/claim-settlements/catalog/draft", { catalog_id: catalogId }),

    // 409 { code: claim_settlements_linked_elsewhere, data.post_id } when
    // another page holds the case.
    catalogLink: (id: number, mode: "auto" | "pinned" | "none", catalogId?: string) =>
      apiClient.post<SettlementAttentionRow>(`/claim-settlements/${id}/catalog-link`, {
        mode,
        catalog_id: catalogId,
      }),

    image: (id: number, action: "url" | "manual" | "sync", url?: string) =>
      apiClient.post<SettlementAttentionRow>(`/claim-settlements/${id}/image`, { action, url }),

    notDuplicate: (id: number) =>
      apiClient.post<SettlementAttentionRow>(`/claim-settlements/${id}/not-duplicate`),

    applySuggestedBasis: (ids?: number[]) =>
      apiClient.post<{ applied: Array<{ id: number; payout_basis: string }> }>(
        "/claim-settlements/attention/apply-suggested-basis",
        { ids },
      ),
  },

  // Sites list — lightweight endpoint for dropdowns
  sitesList: {
    list: () =>
      apiClient.get<{ data: Array<{ id: number; domain: string }> }>("/sites-list").then(r => r.data),
  },

  gmb: {
    listPosts: () =>
      apiClient.get<{ posts: GmbPost[] }>("/gmb/posts"),

    createPost: (data: { body: string; image_url?: string; cta_type?: string; cta_url?: string }) =>
      apiClient.post<{ post: GmbPost }>("/gmb/posts", data),

    updatePost: (id: number, data: { body: string; image_url?: string; cta_type?: string; cta_url?: string }) =>
      apiClient.put<{ post: GmbPost }>(`/gmb/posts/${id}`, data),

    deletePost: (id: number) =>
      apiClient.delete<{ success: boolean }>(`/gmb/posts/${id}`),
  },

  // Broken Link Checker
  brokenLinks: {
    stats: () =>
      apiClient.get<{
        data: {
          total_links: number
          broken_links: number
          redirect_links: number
          warning_links: number
          // Recommended replacements waiting on a human decision
          needs_review: number
          last_scan: {
            id: number
            status: string
            total_links: number
            broken_count: number
            urls_to_check: number
            checked_count: number
            started_at: string
            completed_at: string | null
            error_message: string | null
          } | null
        }
      }>("/broken-links/stats"),

    list: (params?: { filter?: string; search?: string; page?: number; per_page?: number }) =>
      apiClient.get<{
        data: BrokenLinkUrl[]
        current_page: number
        per_page: number
        total: number
        last_page: number
      }>("/broken-links", { params }),

    scan: () =>
      apiClient.post<{ success: boolean; message: string }>("/broken-links/scan"),

    cancelScan: () =>
      apiClient.post<{ success: boolean; message: string }>("/broken-links/scan/cancel"),

    recheck: (id: number) =>
      apiClient.post<{ success: boolean; message: string }>(`/broken-links/${id}/recheck`),

    dismiss: (id: number) =>
      apiClient.post<{ success: boolean }>(`/broken-links/${id}/dismiss`),

    undismiss: (id: number) =>
      apiClient.post<{ success: boolean }>(`/broken-links/${id}/undismiss`),

    // Rewrites the URL in every post that contains it
    edit: (id: number, new_url: string) =>
      apiClient.post<{ success: boolean; message: string; updated_posts?: number }>(`/broken-links/${id}/edit`, { new_url }),

    // Removes the <a> tag everywhere, keeping the anchor text
    unlink: (id: number) =>
      apiClient.post<{ success: boolean; message: string; updated_posts?: number }>(`/broken-links/${id}/unlink`),

    // Replaces the URL with its redirect target everywhere
    fixRedirect: (id: number) =>
      apiClient.post<{ success: boolean; message: string; updated_posts?: number }>(`/broken-links/${id}/fix-redirect`),

    // Replacement review queue. Recommendations are only ever proposals —
    // approve is the single call that rewrites content.
    suggestReplacements: () =>
      apiClient.post<{ success: boolean; queued: number; message: string }>("/broken-links/suggest-replacements"),

    // Swap in the reviewer's own replacement URL; re-enters the queue as pending
    setReplacement: (id: number, url: string) =>
      apiClient.post<{ success: boolean; data: BrokenLinkUrl }>(`/broken-links/${id}/replacement`, { url }),

    // Re-check that the recommended URL resolves
    verifyReplacement: (id: number) =>
      apiClient.post<{ success: boolean; status_code: number | null; message: string; data: BrokenLinkUrl }>(
        `/broken-links/${id}/replacement/verify`
      ),

    // Approve AND apply — rewrites the link in every post that contains it
    approveReplacement: (id: number) =>
      apiClient.post<{ success: boolean; message: string; updated_posts?: number }>(
        `/broken-links/${id}/replacement/approve`
      ),

    // Drop the recommendation; the link stays broken
    rejectReplacement: (id: number) =>
      apiClient.post<{ success: boolean; data: BrokenLinkUrl }>(`/broken-links/${id}/replacement/reject`),
  },

  // Backlinks - Link building campaign tracker
  // Status flow: prospect → under_review → approved → pending_payment → in_progress → live
  // Also: any → rejected, rejected/lost → prospect (re-open)
  backlinks: {
    list: (params?: {
      status?: string
      backlink_type?: string
      assigned_to?: number
      search?: string
      page?: number
      per_page?: number
    }) =>
      apiClient.get<{
        data: Backlink[]
        current_page: number
        per_page: number
        total: number
        last_page: number
      }>("/backlinks", { params }),

    get: (id: number) =>
      apiClient.get<{ data: Backlink }>(`/backlinks/${id}`),

    create: (data: BacklinkInput) =>
      apiClient.post<{ data: Backlink }>("/backlinks", data),

    update: (id: number, data: Partial<BacklinkInput>) =>
      apiClient.put<{ data: Backlink }>(`/backlinks/${id}`, data),

    delete: (id: number) =>
      apiClient.delete<{ success: boolean }>(`/backlinks/${id}`),

    updateStatus: (id: number, status: string) =>
      apiClient.patch<{ data: Backlink }>(`/backlinks/${id}/status`, { status }),

    stats: () =>
      apiClient.get<{
        data: {
          total: number
          live: number
          total_spend: number
          average_da_dr: number
          by_type: Record<string, number>
          by_status: Record<string, number>
        }
      }>("/backlinks/stats"),
  },

  // Source Verification (FOR-127 / FOR-131) — verification-first content workflow
  // Two-section checklist: Section A (Claude-verified claims) + Section B (human checks).
  // Articles can only be approved when can_approve === true (no blockers from missing
  // sources, off-allowlist domains, contradicted claims, or unchecked Section B items).
  verification: {
    // Convenience site-settings fetch — calls the existing GET /api/settings
    // endpoint (uses active site context implicitly). Returns approved_domains
    // and section_b_template alongside the rest of the site settings.
    // Used by the Source Verification settings tab.
    getSettings: () =>
      apiClient.get<{
        settings: {
          approved_domains: ApprovedDomains
          section_b_template: { key: string; label: string }[]
          // Fact checker: legal claims against official sources (admin-only, default off)
          legal_fact_check_enabled?: boolean
          legal_jurisdiction?: string | null
        }
      }>("/settings"),

    getChecklist: (contentId: number) =>
      apiClient.get<ChecklistResponse>(`/content/${contentId}/checklist`),

    // Re-runs Section A generation against current article body. Section B (human
    // checks) is preserved — only Claude-verified items are regenerated.
    regenerateChecklist: (contentId: number) =>
      apiClient.post<ChecklistResponse>(`/content/${contentId}/checklist/regenerate`),

    // Toggle a single item's checked state (or update the optional note).
    // Server returns the full updated checklist so the UI can refresh blocker state.
    updateChecklistItem: (
      contentId: number,
      key: string,
      body: { checked: boolean; note?: string }
    ) =>
      apiClient.patch<ChecklistResponse>(
        `/content/${contentId}/checklist/items/${key}`,
        body
      ),

    // Site-scoped settings — the per-site approved-source allowlist used by the
    // domain_allowed badge in Section A. Categorized so legal/medical can have
    // different default presets (e.g. Porter Law).
    updateApprovedDomains: (body: ApprovedDomains) =>
      apiClient.patch<{ approved_domains: ApprovedDomains }>(
        "/settings/approved-domains",
        body
      ),

    // Site-scoped settings — the per-site Section B template (human-check items
    // injected into every new article's checklist). Keys must be unique slug-cased
    // strings; backend stores them as the canonical Section B template for the site.
    updateSectionBTemplate: (
      body: { items: { key: string; label: string }[] }
    ) =>
      apiClient.patch<{ section_b_template: { key: string; label: string }[] }>(
        "/settings/section-b-template",
        body
      ),
  },

  // Content Clusters (FOR-127 Phase 2 / FOR-132) — group pillar+spoke topics
  // around a shared protocol and reference asset library. Each cluster:
  //   - has at most one parent pillar topic (the hub article)
  //   - holds many spoke topics (the leaves)
  //   - carries a list of reference assets (protocol docs, master sheets, etc.)
  // All endpoints are site-scoped via the X-Active-License-Id header — no
  // siteId in the URL path.
  // Site Protocol — singleton per site. PUT accepts a partial body so each
  // section card on the settings tab can save independently. The GET response
  // also carries lessons_learned (read-only) for the cluster dashboard timeline.
  siteProtocol: {
    get: () => apiClient.get<{ protocol: SiteProtocol }>("/site-protocol"),

    update: (body: Partial<Pick<SiteProtocol, 'locked_fields' | 'internal_link_conventions' | 'legal_exceptions_md' | 'house_style_md' | 'reference_md'>>) =>
      apiClient.put<{ protocol: SiteProtocol }>("/site-protocol", body),
  },

  // Firm Profile — Case Results, Testimonials, Competitor References (FOR-139 /
  // Phase 1 of FOR-138). All site-scoped via X-Active-License-Id. The list
  // endpoints filter to is_active=true by default; pass include_inactive=true
  // to see archived rows.
  caseResults: {
    list: (opts?: { include_inactive?: boolean }) =>
      apiClient.get<{ case_results: CaseResult[] }>("/case-results", {
        params: opts?.include_inactive ? { include_inactive: 1 } : undefined,
      }),

    create: (body: Partial<Omit<CaseResult, 'id' | 'site_id' | 'created_at' | 'updated_at' | 'display_amount'>>) =>
      apiClient.post<{ case_result: CaseResult }>("/case-results", body),

    get: (id: number) =>
      apiClient.get<{ case_result: CaseResult }>(`/case-results/${id}`),

    update: (id: number, body: Partial<Omit<CaseResult, 'id' | 'site_id' | 'created_at' | 'updated_at' | 'display_amount'>>) =>
      apiClient.patch<{ case_result: CaseResult }>(`/case-results/${id}`, body),

    delete: (id: number) => apiClient.delete(`/case-results/${id}`),

    // Bulk display-order update from drag-to-reorder. Server skips ids that
    // don't belong to the active site so partial batches don't 404 mid-drag.
    reorder: (order: { id: number; display_order: number }[]) =>
      apiClient.post("/case-results/reorder", { order }),
  },

  testimonials: {
    list: (opts?: { include_inactive?: boolean }) =>
      apiClient.get<{ testimonials: Testimonial[] }>("/testimonials", {
        params: opts?.include_inactive ? { include_inactive: 1 } : undefined,
      }),

    create: (body: Partial<Omit<Testimonial, 'id' | 'site_id' | 'created_at' | 'updated_at'>>) =>
      apiClient.post<{ testimonial: Testimonial }>("/testimonials", body),

    get: (id: number) =>
      apiClient.get<{ testimonial: Testimonial }>(`/testimonials/${id}`),

    update: (id: number, body: Partial<Omit<Testimonial, 'id' | 'site_id' | 'created_at' | 'updated_at'>>) =>
      apiClient.patch<{ testimonial: Testimonial }>(`/testimonials/${id}`, body),

    delete: (id: number) => apiClient.delete(`/testimonials/${id}`),

    reorder: (order: { id: number; display_order: number }[]) =>
      apiClient.post("/testimonials/reorder", { order }),
  },

  competitorReferences: {
    list: (opts?: { include_inactive?: boolean }) =>
      apiClient.get<{ competitor_references: CompetitorReference[] }>("/competitor-references", {
        params: opts?.include_inactive ? { include_inactive: 1 } : undefined,
      }),

    create: (body: Partial<Omit<CompetitorReference, 'id' | 'site_id' | 'created_at' | 'updated_at'>>) =>
      apiClient.post<{ competitor_reference: CompetitorReference }>("/competitor-references", body),

    get: (id: number) =>
      apiClient.get<{ competitor_reference: CompetitorReference }>(`/competitor-references/${id}`),

    update: (id: number, body: Partial<Omit<CompetitorReference, 'id' | 'site_id' | 'created_at' | 'updated_at'>>) =>
      apiClient.patch<{ competitor_reference: CompetitorReference }>(`/competitor-references/${id}`, body),

    delete: (id: number) => apiClient.delete(`/competitor-references/${id}`),
  },

  // FOR-144 / Phase 3.5b of FOR-138 — site-curated Page Examples library.
  // Auto-injects into every is_page_generator workflow run. fetchFromUrl is
  // a UI helper that scrapes via Jina and returns content for the textarea.
  pageExamples: {
    list: (opts?: { include_inactive?: boolean }) =>
      apiClient.get<{ page_examples: PageExample[] }>("/page-examples", {
        params: opts?.include_inactive ? { include_inactive: 1 } : undefined,
      }),

    create: (body: Partial<Omit<PageExample, 'id' | 'site_id' | 'created_at' | 'updated_at' | 'word_count'>>) =>
      apiClient.post<{ page_example: PageExample }>("/page-examples", body),

    get: (id: number) =>
      apiClient.get<{ page_example: PageExample }>(`/page-examples/${id}`),

    update: (id: number, body: Partial<Omit<PageExample, 'id' | 'site_id' | 'created_at' | 'updated_at' | 'word_count'>>) =>
      apiClient.patch<{ page_example: PageExample }>(`/page-examples/${id}`, body),

    delete: (id: number) => apiClient.delete(`/page-examples/${id}`),

    fetchFromUrl: (url: string) =>
      apiClient.post<{ success: boolean; url: string; content: string; word_count: number }>(
        "/page-examples/fetch-from-url",
        { url },
      ),
  },

  // Page Brief (FOR-127 Phase 3 / FOR-133) — per-topic editorial brief that
  // gates workflow runs when topic.requires_page_brief is set. The UI surfaces
  // this on a standalone /topics/:id/brief route and inline as a Run-Workflow
  // gate dialog. All endpoints are site-scoped via X-Active-License-Id.
  pageBrief: {
    get: (topicId: number) =>
      apiClient.get<{ brief: PageBrief }>(`/topics/${topicId}/page-brief`),

    update: (topicId: number, body: Partial<PageBrief>) =>
      apiClient.put<{ brief: PageBrief }>(`/topics/${topicId}/page-brief`, body),

    // Marks the brief approved by the current user. Backend enforces that all
    // required fields are present before flipping status to 'approved'.
    approve: (topicId: number) =>
      apiClient.post<{ brief: PageBrief }>(`/topics/${topicId}/page-brief/approve`),

    // Asks Claude to draft a starting brief from the topic + cluster context.
    // Populates fields that are still empty; never overwrites filled values.
    generateSuggestion: (topicId: number) =>
      apiClient.post<{ brief: PageBrief }>(`/topics/${topicId}/page-brief/generate-suggestion`),

    // FOR-140 / Phase 2 of FOR-138 — runs Jina against any reference URLs that
    // aren't already scraped or manually overridden. Idempotent. The returned
    // brief carries each row's updated scrape_status + scraped_content.
    fetchReferences: (topicId: number) =>
      apiClient.post<{ brief: PageBrief }>(`/topics/${topicId}/page-brief/fetch-references`),
  },

  // Topic bulk-import for the page generator (FOR-141 / Phase 3 of FOR-138).
  // Distinct from generic topic import because page rows carry richer columns
  // (target URL, primary keyword, optional workflow assignment).
  topicsBulkImport: {
    pages: (rows: BulkImportPageRow[], workflowId?: number) =>
      apiClient.post<BulkImportPagesResponse>("/topics/bulk-import-pages", {
        rows,
        workflow_id: workflowId,
      }),
  },

  // Workflow context preview (FOR-141 / Phase 3 of FOR-138). Same code path as
  // a real run, so what comes back is byte-for-byte what Claude would see.
  // Pass overrides to stage Context Preview UI changes without saving them.
  workflowContextPreview: {
    preview: (
      workflowId: number,
      body: {
        topic_id?: number | null
        content_item_id?: number | null
        overrides?: PageBriefContextOverrides | null
      },
    ) =>
      apiClient.post<ContextPreviewResponse>(
        `/workflows/${workflowId}/preview-context`,
        body,
      ),
  },

  // Source citations (FOR-127 Phase 3 / FOR-133) — citation records produced
  // by the verification pipeline. Cluster dashboard reads byCluster (paginated
  // list + aggregated top_sources). The byContent endpoint is for surfacing
  // citations inside a single article.
  citations: {
    byCluster: (clusterId: number, params?: { page?: number; per_page?: number }) =>
      apiClient.get<{
        citations: SourceCitation[]
        top_sources: TopSourceStat[]
        pagination: {
          current_page: number
          last_page: number
          per_page: number
          total: number
          from: number
          to: number
        }
      }>(`/clusters/${clusterId}/citations`, { params }),

    byContent: (contentId: number) =>
      apiClient.get<{ citations: SourceCitation[] }>(`/content/${contentId}/citations`),
  },

  // Phase 3 .docx export — extends api.content (kept here as a sibling object
  // to keep Phase 3 additions contiguous; the existing api.content lives above).
  // If the content has never been exported, the backend generates fresh.
  contentExport: {
    downloadDocx: async (contentId: number) => {
      // Use blob responseType so binary docx bytes survive the wire intact.
      // Server may return either application/vnd.openxmlformats... (docx) or
      // application/msword (older fallback) — we don't switch on the mime, just
      // pass it through to the browser via createObjectURL.
      const blob = await apiClient.get<Blob>(`/content/${contentId}/docx`, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `content-${contentId}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
  },

  // Invoices — biweekly team invoicing (global, not site-scoped).
  // apiClient already returns the response body, so each method hands back the
  // JSON exactly as the API sends it (the paginator for list, {data} for the rest).
  invoices: {
    list: (params?: Record<string, any>) =>
      apiClient.get<{ data: Invoice[], current_page: number, last_page: number, total: number }>("/invoices", { params }),
    get: (id: number) =>
      apiClient.get<{ data: Invoice }>(`/invoices/${id}`),
    create: (data: Partial<Invoice>) =>
      apiClient.post<{ data: Invoice }>("/invoices", data),
    update: (id: number, data: Partial<Invoice>) =>
      apiClient.put<{ data: Invoice }>(`/invoices/${id}`, data),
    delete: (id: number) =>
      apiClient.delete(`/invoices/${id}`),
    markPaid: (id: number) =>
      apiClient.post<{ data: Invoice }>(`/invoices/${id}/mark-paid`),
    markUnpaid: (id: number) =>
      apiClient.post<{ data: Invoice }>(`/invoices/${id}/mark-unpaid`),
    bulkMarkPaid: (invoiceIds: number[]) =>
      apiClient.post<{ message: string }>("/invoices/bulk-mark-paid", { invoice_ids: invoiceIds }),
    aggregate: (params?: { start_date?: string; end_date?: string }) =>
      apiClient.get<{ data: { invoices: Invoice[]; email_body: string; total: number } }>("/invoices/aggregate", { params }),
    sendSummary: (data: { invoice_ids: number[]; recipients: string[] }) =>
      apiClient.post<{ message: string }>("/invoices/send-summary", data),
    // PDF download — opens in browser via Sanctum cookie auth
    getPdfUrl: (id: number) =>
      `${API_URL}/invoices/${id}/pdf`,
    getParticipants: () =>
      apiClient.get<{ data: InvoiceParticipant[] }>("/invoices/participants"),
    toggleParticipant: (userId: number) =>
      apiClient.post<{ data: { id: number; name: string; invoice_participant: boolean } }>(`/invoices/participants/${userId}/toggle`),
  },

  // Search Console — sitemap management, URL inspection, indexing requests, performance data
  searchConsole: {
    // Batched inspection: fetches one batch of pages starting at offset.
    // Call repeatedly with next_offset until has_more === false to scan all pages.
    getPages: (offset = 0) =>
      apiClient.get<{
        pages: SearchConsolePage[];
        total_in_sitemap: number;
        inspected_count: number;
        offset: number;
        has_more: boolean;
        next_offset: number;
        sitemap_errors: Array<{ sitemap: string; error: string }>;
        gsc_property: string;
      }>("/search-console/pages", { params: { offset } }),

    // Request Google to crawl/index a URL via the Indexing API
    requestIndexing: (url: string) =>
      apiClient.post<{ success: boolean; message: string; url: string; indexing_requested_at: string }>(
        "/search-console/request-indexing",
        { url }
      ),

    // GSC performance data (clicks, impressions, position) grouped by page URL
    getPerformance: (days?: number) =>
      apiClient.get<{
        pages: SearchConsolePerformancePage[];
        date_range: { start: string; end: string; days: number };
        total_rows: number;
        gsc_property: string;
      }>("/search-console/performance", { params: { days } }),

    // Get currently saved sitemap URLs
    getSitemaps: () =>
      apiClient.get<{ sitemap_urls: string[] }>("/search-console/sitemaps"),

    // Save sitemap URLs for the site
    saveSitemaps: (sitemap_urls: string[]) =>
      apiClient.post<{ success: boolean; sitemap_urls: string[] }>(
        "/search-console/sitemaps",
        { sitemap_urls }
      ),

    // Load the last completed scan from the database (survives page refresh + works cross-device)
    getScanCache: () =>
      apiClient.get<{ pages: SearchConsolePage[]; cached_at: string | null }>(
        "/search-console/scan-cache"
      ),

    // Persist completed scan results to the database
    saveScanCache: (pages: SearchConsolePage[]) =>
      apiClient.post<{ success: boolean }>("/search-console/scan-cache", { pages }),
  },

  // Client Health matrix (Agency Hub Phase 1). Admin-only cross-client grid.
  clientHealth: {
    // period_type + date select the reporting window; the backend derives the
    // week/month bounds from `date` (any date inside the period).
    getMatrix: (params: { period_type: "week" | "month"; date: string }) =>
      apiClient.get<ClientHealthMatrix>("/admin/client-health", { params }),

    // Toggle the manual "needs attention" flag for a client. Backend returns
    // the updated attention object bare (no envelope) — same shape the matrix
    // embeds per client.
    setAttention: (siteId: number, body: { flagged: boolean; note: string | null }) =>
      apiClient.post<ClientAttention>(`/admin/sites/${siteId}/attention`, body),
  },

  // Infrastructure status for every app GLUSKA runs. Reads stored probe
  // results rather than probing live, so opening the page cannot hang on the
  // very endpoint it is reporting as broken.
  infrastructure: {
    get: () => apiClient.get<{ data: InfrastructureStatus }>("/admin/infrastructure"),
  },

  // Standing delivery agents. Admin-only and global rather than per-license:
  // an agent may belong to no client at all. The runner's own endpoints
  // (/agent-runner/*, X-Agent-Key) are machine-authenticated and are never
  // called from this app.
  agents: {
    // `schedule: null` deletes the agent's single schedule row; an object
    // upserts it. Employee fields (kind, avatar, tools, …) are all optional so
    // a coder agent's edit dialog can keep sending only what it always did.
    list: (params?: { status?: AgentStatus; site_id?: number }) =>
      apiClient.get<{ data: Agent[] }>("/admin/agents", { params }).then(r => r.data),
    get: (id: number) =>
      apiClient.get<{ data: Agent }>(`/admin/agents/${id}`).then(r => r.data),
    create: (data: AgentWritePayload) =>
      apiClient.post<{ data: Agent }>("/admin/agents", data).then(r => r.data),
    update: (id: number, data: Partial<AgentWritePayload>) =>
      apiClient.put<{ data: Agent }>(`/admin/agents/${id}`, data).then(r => r.data),
    delete: (id: number) =>
      apiClient.delete<{ data: { deleted: boolean } }>(`/admin/agents/${id}`),
    // Pausing stops new work being scheduled; runs already queued stay
    // claimable, so this never strands a runner mid-session.
    pause: (id: number) =>
      apiClient.post<{ data: Agent }>(`/admin/agents/${id}/pause`).then(r => r.data),
    activate: (id: number) =>
      apiClient.post<{ data: Agent }>(`/admin/agents/${id}/activate`).then(r => r.data),
    // Run now. A desk agent starts inside Forge straight away (201 + the run);
    // a coder agent answers 422 because its work has to come from a task.
    run: (id: number, data: { brief?: string; site_id?: number }) =>
      apiClient.post<{ data: AgentRun }>(`/admin/agents/${id}/run`, data).then(r => r.data),
    // Per-site notebooks. Tolerates a bare array as well as the {data} envelope
    // — the endpoint is new and the envelope is the only thing not pinned.
    memories: (id: number) =>
      apiClient
        .get<{ data: AgentMemory[] } | AgentMemory[]>(`/admin/agents/${id}/memory`)
        .then(r => (Array.isArray(r) ? r : r.data ?? [])),
    saveMemory: (id: number, siteId: number, notebook: string) =>
      apiClient
        .put<{ data?: AgentMemory }>(`/admin/agents/${id}/memory/${siteId}`, { notebook })
        .then(r => r.data ?? (r as unknown as AgentMemory)),
  },

  // The read-only tools a desk agent can be granted. Names are the grant
  // strings stored on agents.tools.
  agentTools: {
    list: () =>
      apiClient
        .get<{ data: AgentToolCatalogueItem[] } | AgentToolCatalogueItem[]>("/admin/agent-tools")
        .then(r => (Array.isArray(r) ? r : r.data ?? [])),
  },

  // Runs — one execution each, from delegation to human decision.
  agentRuns: {
    // Returns Laravel's paginator body unwrapped (no { data } envelope around
    // the meta), so the total is read off the response itself. `status` takes
    // a comma-separated list.
    list: (params?: { status?: string; agent_id?: number; site_id?: number; task_id?: number; per_page?: number; page?: number }) =>
      apiClient.get<AgentPaginated<AgentRun>>("/admin/agent-runs", { params }),
    // The transcript is paginated separately from the run: sessions run long
    // and the whole event stream is never the right response. Both halves come
    // back together, so this one keeps the envelope.
    get: (id: number, params?: { per_page?: number; page?: number }) =>
      apiClient.get<{ data: AgentRun; events: AgentPaginated<AgentRunEvent> }>(`/admin/agent-runs/${id}`, { params }),
    // Either point at an existing task or describe the work inline — the
    // inline form still creates a task, which becomes the run's brief.
    create: (data: {
      agent_id?: number | null
      task_id?: number | null
      title?: string | null
      description?: string | null
      site_id?: number | null
      /** "owner/name" — explicit target; overrides the agent's repo mapping. */
      repository?: string | null
      budget?: { max_turns?: number; max_minutes?: number } | null
    }) => apiClient.post<{ data: AgentRun }>("/admin/agent-runs", data).then(r => r.data),
    approve: (id: number, note?: string | null) =>
      apiClient.post<{ data: AgentRun }>(`/admin/agent-runs/${id}/approve`, { note }).then(r => r.data),
    // A note is required: a rejected run comes back to be re-run, and "no"
    // with no reason gives the agent nothing to do differently.
    reject: (id: number, note: string) =>
      apiClient.post<{ data: AgentRun }>(`/admin/agent-runs/${id}/reject`, { note }).then(r => r.data),
    // The middle path — the run re-queues and the agent revises the same work
    // guided by this note, so the note is the whole point and is required.
    requestChanges: (id: number, note: string) =>
      apiClient.post<{ data: AgentRun }>(`/admin/agent-runs/${id}/request-changes`, { note }).then(r => r.data),
    // Ask the agent a follow-up about this report. The API queues a child run
    // (run_kind 'reply') whose answer lands in the parent's `replies`; 422 when
    // the parent is not a desk run, the agent is paused, or three questions
    // are already waiting on it.
    ask: (id: number, question: string) =>
      apiClient.post<{ data: AgentRun }>(`/admin/agent-runs/${id}/ask`, { question }).then(r => r.data),
    // Turn proposed_tasks[index] into a real task. Idempotent on the API side:
    // asking twice returns the task already made. The run's packet gets
    // `proposed_tasks[index].task_id` so the card stays flipped on reload.
    createTaskFromProposal: (
      id: number,
      index: number,
      data: { assignee_id?: number | null; due_date?: string | null; section?: string | null },
    ) =>
      apiClient
        .post<{ data: Task }>(`/admin/agent-runs/${id}/proposed-tasks/${index}/create-task`, data)
        .then(r => r.data),
  },

  // The runner registry: each daemon's self-reported seat-auth verdict and
  // host stats, rendered verbatim by the Infrastructure panel.
  //
  // `unwrapRunners` owns the envelope depth — see lib/agent-runners.ts for why
  // that is a tested decision rather than an inline `.data.data`.
  // What is set up for each client: manifest, pipeline, last run. Bare body,
  // no {data} envelope — see AdminDeliveryStatusController.
  deliveryStatus: {
    get: () => apiClient.get<DeliveryStatus>("/admin/delivery-status"),
  },

  agentRunners: {
    list: () =>
      apiClient.get<{ data: AgentRunnerInfo[] }>("/admin/agent-runners").then(unwrapRunners),
  },

  // Hub Overview — the admin landing dashboard's KPI strip + per-client
  // week-over-week cards (bare response, mirrors clientHealth.getMatrix). Team
  // section data still comes from api.teamDays. `date` selects the reporting
  // week (any date inside it); defaults to the current week when omitted.
  hubOverview: {
    get: (params?: { date?: string }) =>
      apiClient.get<HubOverview>("/admin/hub-overview", { params }),
  },

  // Client service catalog — the per-client list of services + targets that the
  // matrix health is computed against. Managed from the Manage Services dialog.
  // Backend wraps list/create/update in the repo-standard {data} envelope.
  clientServices: {
    list: (siteId: number) =>
      apiClient
        .get<{ data: ClientService[] }>("/admin/client-services", { params: { site_id: siteId } })
        .then((r) => r.data),

    create: (data: {
      site_id: number
      service: string
      status?: string
      target_count?: number | null
      target_period?: "week" | "month" | null
      notes?: string | null
    }) =>
      apiClient
        .post<{ data: ClientService }>("/admin/client-services", data)
        .then((r) => r.data),

    update: (
      id: number,
      data: Partial<{
        status: string
        target_count: number | null
        target_period: "week" | "month" | null
        notes: string | null
      }>
    ) =>
      apiClient
        .put<{ data: ClientService }>(`/admin/client-services/${id}`, data)
        .then((r) => r.data),

    delete: (id: number) => apiClient.delete<{ success: boolean }>(`/admin/client-services/${id}`),
  },

  // Team week view: per-member Mon–Fri recap dots + hours (bare response).
  teamDays: {
    get: (params?: { date?: string }) =>
      apiClient.get<TeamWeek>("/admin/team-days", { params }),
  },

  // Weekly/monthly goals grouped by scope (team / per-person / per-client).
  // Backend wraps list/create/update in the repo-standard {data} envelope.
  goals: {
    list: (params?: { scope?: GoalScope; period?: GoalPeriod; active?: 0 | 1 }) =>
      apiClient.get<{ data: Goal[] }>("/admin/goals", { params }).then((r) => r.data),

    create: (data: {
      scope: GoalScope
      title: string
      period: GoalPeriod
      // Any date inside the target week/month; backend derives period bounds.
      date: string
      description?: string | null
      user_id?: number | null
      site_id?: number | null
      target_value?: number | null
      metric?: string | null
    }) => apiClient.post<{ data: Goal }>("/admin/goals", data).then((r) => r.data),

    update: (
      id: number,
      data: Partial<{
        title: string
        description: string | null
        status: GoalStatus
        target_value: number | null
        current_value: number | null
        metric: string | null
      }>
    ) => apiClient.put<{ data: Goal }>(`/admin/goals/${id}`, data).then((r) => r.data),

    delete: (id: number) => apiClient.delete<{ success: boolean }>(`/admin/goals/${id}`),
  },
}

// Invoice Types
export interface InvoiceLineItem {
  description: string
  hours: number
  rate: number
  total: number
}

export interface Invoice {
  id: number
  invoice_number: number | null
  user_id: number | null
  full_name: string
  email: string
  paypal_email: string
  invoice_start_date: string
  invoice_end_date: string
  amount: number
  line_items: InvoiceLineItem[]
  invoice_link: string | null
  notes: string | null
  paid: boolean
  paid_at: string | null
  created_at: string
  updated_at: string
  user?: { id: number; name: string; email: string; avatar: string | null }
}

export interface InvoiceParticipant {
  id: number
  name: string
  email: string
  avatar: string | null
  invoice_participant: boolean
}

// Search Console Types
export interface SearchConsolePage {
  url: string
  verdict: 'PASS' | 'FAIL' | 'NEUTRAL' | 'UNKNOWN' | 'ERROR'
  indexing_state: string | null
  coverage_state: string | null
  last_crawl_time: string | null
  robots_txt_state: string | null
  page_fetch_state: string | null
  indexing_requested_at?: string | null  // ISO timestamp, written by backend when user requests indexing
  error?: string
}

export interface SearchConsolePerformancePage {
  url: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

// Onboarding Types
export interface OnboardingStep {
  key: string
  label: string
  description: string
  completed: boolean
  link: string
  cta: string
  required?: boolean
  skippable?: boolean
}

export interface OnboardingStatus {
  dismissed: boolean
  skipped_steps: string[]
  steps: OnboardingStep[]
}

// Types
export interface WordPressConnection {
  id: number
  site_id: number
  type: "production" | "staging"
  name: string | null
  url: string
  username: string | null
  connection_method: "app_password" | "plugin"
  /**
   * The stored column. It keeps reading "connected" while the licence kill
   * switch blocks the connection — resolve through resolveConnectionStatus()
   * rather than rendering this directly.
   */
  status: "disconnected" | "connected" | "error"
  /**
   * API-derived presentation status: "license_inactive" when blocked,
   * otherwise the stored status. Optional because payloads cached before the
   * API added it are simply not blocked.
   */
  display_status?: string
  license_inactive?: boolean
  /** Human explanation of the block, for a tooltip or helper line. */
  blocked_reason?: string | null
  error_message: string | null
  wp_version: string | null
  site_title: string | null
  site_tagline: string | null
  timezone: string | null
  default_post_type: string
  default_post_status: string
  default_author_id: number | null
  auto_featured_image: boolean
  post_types: WordPressPostType[] | null
  categories: WordPressCategory[] | null
  tags: WordPressTag[] | null
  users: WordPressUser[] | null
  post_statuses: WordPressPostStatus[] | null
  media_count: number
  last_synced_at: string | null
  last_tested_at: string | null
  auto_sync_enabled: boolean
  auto_sync_frequency: string
  created_at: string
  updated_at: string
  stats: {
    post_types_count: number
    categories_count: number
    tags_count: number
    users_count: number
    media_count: number
  }
}

export interface WordPressPostType {
  slug: string
  name: string
  description: string
  hierarchical: boolean
  public: boolean
  show_in_rest: boolean
  rest_base: string
  supports: string[]
  count: number
}

export interface WordPressCategory {
  id: number
  name: string
  slug: string
  description: string
  parent: number
  count: number
}

export interface WordPressTag {
  id: number
  name: string
  slug: string
  description: string
  count: number
}

export interface PublishingDefaults {
  status?: string
  author_id?: number | null
  category_ids?: number[]
  tag_ids?: number[]
}

export interface WordPressUser {
  id: number
  name: string
  slug: string
  description: string
  avatar_urls: Record<string, string>
}

export interface WordPressPostStatus {
  slug: string
  name: string
  public: boolean
  queryable: boolean
}

// CMS Types
export interface CmsSyncedPost {
  id: number
  site_id: number
  wordpress_connection_id: number
  wp_post_id: number
  wp_guid: string | null
  title: string
  slug: string
  content: string | null
  excerpt: string | null
  status: string
  post_type: string
  wp_author_id: number | null
  author_name: string | null
  wp_created_at: string | null
  wp_modified_at: string | null
  categories: Array<{ id: number; name: string; slug: string }> | null
  tags: Array<{ id: number; name: string; slug: string }> | null
  featured_media_id: number | null
  featured_media_url: string | null
  meta: Record<string, any> | null
  last_synced_at: string | null
  created_at: string
  updated_at: string
}

export interface CmsStatsResponse {
  connection: {
    id: number
    type: string
    url: string
    site_title: string
    status: string
    last_synced_at: string | null
  }
  stats: {
    posts: {
      total: number
      published: number
      draft: number
      posts: number
      pages: number
    }
    categories: number
    tags: number
    users: number
    media: number
  }
  recent_posts: Array<{
    id: number
    wp_post_id: number
    title: string
    status: string
    post_type: string
    wp_modified_at: string | null
    author_name: string | null
  }>
}

export interface CmsPostFilters {
  post_type?: string
  status?: string
  search?: string
  category?: string
  order_by?: string
  order?: "asc" | "desc"
  page?: number
  per_page?: number
}

export interface CmsPostsResponse {
  data: CmsSyncedPost[]
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

export interface CmsMediaItem {
  id: number
  title: string
  alt_text: string
  caption: string
  description: string
  mime_type: string
  media_type: string
  source_url: string
  date: string | null
  sizes: Record<string, { source_url: string; width: number; height: number }>
  width: number | null
  height: number | null
  file_size: number | null
}

export interface CmsMediaFilters {
  search?: string
  media_type?: string
  page?: number
  per_page?: number
}

export interface CmsMediaResponse {
  data: CmsMediaItem[]
  meta: {
    total: number
    total_pages: number
    page: number
    per_page: number
  }
}

// Link Suggestion Types
//
// Suggest Links v2 (server: App\Services\Links\LinkSuggestionStore::serialize).
// Every endpoint that returns a suggestion returns this exact shape; the
// generate endpoint (/links/suggest-internal) returns the same minus `status`
// plus `similarity` / `ranking_version`.

/** Lifecycle of a saved suggestion row. */
export type LinkSuggestionStatus =
  | "pending"
  | "accepted"
  | "pushed"
  | "dismissed"
  | "stale"
  | "skipped"

export type LinkConfidence = "high" | "medium" | "low"

export interface LinkSuggestion {
  id: string
  anchor_text: string
  target_url: string
  target_title?: string | null
  /** Legacy blob — may still carry a trailing "(Relevance: NN%)". Prefer `reason`. */
  context?: string | null
  /** The complete sentence the anchor sits in, verbatim from the article. */
  sentence_context?: string | null
  /** Plain-English "why it fits" — already stripped of the relevance suffix. */
  reason?: string | null
  confidence?: LinkConfidence | null
  type: "internal" | "external"
  /** Absent on freshly generated (unsaved) suggestions. */
  status?: LinkSuggestionStatus
  /** Why the server marked this stale/skipped — a BulkApplySkipReason key. */
  skip_reason?: string | null
  /**
   * True when the previous engine wrote this card's sentence_context (a
   * 600-byte window — truncated on a long sentence), so the browser may
   * match it in that form when placing the link. Current cards match only
   * their whole sentence.
   */
  legacy_sentence?: boolean
  applied_at?: string | null
  created_at?: string | null
  /** Generate-only: cosine similarity of the destination (0–1). */
  similarity?: number | null
  /** Generate-only: which ranking pipeline produced it (e.g. "v2"). */
  ranking_version?: string | null
  /** Generate-only: where in the body the anchor sits (0 = first word, 1 = last). */
  position?: number | null
  /** Generate-only: which third of the body that is. */
  region?: "opening" | "middle" | "lower" | null
}

/** Grouped saved suggestions — GET /links/suggestions/{...}. */
export interface LinkSuggestionsResponse {
  /** status = pending */
  suggestions: LinkSuggestion[]
  accepted: LinkSuggestion[]
  /** status = pushed (live on the site) */
  applied?: LinkSuggestion[]
  dismissed?: LinkSuggestion[]
}

/**
 * Why candidate destinations never reached the model / the user. Keys come
 * straight from the ranking pipeline; see LINK_EXCLUSION_LABELS in
 * components/link-suggestion-card.tsx for the plain-English names.
 */
export type LinkExclusionReason =
  | "self"
  | "near_duplicate"
  | "glossary"
  | "glossary_anchor"
  | "already_linked"
  | "unpublished"
  | "not_a_post"
  | "non_editorial"
  | "vague_anchor"
  | "low_confidence"
  | "anchor_in_protected_content"
  | "anchor_not_in_article"
  | "duplicate_destination"
  | "duplicate_anchor_occurrence"
  | "overlapping_anchor"
  | "same_sentence"
  | "already_suggested"
  | "promotional_context"
  | "no_sentence_context"
  | "judge_rejected"
  | "judge_misleading_anchor"
  | "ungrounded_reason"
  | "unresolved_target"

/**
 * Whether one generate run searched everything it should have. `complete`
 * false means the answer was cut off or the second sweep failed — the cards
 * shown are what survived, and "No opportunities found" must not be said.
 */
export interface LinkSuggestSearch {
  complete: boolean
  /** 1 = the first pass was enough; 2 = the engine swept the article again. */
  passes: number
  target: number
  /** Candidate placements evaluated across both passes. */
  pool: number
  reason: string | null
}

export interface LinkSuggestStats {
  posts_searched?: number | string
  candidates_found?: number
  sent_to_ai?: number
  excluded?: Partial<Record<LinkExclusionReason, number>> & Record<string, number>
  search?: LinkSuggestSearch | null
}

export interface SuggestInternalResponse {
  suggestions: LinkSuggestion[]
  method?: string
  /** Why fewer than asked for came back: an honest shortage, everything already placed, or an incomplete search. */
  message?: string
  error?: string
  stats?: LinkSuggestStats
  content_hash?: string
}

// --- Internal-link coverage (Internal Links tab) ---
export type LinkCoverageSort = "internal_count" | "title" | "published_at"
export type LinkCoverageFilter = "below_floor" | "orphans" | "all"

/**
 * The state of the most recent suggestion-generation job for one post.
 *
 * `skipped` is a deliberate server decision (already at the link floor, no
 * readable content, embeddings off) — NOT a success, and never to be folded
 * into a "done" count. `generated` counts candidates that survived the
 * filters; `saved` counts the new pending cards actually created, so
 * generated > 0 with saved 0 means the filters ate everything.
 */
export type LinkSuggestRunStatus = "queued" | "running" | "done" | "skipped" | "failed"

export interface LinkSuggestRun {
  status: LinkSuggestRunStatus
  requested_at: string | null
  finished_at: string | null
  generated: number | null
  saved: number | null
  /**
   * Skip-reason key ('no_content', 'embeddings_disabled', …), a failure
   * message, or — on a done run that came back short — the engine's own
   * explanation of why.
   */
  reason: string | null
}

export interface LinkSuggestRunsResponse {
  /** Keyed by wp_post_id; every requested id is present, null = never run. */
  runs: Record<number, LinkSuggestRun | null>
}

export interface LinkApplyRun {
  id: string
  status: "queued" | "running" | "done" | "skipped" | "failed"
  requested_at: string | null
  finished_at: string | null
  result: BulkApplyResult | null
}

export interface LinkApplyRunsResponse {
  runs: Record<number, LinkApplyRun | null>
}

export interface LinkCoverageRow {
  wp_post_id: number
  title: string
  url: string
  published_at: string | null
  internal_link_count: number
  external_link_count: number
  // How many other posts link TO this one (0 = orphan).
  inbound_internal_count: number
  // True when internal_link_count is under the site's internal-link floor (< 3).
  below_floor: boolean
  suggestions: {
    suggested: number
    accepted: number
    pushed: number
  }
  // Null until the post has ever been queued for suggestions. This is how the
  // page reports batch progress — bulk-suggest returns before any job runs.
  suggest_run?: LinkSuggestRun | null
  apply_run?: LinkApplyRun | null
  last_scanned_at: string | null
}

export interface BulkSuggestResponse {
  queued: boolean
  post_count: number
  queued_ids: number[]
  /** Ids that don't resolve to a post on this site. */
  unknown_ids: number[]
  /** Ids already mid-generation; re-queuing them would double the work. */
  already_running_ids: number[]
}

export interface LinkCoverageTotals {
  post_count: number
  below_floor_count: number
  orphan_count: number
  avg_internal: number
  embeddings_enabled: boolean
  last_scan_at: string | null
}

export interface LinkCoverageMeta {
  current_page: number
  per_page: number
  last_page: number
  total: number
}

export interface LinkCoverageResponse {
  data: LinkCoverageRow[]
  meta: LinkCoverageMeta
  totals: LinkCoverageTotals
}

/**
 * Why one suggestion was left out of an apply run (server:
 * App\Services\Links\BulkLinkApplier). `anchor_not_found` and
 * `anchor_in_protected_content` mark the row STALE on a real apply; the
 * already-linked / duplicate / glossary ones mark it SKIPPED (done).
 * `low_confidence` and `per_article_cap` leave the row eligible next run.
 */
export type BulkApplySkipReason =
  | "low_confidence"
  | "glossary_destination"
  | "already_linked"
  | "duplicate_destination"
  | "per_article_cap"
  | "anchor_not_found"
  | "anchor_in_protected_content"

export interface BulkApplyPreviewChange {
  /** Which saved suggestion produced this change (undo keys off it). */
  suggestion_id: string
  anchor: string
  url: string
  // The surrounding sentence/HTML snippet where the link would be inserted.
  context_html: string
}

export interface BulkApplySkipped {
  suggestion_id: string
  anchor: string
  url: string
  reason: BulkApplySkipReason | string
}

export interface BulkApplyPreview {
  wp_post_id: number
  changes: BulkApplyPreviewChange[]
  skipped?: BulkApplySkipped[]
  /** Post-level failure (couldn't fetch, empty page-builder body, write rejected). */
  error?: string
}

export interface BulkApplyResult {
  wp_post_id: number
  links_added: number
  changes?: BulkApplyPreviewChange[]
  skipped?: BulkApplySkipped[]
  error?: string
}

// Google Integration Types
export interface GoogleConnection {
  id: number
  site_id: number
  connected: boolean
  email: string | null
  gsc_property: string | null
  gsc_site_url: string | null
  ga4_property: string | null
  ga4_property_name: string | null
  connected_at: string | null
  last_query_at: string | null
  created_at: string
  updated_at: string
}

export interface AnalyticsSnapshot {
  id: number
  content_item_id: number
  gsc_clicks: number
  gsc_impressions: number
  gsc_ctr: number
  gsc_position: number
  ga4_pageviews: number
  ga4_sessions: number
  ga4_avg_engagement_time: number
  ga4_bounce_rate: number
  period_start: string
  period_end: string
  period_type: 'daily' | 'weekly' | 'monthly'
  created_at: string
}

// A Perplexity source. `search_results` rows are objects; the legacy
// `citations` array is bare URL strings — components must handle both.
export interface StepSearchResult {
  title?: string
  url: string
  date?: string | null
}

export interface WorkflowStepRun {
  id: number
  // Provenance from the linked workflow step. All null when the step run has
  // no workflow_step_id (formatting steps), and variable_mappings/output_variable
  // read the workflow's CURRENT wiring — an edit after the run makes them stale.
  workflow_step_id?: number | null
  variable_mappings?: Record<string, string> | null
  output_variable?: string | null
  prompt_name?: string | null
  step_name: string
  step_order: number
  provider: string
  model: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  error_message: string | null
  input_variables: Record<string, any>
  system_prompt_compiled: string | null
  user_prompt_compiled: string | null
  output: string | null
  input_tokens: number
  output_tokens: number
  cost_usd: number
  settings_used: Record<string, any> | null
  request_payload: Record<string, any> | null
  response_payload: Record<string, any> | null
  response_metadata: Record<string, any> | null
  tool_calls_audit: Record<string, any> | Array<Record<string, any>> | null
}

export interface WorkflowRunDetails {
  id: number
  workflow_id: number
  workflow_name: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  error_message: string | null
  input_data: Record<string, any>
  output: string | null
  context: Record<string, any>
  total_input_tokens: number
  total_output_tokens: number
  total_cost: number
  final_output_step: number | null
  steps: WorkflowStepRun[]
}

// Light run row from GET /content/{id}/workflow-runs (fat columns stripped
// server-side, same shape as the workflow runs list).
export interface ContentWorkflowRun {
  id: number
  workflow_id: number
  status: 'queued' | 'pending' | 'running' | 'completed' | 'failed' | 'paused'
  current_step: number | null
  content_item_id: number | null
  duration_seconds: number | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  workflow?: { id: number; name: string } | null
}

export interface BrokenLinkInstance {
  id: number
  link_url_id: number
  source_post_id: number | null
  // Title + permalink of the post the broken link sits in. The API resolves
  // these against the synced WordPress posts when the scan didn't capture
  // them; they stay null for an external referrer or a post since deleted.
  source_title: string | null
  source_url: string | null
  anchor_text: string | null
  link_type: "anchor" | "image" | "script" | "stylesheet" | "other"
}

/** Where a recommended replacement came from, most trusted first. */
export type ReplacementSource = "slug_match" | "redirect" | "slug_similar" | "archive" | "manual"

export type ReplacementStatus = "none" | "pending" | "approved" | "rejected"

export interface BrokenLinkUrl {
  id: number
  site_id: number
  url: string
  url_hash: string
  is_internal: boolean
  is_broken: boolean
  is_dismissed: boolean
  // Failing but not yet confirmed broken — escalates after 3 consecutive
  // failed checks or 24h of failure
  warning: boolean
  // Failure pattern looks like bot-blocking/hotlink protection; never escalates
  false_positive: boolean
  // Consecutive failed checks so far (toward the 3-strike escalation)
  check_count: number
  first_failure_at: string | null
  last_success_at: string | null
  status_code: number | null
  status_text: string | null
  redirect_url: string | null
  redirect_count: number
  // Recommended replacement, awaiting review. Nothing is applied until
  // someone explicitly approves it.
  suggested_replacement_url: string | null
  replacement_source: ReplacementSource | null
  replacement_confidence: number | null
  replacement_status: ReplacementStatus
  // HTTP status the replacement returned when last verified; null = unreachable
  replacement_status_code: number | null
  replacement_checked_at: string | null
  replacement_reviewed_at: string | null
  replacement_applied_at: string | null
  response_time_ms: number | null
  error_message: string | null
  last_checked_at: string | null
  created_at: string
  updated_at: string
  instances: BrokenLinkInstance[]
}

export interface GmbPost {
  id: number
  site_id: number
  body: string
  image_url: string | null
  cta_type: string | null
  cta_url: string | null
  status: string
  scheduled_at: string | null
  published_at: string | null
  google_post_id: string | null
  error_message: string | null
  created_at: string
  updated_at: string
}

// Backlink types
export type BacklinkStatus = 'prospect' | 'under_review' | 'approved' | 'pending_payment' | 'in_progress' | 'live' | 'rejected' | 'lost'
export type BacklinkType = 'guest_post' | 'niche_edit' | 'directory' | 'haro' | 'link_insert'

export interface Backlink {
  id: number
  site_id: number
  referring_domain: string
  specific_url: string | null
  da_dr: number | null
  backlink_type: BacklinkType
  price: number | null
  destination_page: string
  anchor_text: string | null
  contact: string | null
  acquisition_date: string | null
  notes: string | null
  status: BacklinkStatus
  assigned_to: number | null
  assignee: { id: number; name: string; email: string; avatar: string | null } | null
  invoice_url: string | null
  paid_at: string | null
  created_at: string
  updated_at: string
}

export interface BacklinkInput {
  referring_domain: string
  specific_url?: string
  da_dr?: number | null
  backlink_type: BacklinkType
  price?: number | null
  destination_page: string
  anchor_text?: string
  contact?: string
  notes?: string
  status?: BacklinkStatus
  assigned_to?: number | null
  invoice_url?: string
  paid_at?: string
}

// Technical SEO types

export interface ApiToken {
  id: number
  name: string
  abilities: string[]
  last_used_at: string | null
  created_at: string | null
  /** Null = never expires. Absent on API builds that predate token expiry. */
  expires_at?: string | null
  is_current: boolean
  /** Requests in the last 7 days; absent on API builds without the activity log. */
  requests_7d?: number
}
